import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type {
  ContactStatusMenu,
  CptCode,
  FireActionResult,
  FireActionReminder,
  RcmPayorOption,
} from '@ehr/shared';
import {
  CARE_TEAM_ROLES,
  DEFAULT_REFERRAL_SOURCES,
  fillReferralSource,
  FILE_CATEGORIES,
  FILE_CATEGORY_LABELS,
  FILE_NOTE_MAX,
  type CareTeamProvider,
  type FileCategory,
  FILE_ACCEPT,
  FILE_MAX_LABEL,
  fileUploadProblem,
  ICD10_CATALOG,
  isClientNoteType,
  isOpenTask,
  maskBlobSsn,
  migrateReferral,
  preferredContactBlock,
  preferredContactGap,
  QR_FORM_SUBMITTER,
  referralNpiValid,
  referralSourceOptions,
  type PreferredContactGap,
  ACCESS_DENIED_MESSAGE,
  drawerTabs,
  type MyContactAccess,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import {
  authAllowed,
  authPayorOptions,
  cptOptions,
  dxOptions,
  missingAuthFields,
  missingAuthMessage,
  splitCodes,
  type AuthRequiredField,
} from '../lib/authorizations.js';
import {
  DuplicateContactDialog,
  duplicateOf,
  NO_ACCESS_MESSAGE,
  openableDup,
  isDupPhone,
  type DuplicateContact,
} from '../components/DuplicateContactNotice.js';
import { askConfirm, errorText, showError } from '../lib/dialogs.js';
import { drawerMenuAllowed, isPrimaryClick, runOnce } from '../lib/clickGuard.js';
import { stopSharingConfirm } from '../lib/fileShare.js';
import { clientShareHint } from '../lib/clientShareHint.js';
import { tpChartLink } from '../lib/tpPlans.js';
import {
  isReferralSpecifyBox,
  REFERRAL_SPECIFY_PLACEHOLDER,
  referralSpecifyRed,
} from '../lib/referralOther.js';
import { relatedHeader } from '../lib/relatedHeader.js';
import { apptEmptyText, calendarApptHref, calendarNewApptHref } from '../lib/apptLink.js';
import { fetchNoteUsers, personLabel } from '../lib/noteUsers.js';
import { ownerChip } from '../lib/ownerChip.js';
import { payorOptions, payorValue } from '../lib/payorOptions.js';
import { matchFile, terms } from '../lib/recordSearch.js';
import { SsnField } from './SsnField.js';
import {
  emailValid,
  maskPhone,
  maskSsn,
  maskZip,
  phoneValid,
  zipValid,
} from '../lib/contactFields.js';
import { apptStatusInfo, type ApptTone } from '../lib/apptStatus.js';
import { useScrollLock } from '../lib/scrollLock.js';
import { useLiveRefresh, useLiveVersion } from '../lib/useLiveRefresh.js';
import { ContactPickField } from '../components/ContactPickField.js';
import { FormModal, type LibForm, type FormMode } from '../components/FormModal.js';
import { ScoreHistoryTable } from '../components/ScoreHistory.js';
import {
  buildScoreHistory,
  portalAnswers,
  scoredItems,
  type ScoreFill,
} from '../lib/scoreHistory.js';
import type { SubmissionAnswers } from '../lib/formAnswers.js';
import { ClinicalNotesPanel } from '../components/ClinicalNotesPanel.js';
import { Modal } from '../components/Modal.js';
import { FilePreviewModal } from '../components/FilePreviewModal.js';
import { useContactAccessDialogs } from '../components/share/useContactAccessDialogs.js';
import { ownTask, tabWritable } from '../components/share/tabAccess.js';
import { fetchMyAccess } from '../lib/contactAccess.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { SaveBlockDialog } from '../components/SaveBlockDialog.js';
import { ArchiveIcon, RestoreButton, ShowArchivedToggle } from '../components/ArchiveControls.js';
import { archiveItem, archivedUrl, restoreItem } from '../lib/chartArchive.js';
import { formPrintHtml } from '../lib/formPrint.js';
import { formAsFilled, layoutOf, type FilledLayout } from '../lib/formLayout.js';
import {
  NoteAccessToggle,
  NoteSharePopover,
  ShareHint,
  ShareIcon,
  UploadShareIcon,
  nsAvatarColor,
  nsInitials,
  nsRoleLabel,
  nsUserName,
  noteShares,
  type NoteAccess,
  type NoteShare,
  type NoteUser,
} from '../components/noteSharing.js';
import { Cd, Combobox, Dd, MultiDd } from '../components/Dropdown.js';
import { DateField } from '../components/DateTimeField.js';
import {
  RecurrenceField,
  NO_RECURRENCE,
  nextOccurrence,
  recurrenceEnded,
  type Recurrence,
} from '../components/RecurrenceField.js';
import { useSession, canManage } from '../auth/session.js';
import { ArchivedTasks, TaskDrawer } from './TasksTab.js';
import { TaskListView, TaskBoardView, type TaskViewStatus } from '../components/taskViews.js';
import { MessagesPanel } from './MessagesPanel.js';
import { SecurityQuestionsSection } from '../components/SecurityQuestionsSection.js';
import {
  SlidingScaleSection,
  focusSlidingError,
  useSlidingScale,
} from '../components/SlidingScale.js';
import { CONTACT_CHANNEL_KEYS, channelAnswer } from '../lib/appointmentReminders.js';
import { SummaryTab } from '../components/SummaryTab.js';
import { FormPreviewModal, type Task as SignoffTaskRow } from './SignoffTab.js';
import { sendState, sentBackAt, sentStatus, type SentFormRow } from '../lib/sentForms.js';
import {
  GROUP_LABELS,
  UNFILLED_PAGE,
  buildFormRows,
  kindRows,
  rowIsScreener,
  type FormKind,
  daysAgo,
  fmtDay,
  grouped,
  searchRows,
  type FormRow,
  type FormsFilter,
  type RowGroup,
} from '../lib/formsTable.js';
import { IMAGE_ACCEPT, IMAGE_HINT, uploadError } from '../lib/uploads.js';
import { timeOpts } from '../lib/timePrefs.js';
import {
  SaveButton,
  SaveButtons,
  SaveCardAnchor,
  failedNotice,
  okNotice,
  runSave,
  savedNotice,
  type SaveNotice,
} from '../components/SaveToast.js';
import { ChartSearch } from '../components/ChartSearch.js';
import { ProfileSectionTabs } from '../components/ProfileSectionTabs.js';
import { ChartTabs } from '../components/ChartTabs.js';
import {
  AUTH_LABEL,
  AUTH_SECTION,
  profileProblems,
  profileTabOrder,
  profileTabs,
  sectionsNeedingInfo,
  startView,
} from '../lib/profileTabs.js';
import {
  buildProfileIndex,
  flashEl,
  headerIndex,
  headerTarget,
  previewsAfterCancel,
  profileTarget,
  savedProfile,
  sectionHit,
  showProfileSaveBar,
  type ChartHit,
} from '../lib/chartSearch.js';
import {
  LAST_SAVED_TICK_MS,
  SAVE_MIN_MS,
  afterSave,
  atLeast,
  lastSavedFromServer,
  lastSavedLine,
  savedLine,
  type LastSaved,
} from '../lib/saveTiming.js';
import {
  authErrText,
  authSavedText,
  followupErrText,
  followupSavedText,
} from '../lib/saveCards.js';
import {
  PreferredContactWarning,
  preferredContactRefusal,
} from '../components/PreferredContactWarning.js';
import {
  contactFieldLabel,
  contactSaveMissing,
  requiredContactMessage,
  requiredContactRefusal,
} from '../lib/requiredContact.js';
import { ApptClientNote, ApptClientNoteIcon } from '../components/ApptClientNote.js';

/** Per-note share access level (V38 — View Only vs Can Edit). */
interface Note {
  id: string;
  authorId: string;
  type: string;
  sharedWith: string[];
  /** Per-user access grade for `sharedWith`: { userId: 'view' | 'edit' }. */
  sharedAccess: Record<string, NoteAccess>;
  body: string;
  // Data Import (#31): 'legacy' notes were imported from a prior system; they
  // show the original author name + date and a "Legacy" tag in the timeline.
  source?: string;
  legacyAuthorLabel?: string | null;
  originalCreatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  /** ehr#736: set on an archived note (listed only with Show archived). */
  archivedAt?: string | null;
}
/** Simple share-with-users popover (P2) — used by form submissions (Forms tab).
 *  Notes use the richer NoteSharePopover; this stays as-is for form sharing. */
function SharePicker({
  users,
  current,
  myId,
  onSave,
  onClose,
}: {
  users: NoteUser[];
  current: string[];
  myId: string | undefined;
  onSave: (ids: string[]) => void;
  onClose: () => void;
}) {
  const [sel, setSel] = useState<Set<string>>(new Set(current));
  const name = (u: NoteUser) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
  const pool = users.filter((u) => u.id !== myId);
  return (
    <div className="share-picker" onClick={(e) => e.stopPropagation()}>
      <div className="share-picker-head">Share with — select one or more</div>
      <ShareHint text={clientShareHint('form')} />
      <div className="share-picker-list">
        {pool.length === 0 ? (
          <p className="muted" style={{ margin: '6px 4px', fontSize: 12.5 }}>
            No other users to share with.
          </p>
        ) : (
          pool.map((u) => (
            <label key={u.id} className="share-picker-item">
              <input
                type="checkbox"
                checked={sel.has(u.id)}
                onChange={() =>
                  setSel((s) => {
                    const n = new Set(s);
                    if (n.has(u.id)) n.delete(u.id);
                    else n.add(u.id);
                    return n;
                  })
                }
              />
              {name(u)}
            </label>
          ))
        )}
      </div>
      <div className="share-picker-actions">
        <button className="btn sm" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary sm" onClick={() => onSave([...sel])}>
          Save
        </button>
      </div>
    </div>
  );
}

// Note-type pills — mirror the CRM's composer (P5 parity).
const NOTE_TYPES: { key: string; label: string }[] = [
  { key: 'note', label: 'Note' },
  { key: 'call', label: 'Call' },
  { key: 'email', label: 'Email' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'outreach', label: 'Text / SMS' },
  { key: 'social', label: 'Social' },
  // alertshub#14 (Jay 10-13): written for the client: their portal shows them (Records → Notes)
  { key: 'avs', label: 'After-visit summary' },
  { key: 'resource', label: 'Resource' },
];
const noteTypeLabel = (t: string) => NOTE_TYPES.find((x) => x.key === t)?.label ?? t;
const fmtNoteTime = (iso: string) => {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', timeOpts())
  );
};
const EditIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="12"
    height="12"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

/**
 * Notes tab — CRM-parity activity timeline (P5). Note-type pills + composer,
 * a searchable timeline, and inline edit / archive (author or admin/manager; ehr#736 never delete).
 * Bodies are encrypted PHI, role-gated + audited on the server.
 */
function NotesPanel({
  contactId,
  isClient = false,
  readOnly = false,
}: {
  contactId: string;
  isClient?: boolean;
  /** ehr#730: a View tab (Share & Access): no composer, Edit, Share, Archive or Restore. */
  readOnly?: boolean;
}) {
  const { user } = useSession();
  const [notes, setNotes] = useState<Note[]>([]);
  const [users, setUsers] = useState<NoteUser[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [draft, setDraft] = useState('');
  const [type, setType] = useState('note');
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [shareId, setShareId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Note pending a themed archive confirmation (replaces window.confirm — #43; ehr#736 archive,
  // never delete).
  const [confirmDelId, setConfirmDelId] = useState<string | null>(null);
  // ehr#736: Show archived lists the archived notes (with Restore) under the current ones.
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState<Note[] | null>(null);
  // Composer share ("Add for myself and others"): inline user picker.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shareUserIds, setShareUserIds] = useState<Set<string>>(new Set());
  const [noteAccess, setNoteAccess] = useState<NoteAccess>('edit');
  const [nsSearch, setNsSearch] = useState('');
  // Pending share removals awaiting a themed confirm (mirrors the mockup credConfirm).
  const [pendingUnshare, setPendingUnshare] = useState<{
    noteId: string;
    userId: string;
    name: string;
  } | null>(null);
  const [pendingCancelAll, setPendingCancelAll] = useState<{
    noteId: string;
    names: string;
  } | null>(null);

  const load = () => {
    api
      .get<Note[]>(`/contacts/${contactId}/notes`)
      .then((n) => {
        // ehr#31: imported (legacy) notes are progress notes; they list on the Clinical Notes tab.
        setNotes(n.filter((x) => x.source !== 'legacy'));
        setStatus('ready');
      })
      .catch((e: unknown) =>
        setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error'),
      );
  };
  useEffect(load, [contactId]);
  const loadArchived = () => {
    api
      .get<Note[]>(archivedUrl(`/contacts/${contactId}/notes`))
      .then((n) => setArchived(n.filter((x) => x.source !== 'legacy')))
      .catch(() => setArchived([]));
  };
  useEffect(() => {
    if (showArchived) loadArchived();
    else setArchived(null);
  }, [showArchived, contactId]);
  const restore = (id: string) => {
    setBusy(true);
    restoreItem({ kind: 'note', contactId, id })
      .then(() => {
        load();
        loadArchived();
      })
      .catch(() => setStatus('error'))
      .finally(() => setBusy(false));
  };
  useEffect(() => {
    // All org users — resolve note authors AND are the note share candidates
    // (any teammate, matching the Tasks tab — not just contact-access users).
    // ehr#623: names for clinicians too (they can't read /settings/users).
    void fetchNoteUsers().then(setUsers);
  }, []);

  const authorName = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : '';
  };
  const firstName = (id: string) => authorName(id).split(' ')[0] || '';
  // Edit rights: author, admin/manager, OR a user the note is shared with at "edit".
  const canEdit = (n: Note) =>
    !readOnly &&
    Boolean(
      user &&
      (n.authorId === user.id || canManage(user.role) || n.sharedAccess[user.id] === 'edit'),
    );
  // A note's current share set as API instructions ({userId, access}).

  // Create the note, then (optionally) share it with the picked users at the
  // chosen View/Can-Edit access level.
  const createNote = (shares: NoteShare[]) => {
    if (!draft.trim()) return;
    setSaving(true);
    api
      .post<Note>(`/contacts/${contactId}/notes`, { body: draft.trim(), type })
      .then(async (created) => {
        if (shares.length && created?.id) {
          await api
            .post(`/contacts/${contactId}/notes/${created.id}/share`, { shares })
            .catch(() => undefined);
        }
        setDraft('');
        setPickerOpen(false);
        setShareUserIds(new Set());
        setNsSearch('');
        load();
      })
      .catch(() => setStatus('error'))
      .finally(() => setSaving(false));
  };
  const add = () => createNote([]);
  // Save & Share from the composer — the picked users at the current access level.
  const saveAndShare = () =>
    createNote([...shareUserIds].map((userId) => ({ userId, access: noteAccess })));
  const saveEdit = (id: string) => {
    if (!editDraft.trim()) return;
    setBusy(true);
    api
      .patch(`/contacts/${contactId}/notes/${id}`, { body: editDraft.trim() })
      .then(() => {
        setEditId(null);
        load();
      })
      .catch(() => setStatus('error'))
      .finally(() => setBusy(false));
  };
  const del = (id: string) => setConfirmDelId(id);
  const doDelete = () => {
    const id = confirmDelId;
    if (!id) return;
    setBusy(true);
    archiveItem({ kind: 'note', contactId, id })
      .then(() => {
        load();
        if (showArchived) loadArchived();
      })
      .catch(() => setStatus('error'))
      .finally(() => {
        setBusy(false);
        setConfirmDelId(null);
      });
  };
  // Replace a note's complete share set (V38 replace semantics).
  const postShare = (noteId: string, shares: NoteShare[]) => {
    api
      .post(`/contacts/${contactId}/notes/${noteId}/share`, { shares })
      .then(load)
      .catch(() => setStatus('error'));
  };
  // Popover: add one recipient at the chosen access level (shares immediately).
  const addShare = (noteId: string, userId: string, access: NoteAccess) => {
    const n = notes.find((x) => x.id === noteId);
    if (!n) return;
    postShare(noteId, [...noteShares(n).filter((s) => s.userId !== userId), { userId, access }]);
  };
  // Popover: remove one recipient (confirmed) or cancel all sharing (confirmed).
  const confirmUnshare = () => {
    if (!pendingUnshare) return;
    const n = notes.find((x) => x.id === pendingUnshare.noteId);
    if (n)
      postShare(
        n.id,
        noteShares(n).filter((s) => s.userId !== pendingUnshare.userId),
      );
    setPendingUnshare(null);
  };
  const confirmCancelAll = () => {
    if (pendingCancelAll) postShare(pendingCancelAll.noteId, []);
    setPendingCancelAll(null);
  };

  if (status === 'loading') return <p className="muted">Loading notes…</p>;
  if (status === 'forbidden')
    return <p className="muted">Your role cannot view clinical notes (403).</p>;
  if (status === 'error') return <p className="muted">Could not load notes.</p>;

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? notes.filter(
        (n) =>
          n.body.toLowerCase().includes(needle) ||
          noteTypeLabel(n.type).toLowerCase().includes(needle) ||
          authorName(n.authorId).toLowerCase().includes(needle),
      )
    : notes;

  return (
    <>
      <div className="note-composer" hidden={readOnly}>
        <div className="note-composer-actions">
          <div className="note-type-pills">
            {NOTE_TYPES.filter((t) => isClient || !isClientNoteType(t.key)).map((t) => (
              <button
                key={t.key}
                className={`note-type-pill ${type === t.key ? 'active' : ''}${isClientNoteType(t.key) ? ' for-client' : ''}`}
                onClick={() => setType(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        {isClientNoteType(type) && (
          <p className="note-client-hint">
            The client sees this in their portal, in Records under Notes, and gets a notice in their
            bell.
          </p>
        )}
        <textarea
          placeholder="Add a note, log a call, or capture what just happened…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="New note"
        />
        <div className="note-add-btn-row">
          <button
            className={`note-add-self${pickerOpen ? ' inactive' : ''}`}
            onClick={() => {
              setPickerOpen(false);
              setShareUserIds(new Set());
              add();
            }}
            disabled={saving || !draft.trim()}
          >
            {saving ? 'Adding…' : 'Add for myself'}
          </button>
          <button
            className={`note-add-others${pickerOpen ? ' active' : ''}`}
            onClick={() => {
              setPickerOpen((o) => !o);
              setNsSearch('');
            }}
            disabled={saving || !draft.trim()}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </svg>
            Add for myself and others
            {shareUserIds.size > 0 && <span className="note-add-count">{shareUserIds.size}</span>}
          </button>
          {shareUserIds.size > 0 && (
            <button
              className="btn primary"
              style={{ marginLeft: 'auto' }}
              onClick={saveAndShare}
              disabled={saving}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Save &amp; Share
            </button>
          )}
        </div>
        {pickerOpen &&
          (() => {
            const pool = users.filter((u) => u.id !== user?.id);
            const nd = nsSearch.trim().toLowerCase();
            const shown = nd
              ? pool.filter(
                  (u) =>
                    nsUserName(u).toLowerCase().includes(nd) ||
                    (u.role ?? '').toLowerCase().includes(nd),
                )
              : pool;
            return (
              <div className="note-share-picker">
                <div className="note-share-picker-head">Share with — select one or more</div>
                <ShareHint text={clientShareHint('note')} />
                {pool.length === 0 ? (
                  <div className="note-share-empty">No teammates to share with.</div>
                ) : (
                  <>
                    <NoteAccessToggle value={noteAccess} onChange={setNoteAccess} />
                    {pool.length > 1 && (
                      <div className="note-share-search-wrap">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                          className="note-share-search"
                          type="search"
                          placeholder="Search users…"
                          autoComplete="off"
                          value={nsSearch}
                          onChange={(e) => setNsSearch(e.target.value)}
                        />
                      </div>
                    )}
                    {shown.length === 0 ? (
                      <div className="note-share-empty">No users match your search.</div>
                    ) : (
                      shown.map((u) => {
                        const nm = nsUserName(u);
                        return (
                          <label key={u.id} className="note-share-user-row">
                            <input
                              type="checkbox"
                              checked={shareUserIds.has(u.id)}
                              onChange={() =>
                                setShareUserIds((s) => {
                                  const n = new Set(s);
                                  if (n.has(u.id)) n.delete(u.id);
                                  else n.add(u.id);
                                  return n;
                                })
                              }
                            />
                            <span
                              className="user-avatar tiny"
                              style={{ background: nsAvatarColor(u.id) }}
                            >
                              {nsInitials(nm)}
                            </span>
                            <span className="note-share-user-name">{nm}</span>
                            <span className="note-share-user-role">{nsRoleLabel(u.role)}</span>
                          </label>
                        );
                      })
                    )}
                  </>
                )}
                <div className="note-share-foot">
                  <button
                    className="btn ghost-sm"
                    onClick={() => {
                      setPickerOpen(false);
                      setNsSearch('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn primary"
                    onClick={saveAndShare}
                    disabled={saving || shareUserIds.size === 0}
                    title={
                      pool.length === 0
                        ? 'Share the contact first — there is nobody with access to share a note with'
                        : undefined
                    }
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Save &amp; Share{shareUserIds.size > 0 ? ` (${shareUserIds.size})` : ''}
                  </button>
                </div>
              </div>
            );
          })()}
      </div>

      {notes.length === 0 ? (
        <p className="muted" style={{ fontStyle: 'italic', margin: '24px 0' }}>
          No activity yet. Use the composer above to log your first interaction.
        </p>
      ) : (
        <>
          <div className="notes-search">
            <SearchIcon />
            <input
              type="search"
              placeholder="Search notes…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search notes"
            />
          </div>
          <div className="timeline">
            {filtered.map((n) => {
              const isMyNote = n.authorId === user?.id;
              const isSharedToMe = !isMyNote && n.sharedWith.includes(user?.id ?? '');
              const myAccess = user ? n.sharedAccess[user.id] : undefined;
              const showEdit = canEdit(n);
              // ehr#736: Archive (never Delete), for the author or an admin / manager
              const showDelete = !readOnly && Boolean(isMyNote || (user && canManage(user.role)));
              return (
                <div
                  key={n.id}
                  className={`timeline-item ${n.type}${isSharedToMe ? ' note-from-share' : ''}`}
                >
                  <div className="timeline-dot" />
                  <div className="timeline-meta">
                    <span className={`ttype ${n.type}`}>{noteTypeLabel(n.type)}</span>
                    {isClientNoteType(n.type) && (
                      <span className="ttype client-sees" title="Shown in the client portal">
                        Client can see
                      </span>
                    )}
                    {n.source === 'legacy' && (
                      <span className="ttype legacy" title="Imported from a prior system">
                        Legacy
                      </span>
                    )}
                    {/* Legacy notes show the original author name (free text, not a
                        system user); native notes show the system author avatar. */}
                    {n.source === 'legacy'
                      ? n.legacyAuthorLabel && (
                          <span className="timeline-by">
                            <strong>{n.legacyAuthorLabel}</strong>
                          </span>
                        )
                      : authorName(n.authorId) && (
                          <span className="timeline-by">
                            <span
                              className="user-avatar tiny"
                              style={{ background: nsAvatarColor(n.authorId) }}
                            >
                              {nsInitials(authorName(n.authorId))}
                            </span>
                            <strong>{firstName(n.authorId)}</strong>
                          </span>
                        )}
                    <span className="ttime">{fmtNoteTime(n.originalCreatedAt || n.createdAt)}</span>
                    {isMyNote && n.sharedWith.length > 0 && (
                      <span className="note-shared-badge">
                        <UploadShareIcon /> Shared with{' '}
                        {n.sharedWith.map((id) => firstName(id) || 'user').join(', ')}
                      </span>
                    )}
                    {isSharedToMe && (
                      <span className="note-shared-by-badge">
                        <UploadShareIcon /> Shared by {firstName(n.authorId) || 'someone'}
                        {myAccess === 'view' ? ' · view only' : ''}
                      </span>
                    )}
                    {isMyNote && !readOnly && (
                      <div className="share-wrap">
                        <button
                          className={`note-share-later-btn${n.sharedWith.length ? ' shared' : ''}`}
                          title={
                            n.sharedWith.length
                              ? `Shared with: ${n.sharedWith
                                  .map((id) => authorName(id) || 'user')
                                  .join(', ')}`
                              : 'Share note with others'
                          }
                          onClick={() => setShareId((s) => (s === n.id ? null : n.id))}
                        >
                          <ShareIcon />
                          {n.sharedWith.length
                            ? ` Shared (${n.sharedWith.length})`
                            : ' Share note with others'}
                        </button>
                        {shareId === n.id && (
                          <NoteSharePopover
                            note={n}
                            candidates={users}
                            myId={user?.id}
                            hint={clientShareHint('note')}
                            emptyText="No teammates to share with."
                            onAdd={(uid, acc) => addShare(n.id, uid, acc)}
                            onRemove={(uid, nm) =>
                              setPendingUnshare({ noteId: n.id, userId: uid, name: nm })
                            }
                            onCancelAll={() =>
                              setPendingCancelAll({
                                noteId: n.id,
                                names: n.sharedWith.map((id) => firstName(id) || 'user').join(', '),
                              })
                            }
                            onClose={() => setShareId(null)}
                          />
                        )}
                      </div>
                    )}
                    {isMyNote && !readOnly && n.sharedWith.length > 0 && (
                      <button
                        className="note-cancel-share-btn"
                        onClick={() =>
                          setPendingCancelAll({
                            noteId: n.id,
                            names: n.sharedWith.map((id) => firstName(id) || 'user').join(', '),
                          })
                        }
                      >
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                        Cancel Sharing
                      </button>
                    )}
                    {(showEdit || showDelete) && (
                      <div className="tbtns">
                        {showEdit && (
                          <button
                            className="tedit"
                            title="Edit"
                            onClick={() => {
                              setEditId(n.id);
                              setEditDraft(n.body);
                            }}
                          >
                            <EditIcon />
                          </button>
                        )}
                        {showDelete && (
                          <button
                            className="tdelete tarchive"
                            title="Archive"
                            aria-label="Archive note"
                            onClick={() => del(n.id)}
                            disabled={busy}
                          >
                            <ArchiveIcon />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {editId === n.id ? (
                    <div className="tl-edit-wrap">
                      <textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') saveEdit(n.id);
                          if (e.key === 'Escape') setEditId(null);
                        }}
                        autoFocus
                      />
                      <div className="tl-edit-actions">
                        <span className="tl-edit-hint">⌘+Enter to save · Esc to cancel</span>
                        <button className="btn sm" onClick={() => setEditId(null)}>
                          Cancel
                        </button>
                        <button
                          className="btn primary sm"
                          onClick={() => saveEdit(n.id)}
                          disabled={busy || !editDraft.trim()}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="timeline-body">{n.body}</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className="archived-bar">
        <ShowArchivedToggle on={showArchived} onChange={setShowArchived} count={archived?.length} />
      </div>
      {showArchived && archived && (
        <div className="timeline archived-list" aria-label="Archived notes">
          {archived.length === 0 ? (
            <p className="muted">No archived notes.</p>
          ) : (
            archived.map((n) => (
              <div key={n.id} className={`timeline-item archived ${n.type}`}>
                <div className="timeline-dot" />
                <div className="timeline-meta">
                  <span className={`ttype ${n.type}`}>{noteTypeLabel(n.type)}</span>
                  <span className="ttype archived-tag">Archived</span>
                  {authorName(n.authorId) && (
                    <span className="timeline-by">
                      <strong>{firstName(n.authorId)}</strong>
                    </span>
                  )}
                  <span className="ttime">{fmtNoteTime(n.originalCreatedAt || n.createdAt)}</span>
                  {!readOnly &&
                    Boolean(n.authorId === user?.id || (user && canManage(user.role))) && (
                      <div className="tbtns">
                        <RestoreButton onClick={() => restore(n.id)} disabled={busy} />
                      </div>
                    )}
                </div>
                <div className="timeline-body">{n.body}</div>
              </div>
            ))
          )}
        </div>
      )}
      {confirmDelId && (
        <ConfirmDialog
          message="Archive this note? It leaves the list. Turn on Show archived to see it again or restore it."
          confirmLabel="Archive"
          danger={false}
          busy={busy}
          onConfirm={doDelete}
          onClose={() => setConfirmDelId(null)}
        />
      )}
      {pendingUnshare && (
        <ConfirmDialog
          message={`Stop sharing this note with ${pendingUnshare.name}?`}
          confirmLabel="Yes, stop sharing"
          onConfirm={confirmUnshare}
          onClose={() => setPendingUnshare(null)}
        />
      )}
      {pendingCancelAll && (
        <ConfirmDialog
          message={`Stop sharing this note with ${pendingCancelAll.names}?`}
          confirmLabel="Yes, stop sharing"
          onConfirm={confirmCancelAll}
          onClose={() => setPendingCancelAll(null)}
        />
      )}
    </>
  );
}

interface Eligibility {
  id: string;
  checkedAt: string;
  payer: string;
  memberId: string | null;
  planName: string | null;
  groupNum: string | null;
  deductibleMet: boolean;
  oopMet: boolean;
  copay: string | null;
  coinsurance: string | null;
  status: string;
  method: string | null;
  checkedById: string | null;
  notes: string | null;
}

/** Map an EligibilityStatus enum value to the mockup's badge variant + label. */
const eligBadge = (s: string): { cls: string; label: string } => {
  const label = s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
  const cls = s === 'active' ? 'active' : s === 'pending' ? 'pending' : 'terminated';
  return { cls, label };
};
const yesNo = (v: boolean) =>
  v ? (
    <span className="elig-badge active">Yes</span>
  ) : (
    <span className="elig-badge terminated">No</span>
  );

/** Eligibility tab (Phase 16) — insurance verification history + add. PHI-gated.
 *  Table layout mirrors the ehr-revised mockup 1:1. */
function EligibilityPanel({
  contactId,
  readOnly = false,
}: {
  contactId: string;
  readOnly?: boolean;
}) {
  const { user } = useSession();
  const [rows, setRows] = useState<Eligibility[]>([]);
  const [users, setUsers] = useState<NoteUser[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [showForm, setShowForm] = useState(false);

  const [checkedAt, setCheckedAt] = useState('');
  const [payer, setPayer] = useState('');
  const [memberId, setMemberId] = useState('');
  const [groupNum, setGroupNum] = useState('');
  const [planName, setPlanName] = useState('');
  const [eligStatus, setEligStatus] = useState('active');
  const [copay, setCopay] = useState('');
  const [coinsurance, setCoinsurance] = useState('');
  const [deductibleMet, setDeductibleMet] = useState('no');
  const [oopMet, setOopMet] = useState('no');
  const [method, setMethod] = useState('Portal');
  const [notes, setNotes] = useState('');

  // ehr#730: a View tab (Share & Access) has no Add
  const canEdit = !readOnly && Boolean(user && canManage(user.role));

  const load = () => {
    api
      .get<Eligibility[]>(`/contacts/${contactId}/eligibility`)
      .then((r) => {
        setRows(r);
        setStatus('ready');
      })
      .catch((e: unknown) =>
        setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error'),
      );
  };
  useEffect(load, [contactId]);
  useEffect(() => {
    // ehr#623: names for clinicians too (they can't read /settings/users).
    void fetchNoteUsers().then(setUsers);
  }, []);

  const userName = (id: string | null) => {
    if (!id) return '—';
    const u = users.find((x) => x.id === id);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : '—';
  };

  const resetForm = () => {
    setCheckedAt('');
    setPayer('');
    setMemberId('');
    setGroupNum('');
    setPlanName('');
    setEligStatus('active');
    setCopay('');
    setCoinsurance('');
    setDeductibleMet('no');
    setOopMet('no');
    setMethod('Portal');
    setNotes('');
  };

  const add = () => {
    if (!payer.trim()) return;
    api
      .post(`/contacts/${contactId}/eligibility`, {
        checkedAt: checkedAt || undefined,
        payer: payer.trim(),
        memberId: memberId.trim() || undefined,
        groupNum: groupNum.trim() || undefined,
        planName: planName.trim() || undefined,
        status: eligStatus,
        copay: copay.trim() || undefined,
        coinsurance: coinsurance.trim() || undefined,
        deductibleMet: deductibleMet === 'yes',
        oopMet: oopMet === 'yes',
        method: method || undefined,
        notes: notes.trim() || undefined,
      })
      .then(() => {
        resetForm();
        setShowForm(false);
        load();
      })
      .catch(() => setStatus('error'));
  };

  if (status === 'loading') return <p className="muted">Loading eligibility…</p>;
  if (status === 'forbidden') return <p className="muted">Your role cannot view PHI (403).</p>;
  if (status === 'error') return <p className="muted">Could not load eligibility.</p>;

  return (
    <div className="notes-panel">
      <div className="tab-toolbar">
        <div className="tab-toolbar-label">
          Eligibility History · {rows.length} check{rows.length === 1 ? '' : 's'}
        </div>
        {canEdit && (
          <button className="auth-add-btn" onClick={() => setShowForm((v) => !v)}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Check Eligibility
          </button>
        )}
      </div>

      {showForm && (
        <div className="auth-form-card">
          <div className="auth-form-title">New Eligibility Check</div>
          <div className="auth-form-grid">
            <label className="demo-field">
              <span className="muted">Date Checked</span>
              <DateField value={checkedAt} onChange={setCheckedAt} />
            </label>
            <label className="demo-field">
              <span className="muted">Payer / Insurance</span>
              <input
                value={payer}
                onChange={(e) => setPayer(e.target.value)}
                placeholder="Aetna PPO"
              />
            </label>
            <label className="demo-field">
              <span className="muted">Member ID</span>
              <input
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
                placeholder="AET-882043"
              />
            </label>
            <label className="demo-field">
              <span className="muted">Group Number</span>
              <input
                value={groupNum}
                onChange={(e) => setGroupNum(e.target.value)}
                placeholder="GRP-4410"
              />
            </label>
            <label className="demo-field">
              <span className="muted">Plan Name</span>
              <input
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder="Select Plus PPO"
              />
            </label>
            <label className="demo-field">
              <span className="muted">Status</span>
              <Dd
                value={eligStatus}
                searchable={false}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'terminated', label: 'Terminated' },
                  { value: 'unknown', label: 'Unknown' },
                ]}
                onChange={setEligStatus}
              />
            </label>
            <label className="demo-field">
              <span className="muted">Copay</span>
              <input value={copay} onChange={(e) => setCopay(e.target.value)} placeholder="$30" />
            </label>
            <label className="demo-field">
              <span className="muted">Coinsurance</span>
              <input
                value={coinsurance}
                onChange={(e) => setCoinsurance(e.target.value)}
                placeholder="20%"
              />
            </label>
            <label className="demo-field">
              <span className="muted">Deductible Met?</span>
              <Dd
                value={deductibleMet}
                searchable={false}
                options={[
                  { value: 'no', label: 'No' },
                  { value: 'yes', label: 'Yes' },
                ]}
                onChange={setDeductibleMet}
              />
            </label>
            <label className="demo-field">
              <span className="muted">OOP Met?</span>
              <Dd
                value={oopMet}
                searchable={false}
                options={[
                  { value: 'no', label: 'No' },
                  { value: 'yes', label: 'Yes' },
                ]}
                onChange={setOopMet}
              />
            </label>
            <label className="demo-field">
              <span className="muted">Method Collected</span>
              <Cd
                value={method}
                options={['Portal', 'Phone', 'Fax', 'EDI', 'Email']}
                onChange={setMethod}
              />
            </label>
            <label className="demo-field auth-form-full">
              <span className="muted">Notes</span>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Deductible amounts, coverage details, limitations…"
              />
            </label>
          </div>
          <div className="auth-form-actions">
            <button className="btn primary" onClick={add} disabled={!payer.trim()}>
              Save Check
            </button>
            <button
              className="btn"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="muted">No eligibility checks on file.</p>
      ) : (
        <div className="elig-table-wrap">
          <table className="elig-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Payer</th>
                <th>Plan</th>
                <th>Member ID</th>
                <th>Group #</th>
                <th>Copay</th>
                <th>Coin.</th>
                <th>Ded. Met</th>
                <th>OOP Met</th>
                <th>Method</th>
                <th>Checked By</th>
                <th>Status</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const badge = eligBadge(r.status);
                return (
                  <tr key={r.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtAuthDate(r.checkedAt)}</td>
                    <td>{r.payer}</td>
                    <td className="td-muted">{r.planName || '—'}</td>
                    <td className="td-muted">{r.memberId || '—'}</td>
                    <td className="td-muted">{r.groupNum || '—'}</td>
                    <td>{r.copay || '—'}</td>
                    <td>{r.coinsurance || '—'}</td>
                    <td>{yesNo(r.deductibleMet)}</td>
                    <td>{yesNo(r.oopMet)}</td>
                    <td className="td-muted">{r.method || '—'}</td>
                    <td className="td-muted">{userName(r.checkedById)}</td>
                    <td>
                      <span className={`elig-badge ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="td-muted">{r.notes || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface Authorization {
  id: string;
  authNo: string;
  payer: string;
  serviceType: string | null;
  cptCodes: string[] | null;
  sessionsApproved: number | null;
  sessionsUsed: number;
  startsOn: string | null;
  expiresOn: string | null;
  method: string | null;
  status: string;
  notes: string | null;
  createdById: string | null;
  createdAt: string;
  // Mockup fields not yet in the backend schema — render as "—" until wired.
  diagnosis?: string | null;
  frequency?: string | null;
  priorAuthRequired?: boolean | null;
  dateAuthorized?: string | null;
  requestingProvider?: string | null;
  referringProvider?: string | null;
  unitsAuthorized?: string | null;
  // ehr#565: counted by the API from the client's checked-out sessions (auth-usage.ts).
  used?: number;
  authorized?: number | null;
  unitKind?: 'sessions' | 'units' | null;
  remaining?: number | null;
  usageSource?: 'calendar' | 'stored';
}

/** mm/dd/yyyy to match the mockup; em-dash when empty. */
const fmtAuthDate = (s?: string | null) =>
  s
    ? new Date(s).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : '—';
/** ehr#353: a calendar date (Effective, Expiry, Date Authorized) is stored as midnight UTC of the
 *  day entered; read the day itself, not the local time (which is the day before in the US). */
const fmtAuthDay = (s?: string | null) => {
  const m = s ? /^(\d{4})-(\d{2})-(\d{2})/.exec(s) : null;
  return m ? `${m[2]}/${m[3]}/${m[1]}` : fmtAuthDate(s);
};

/** Map an AuthStatus enum value to the mockup's badge variant + display label. */
const authBadge = (s: string): { cls: string; label: string } => {
  switch (s) {
    case 'approved':
      return { cls: 'active', label: 'Active' };
    case 'requested':
      return { cls: 'pending', label: 'Requested' };
    case 'denied':
      return { cls: 'inactive', label: 'Denied' };
    case 'exhausted':
      return { cls: 'inactive', label: 'Exhausted' };
    case 'expired':
      return { cls: 'terminated', label: 'Expired' };
    default:
      return { cls: 'terminated', label: s ? s.charAt(0).toUpperCase() + s.slice(1) : '—' };
  }
};

/** Authorizations tab (Phase 16) — payor auth log + session use. PHI-gated.
 *  Card layout mirrors the ehr-revised mockup 1:1. */
function AuthorizationsPanel({
  contactId,
  onChanged,
  readOnly = false,
}: {
  contactId: string;
  /** ehr#565: a save changed the counts (the header's Auth Remaining refetches). */
  onChanged?: () => void;
  /** ehr#730: a View tab (Share & Access): no Add or Edit. */
  readOnly?: boolean;
}) {
  const { user } = useSession();
  const [rows, setRows] = useState<Authorization[]>([]);
  const [users, setUsers] = useState<NoteUser[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [showForm, setShowForm] = useState(false);
  // ehr#365: the authorization being edited (null = adding a new one).
  const [editId, setEditId] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);

  // Add-authorization form state.
  const [authNo, setAuthNo] = useState('');
  const [payer, setPayer] = useState('');
  const [authStatus, setAuthStatus] = useState('requested');
  const [serviceType, setServiceType] = useState('');
  // ehr#565 (Chase 10-25): CPT and diagnosis are picked from lists, so they're held as code lists.
  const [cptCodes, setCptCodes] = useState<string[]>([]);
  const [diagnosis, setDiagnosis] = useState<string[]>([]);
  const [frequency, setFrequency] = useState('');
  const [priorAuth, setPriorAuth] = useState('');
  const [dateAuthorized, setDateAuthorized] = useState('');
  const [requestingProvider, setRequestingProvider] = useState('');
  const [referringProvider, setReferringProvider] = useState('');
  const [unitsAuthorized, setUnitsAuthorized] = useState('');
  const [sessions, setSessions] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [method, setMethod] = useState('Portal');
  const [notes, setNotes] = useState('');
  // ehr#354 / #355: what's wrong with the form, by field ('form' = the save itself). A rejected
  // save keeps everything typed and says why; it used to blank the tab.
  const [formErr, setFormErr] = useState<
    Partial<Record<'sessions' | 'expiresOn' | 'form', string>>
  >({});
  const [saving, setSaving] = useState(false);
  // ehr#681 follow-up: the save card. A saved authorization closes the form, so its card hangs
  // under Add Authorization; "Not saved" stays above the form's Save button.
  const [saveNote, setSaveNote] = useState<SaveNotice | null>(null);
  // ehr#565 (Chase 10-25): Save with a required field empty names what's missing (until it's filled)
  // and outlines those fields in red for a few seconds.
  const [missing, setMissing] = useState<AuthRequiredField[]>([]);
  const [flash, setFlash] = useState<AuthRequiredField[]>([]);
  const flashTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(flashTimer.current), []);
  // ehr#565: the pick lists - the Calendar's chargemaster (CPT) and RCM's payors, as the Calendar
  // and Insurance / Payor use them. null = still loading; [] = none, or couldn't load.
  const [cptList, setCptList] = useState<CptCode[] | null>(null);
  const [payorList, setPayorList] = useState<RcmPayorOption[] | null>(null);
  const clearErr = (k: keyof typeof formErr) =>
    setFormErr((e) => {
      if (!e[k]) return e;
      const next = { ...e };
      delete next[k];
      return next;
    });

  const canEdit = !readOnly && Boolean(user && canManage(user.role));

  const load = () => {
    api
      .get<Authorization[]>(`/contacts/${contactId}/authorizations`)
      .then((r) => {
        setRows(r);
        setStatus('ready');
      })
      .catch((e: unknown) =>
        setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error'),
      );
  };
  useEffect(load, [contactId]);
  useEffect(() => {
    // ehr#623: names for clinicians too (they can't read /settings/users).
    void fetchNoteUsers().then(setUsers);
  }, []);

  const userName = (id: string | null) => {
    if (!id) return '—';
    const u = users.find((x) => x.id === id);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : '—';
  };

  // ehr#565: the lists load once, the first time the form opens.
  const listsAsked = useRef(false);
  useEffect(() => {
    if (!showForm || listsAsked.current) return;
    listsAsked.current = true;
    api
      .get<{ available: boolean; codes: CptCode[] }>('/cpt-codes')
      .then((r) => setCptList(r.codes ?? []))
      .catch(() => setCptList([]));
    api
      .get<RcmPayorOption[]>('/payors')
      .then(setPayorList)
      .catch(() => setPayorList([]));
  }, [showForm]);

  /** Filling a required field takes it off the "Fill in …" message. */
  const filled = (k: AuthRequiredField) => {
    setMissing((m) => (m.includes(k) ? m.filter((x) => x !== k) : m));
    setFlash((f) => (f.includes(k) ? f.filter((x) => x !== k) : f));
  };
  const reqCls = (k: AuthRequiredField) => `demo-field${flash.includes(k) ? ' auth-missing' : ''}`;

  const resetForm = () => {
    setAuthNo('');
    setPayer('');
    setAuthStatus('requested');
    setServiceType('');
    setCptCodes([]);
    setDiagnosis([]);
    setFrequency('');
    setPriorAuth('');
    setDateAuthorized('');
    setRequestingProvider('');
    setReferringProvider('');
    setUnitsAuthorized('');
    setSessions('');
    setStartsOn('');
    setExpiresOn('');
    setMethod('Portal');
    setNotes('');
    setFormErr({});
    setMissing([]);
    setFlash([]);
    setEditId(null);
  };

  /** ehr#365: open the form on an existing authorization, its values filled in. */
  const startEdit = (r: Authorization) => {
    const day = (s?: string | null) => (s ? s.slice(0, 10) : ''); // stored as midnight UTC of the day
    setFormErr({});
    setMissing([]);
    setFlash([]);
    setEditId(r.id);
    setAuthNo(r.authNo ?? '');
    setPayer(r.payer ?? '');
    setAuthStatus(r.status || 'requested');
    setServiceType(r.serviceType ?? '');
    setCptCodes(splitCodes(r.cptCodes ?? []));
    setDiagnosis(splitCodes(r.diagnosis));
    setFrequency(r.frequency ?? '');
    setPriorAuth(r.priorAuthRequired == null ? '' : r.priorAuthRequired ? 'yes' : 'no');
    setDateAuthorized(day(r.dateAuthorized));
    setRequestingProvider(r.requestingProvider ?? '');
    setReferringProvider(r.referringProvider ?? '');
    setUnitsAuthorized(r.unitsAuthorized ?? '');
    setSessions(r.sessionsApproved != null ? String(r.sessionsApproved) : '');
    setStartsOn(day(r.startsOn));
    setExpiresOn(day(r.expiresOn));
    setMethod(r.method || 'Portal');
    setNotes(r.notes ?? '');
    setShowForm(true);
    requestAnimationFrame(() =>
      formRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }),
    );
  };

  const add = () => {
    if (saving) return;
    const empty = missingAuthFields({ authNo, payer });
    setMissing(empty);
    if (empty.length) {
      setFlash(empty);
      window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setFlash([]), 3000);
      return;
    }
    const errs: typeof formErr = {};
    const n = sessions.trim();
    if (n && !(/^\d+$/.test(n) && Number(n) <= 9999))
      errs.sessions = 'Enter a whole number from 0 to 9999.';
    // ISO YYYY-MM-DD strings compare as dates.
    if (startsOn && expiresOn && expiresOn < startsOn)
      errs.expiresOn = 'The Expiry Date must be on or after the Effective Date.';
    setFormErr(errs);
    if (Object.keys(errs).length) return;
    setSaving(true);
    setSaveNote(null);
    const cpt = splitCodes(cptCodes);
    const dx = splitCodes(diagnosis).join(', ');
    // ehr#365: an edit sends every field, a cleared one as null.
    const save = editId
      ? api.patch(`/authorizations/${editId}`, {
          authNo: authNo.trim(),
          payer: payer.trim(),
          status: authStatus,
          serviceType: serviceType.trim() || null,
          cptCodes: cpt.length ? cpt : null,
          diagnosis: dx || null,
          frequency: frequency.trim() || null,
          priorAuthRequired: priorAuth === '' ? null : priorAuth === 'yes',
          dateAuthorized: dateAuthorized || null,
          requestingProvider: requestingProvider.trim() || null,
          referringProvider: referringProvider.trim() || null,
          unitsAuthorized: unitsAuthorized.trim() || null,
          sessionsApproved: n ? Number(n) : null,
          startsOn: startsOn || null,
          expiresOn: expiresOn || null,
          method: method || null,
          notes: notes.trim() || null,
        })
      : api.post(`/contacts/${contactId}/authorizations`, {
          authNo: authNo.trim(),
          payer: payer.trim(),
          status: authStatus,
          serviceType: serviceType.trim() || undefined,
          cptCodes: cpt.length ? cpt : undefined,
          diagnosis: dx || undefined,
          frequency: frequency.trim() || undefined,
          priorAuthRequired: priorAuth === '' ? undefined : priorAuth === 'yes',
          dateAuthorized: dateAuthorized || undefined,
          requestingProvider: requestingProvider.trim() || undefined,
          referringProvider: referringProvider.trim() || undefined,
          unitsAuthorized: unitsAuthorized.trim() || undefined,
          sessionsApproved: n ? Number(n) : undefined,
          startsOn: startsOn || undefined,
          expiresOn: expiresOn || undefined,
          method: method || undefined,
          notes: notes.trim() || undefined,
        });
    // ehr#681 follow-up: Save reads "Saving…" for at least 2 s, then the card.
    void runSave(save, {
      ok: authSavedText(!!editId),
      err: authErrText,
      at: 'auth',
      okBelow: true,
    }).then((r) => {
      setSaving(false);
      setSaveNote(r.notice);
      if (r.ok) {
        resetForm();
        setShowForm(false);
        load();
        onChanged?.();
      } else {
        const e = r.error;
        setFormErr({
          form:
            e instanceof ApiError && e.code !== 'VALIDATION' && e.message
              ? `Not saved: ${e.message}`
              : 'Not saved. Check the fields and try again.',
        });
      }
    });
  };

  // ehr#565: the form's Used comes from the saved authorization (0 for a new one); Remaining follows
  // what's typed in Sessions (or Units) Authorized.
  const editing = editId ? rows.find((r) => r.id === editId) : undefined;
  const formUsed = editing ? (editing.used ?? (Number(editing.sessionsUsed) || 0)) : 0;
  const formAllowed = authAllowed(sessions, unitsAuthorized);
  const formRemaining = formAllowed != null ? Math.max(0, formAllowed - formUsed) : null;

  if (status === 'loading') return <p className="muted">Loading authorizations…</p>;
  if (status === 'forbidden') return <p className="muted">Your role cannot view PHI (403).</p>;
  if (status === 'error') return <p className="muted">Could not load authorizations.</p>;

  return (
    <div className="notes-panel">
      <div className="tab-toolbar save-host">
        <div className="tab-toolbar-label">Authorizations · {rows.length} on file</div>
        {canEdit && (
          <SaveCardAnchor
            notice={saveNote?.below ? saveNote : null}
            onClose={() => setSaveNote(null)}
          >
            <button
              className="auth-add-btn"
              onClick={() => {
                // Adding while an edit is open starts a blank form instead.
                if (editId) {
                  resetForm();
                  setShowForm(true);
                } else setShowForm((v) => !v);
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Authorization
            </button>
          </SaveCardAnchor>
        )}
      </div>

      {showForm && (
        <div className="auth-form-card" ref={formRef}>
          <div className="auth-form-title">
            {editId ? 'Edit Authorization' : 'New Authorization'}
          </div>
          <div className="auth-form-grid">
            <label className={reqCls('authNo')}>
              <span className="muted">Auth Number</span>
              <input
                value={authNo}
                aria-invalid={missing.includes('authNo')}
                onChange={(e) => {
                  setAuthNo(e.target.value);
                  if (e.target.value.trim()) filled('authNo');
                }}
                placeholder="AUTH-2026-00500"
              />
            </label>
            <div className={reqCls('payer')}>
              <span className="muted">Payor / Insurance</span>
              <Dd
                value={payer}
                ariaLabel="Payor / Insurance"
                floating
                placeholder={
                  payorList === null
                    ? 'Loading payors…'
                    : payorList.length
                      ? 'Pick a payor…'
                      : 'No payors turned on in RCM (RCM → Payors → Active)'
                }
                options={authPayorOptions(payorList ?? [], payer)}
                onChange={(v) => {
                  setPayer(v);
                  if (v.trim()) filled('payer');
                }}
              />
            </div>
            <label className="demo-field">
              <span className="muted">Status</span>
              <Dd
                value={authStatus}
                searchable={false}
                options={[
                  { value: 'requested', label: 'Requested' },
                  { value: 'approved', label: 'Active' },
                  { value: 'denied', label: 'Denied' },
                  { value: 'expired', label: 'Expired' },
                  { value: 'exhausted', label: 'Exhausted' },
                ]}
                onChange={setAuthStatus}
              />
            </label>
            <label className="demo-field">
              <span className="muted">Service Type</span>
              <input
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                placeholder="Outpatient Mental Health"
              />
            </label>
            <div className="demo-field">
              <span className="muted">CPT Codes</span>
              <MultiDd
                values={cptCodes}
                ariaLabel="CPT Codes"
                floating
                clearable
                placeholder={cptList === null ? 'Loading CPT codes…' : 'Pick CPT codes…'}
                emptyText="No CPT codes yet. Add them in Manage Settings → CPT codes."
                options={cptOptions(cptList ?? [], cptCodes)}
                onChange={setCptCodes}
              />
            </div>
            <div className="demo-field">
              <span className="muted">Diagnosis (ICD-10)</span>
              <MultiDd
                values={diagnosis}
                ariaLabel="Diagnosis (ICD-10)"
                floating
                clearable
                placeholder="Pick diagnosis codes…"
                options={dxOptions(ICD10_CATALOG, diagnosis)}
                onChange={setDiagnosis}
              />
            </div>
            <label className="demo-field">
              <span className="muted">Sessions Authorized</span>
              <input
                type="number"
                min={0}
                step={1}
                value={sessions}
                className={formErr.sessions ? 'demo-input-invalid' : undefined}
                aria-invalid={Boolean(formErr.sessions)}
                onChange={(e) => {
                  setSessions(e.target.value);
                  clearErr('sessions');
                }}
                placeholder="24"
              />
              {formErr.sessions && <span className="demo-field-err">{formErr.sessions}</span>}
            </label>
            <label className="demo-field">
              <span className="muted">Effective Date</span>
              <DateField
                value={startsOn}
                onChange={(v) => {
                  setStartsOn(v);
                  clearErr('expiresOn');
                }}
              />
            </label>
            <label className={`demo-field${formErr.expiresOn ? ' auth-date-invalid' : ''}`}>
              <span className="muted">Expiry Date</span>
              <DateField
                value={expiresOn}
                onChange={(v) => {
                  setExpiresOn(v);
                  clearErr('expiresOn');
                }}
              />
              {formErr.expiresOn && <span className="demo-field-err">{formErr.expiresOn}</span>}
            </label>
            <label className="demo-field">
              <span className="muted">Frequency</span>
              <input
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                placeholder="Weekly"
              />
            </label>
            <label className="demo-field">
              <span className="muted">Prior Auth Required</span>
              <Dd
                value={priorAuth}
                searchable={false}
                placeholder="—"
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                ]}
                onChange={setPriorAuth}
              />
            </label>
            <label className="demo-field">
              <span className="muted">Date Authorized</span>
              <DateField value={dateAuthorized} onChange={setDateAuthorized} />
            </label>
            <label className="demo-field">
              <span className="muted">Method Collected</span>
              <Cd
                value={method}
                options={['Portal', 'Phone', 'Fax', 'EDI', 'Email']}
                onChange={setMethod}
              />
            </label>
            <label className="demo-field">
              <span className="muted">Units Authorized</span>
              <input
                value={unitsAuthorized}
                onChange={(e) => setUnitsAuthorized(e.target.value)}
                placeholder="48 units (optional)"
              />
            </label>
            <label className="demo-field">
              <span className="muted">Requesting Provider</span>
              <input
                value={requestingProvider}
                onChange={(e) => setRequestingProvider(e.target.value)}
                placeholder="Dr. Aisha Patel, PsyD"
              />
            </label>
            <label className="demo-field">
              <span className="muted">Referring Provider</span>
              <input
                value={referringProvider}
                onChange={(e) => setReferringProvider(e.target.value)}
                placeholder="Dr. Marcus Lee, MD"
              />
            </label>
            {/* ehr#565 (AUTH_MOCK_10-20-V1): counted from checked-out sessions, not typed. */}
            <div className="demo-field auth-count-field">
              <span className="muted">Authorizations Used</span>
              <div className="auth-count-box" aria-label="Authorizations Used">
                {formUsed}
              </div>
            </div>
            <div className="demo-field auth-count-field">
              <span className="muted">Authorizations Remaining</span>
              <div className="auth-count-box" aria-label="Authorizations Remaining">
                {formRemaining == null ? '—' : formRemaining}
              </div>
            </div>
            <label className="demo-field auth-form-full">
              <span className="muted">Notes</span>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Approval conditions, renewal instructions, denial reason…"
              />
            </label>
          </div>
          {missing.length > 0 && (
            <p className="demo-field-err auth-form-err" role="alert">
              {missingAuthMessage(missing)}
            </p>
          )}
          {formErr.form && (
            <p className="demo-field-err auth-form-err" role="alert">
              {formErr.form}
            </p>
          )}
          <div className="auth-form-actions save-host">
            <SaveButton
              label={editId ? 'Save changes' : 'Save Authorization'}
              busy={saving}
              notice={saveNote && !saveNote.below ? saveNote : null}
              onCloseNotice={() => setSaveNote(null)}
              onClick={add}
            />
            <button
              className="btn"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="muted">No authorizations on file.</p>
      ) : (
        <div className="auth-log">
          {rows.map((r) => {
            // ehr#565: used / remaining as the API counted them (sessions, else units).
            const used = r.used ?? (Number(r.sessionsUsed) || 0);
            const total =
              r.authorized !== undefined
                ? r.authorized
                : r.sessionsApproved != null
                  ? Number(r.sessionsApproved)
                  : null;
            const remaining = r.remaining ?? (total != null ? Math.max(0, total - used) : 0);
            const kind = r.unitKind === 'units' ? 'units' : 'sessions';
            const pct = total && total > 0 ? Math.round((used / total) * 100) : 0;
            const badge = authBadge(r.status);
            return (
              <div className="auth-card" key={r.id}>
                <div className="auth-card-header">
                  <div className="auth-card-num">{r.authNo || 'No Auth #'}</div>
                  <div className="auth-card-head-right">
                    <span className={`elig-badge ${badge.cls}`}>{badge.label}</span>
                    {canEdit && (
                      <button
                        type="button"
                        className="auth-edit-btn"
                        onClick={() => startEdit(r)}
                        aria-label={`Edit authorization ${r.authNo || ''}`.trim()}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
                <div className="auth-grid">
                  <div className="auth-field">
                    <div className="auth-label">Payor</div>
                    <div className="auth-value">{r.payer || '—'}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Service Type</div>
                    <div className="auth-value">{r.serviceType || '—'}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">CPT Codes</div>
                    <div className="auth-value">
                      {r.cptCodes && r.cptCodes.length ? r.cptCodes.join(', ') : '—'}
                    </div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Diagnosis (ICD-10)</div>
                    <div className="auth-value muted">{r.diagnosis || '—'}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Effective Date</div>
                    <div className="auth-value muted">{fmtAuthDay(r.startsOn)}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Expiry Date</div>
                    <div className="auth-value muted">{fmtAuthDay(r.expiresOn)}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Frequency</div>
                    <div className="auth-value muted">{r.frequency || '—'}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Prior Auth Required</div>
                    <div className="auth-value muted">
                      {r.priorAuthRequired == null ? '—' : r.priorAuthRequired ? 'Yes' : 'No'}
                    </div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Date Authorized</div>
                    <div className="auth-value muted">{fmtAuthDay(r.dateAuthorized)}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Method Collected</div>
                    <div className="auth-value muted">{r.method || '—'}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Collected By</div>
                    <div className="auth-value muted">{userName(r.createdById)}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Date Collected</div>
                    <div className="auth-value muted">{fmtAuthDate(r.createdAt)}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Requesting Provider</div>
                    <div className="auth-value muted">{r.requestingProvider || '—'}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Referring Provider</div>
                    <div className="auth-value muted">{r.referringProvider || '—'}</div>
                  </div>
                  <div className="auth-field">
                    <div className="auth-label">Units Authorized</div>
                    <div className="auth-value muted">{r.unitsAuthorized || '—'}</div>
                  </div>
                  <div className="auth-field auth-count">
                    <div className="auth-label">Authorizations Used</div>
                    <div className="auth-value">{used}</div>
                  </div>
                  <div className="auth-field auth-count">
                    <div className="auth-label">Authorizations Remaining</div>
                    <div className="auth-value">{total != null ? remaining : '—'}</div>
                  </div>
                </div>
                {total != null && total > 0 && (
                  <div className="auth-sessions-row">
                    <div className="auth-sessions-bar">
                      <div className="auth-sessions-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="auth-sessions-label">
                      {used} of {total} {kind} used · <strong>{remaining} remaining</strong> ({pct}
                      %)
                    </div>
                  </div>
                )}
                {r.notes && <div className="auth-card-notes">{r.notes}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface Ledger {
  charges: { id: string; amountCents: number; description: string | null; status: string }[];
  payments: { id: string; amountCents: number; source: string }[];
  balanceCents: number;
}
const money = (c: number) => `$${(c / 100).toFixed(2)}`;

/** Billing tab (Phase 18) — per-contact ledger + add charge/payment. */
function BillingPanel({ contactId }: { contactId: string }) {
  const [led, setLed] = useState<Ledger | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [charge, setCharge] = useState('');
  const [payment, setPayment] = useState('');

  const load = () => {
    api
      .get<Ledger>(`/contacts/${contactId}/billing`)
      .then((l) => {
        setLed(l);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  };
  useEffect(load, [contactId]);

  const dollarsToCents = (s: string) => Math.round(parseFloat(s) * 100);
  const addCharge = () => {
    const cents = dollarsToCents(charge);
    if (!cents || cents <= 0) return;
    api
      .post('/billing/charges', { contactId, amountCents: cents, description: 'Manual charge' })
      .then(() => {
        setCharge('');
        load();
      })
      .catch(() => setStatus('error'));
  };
  const addPayment = () => {
    const cents = dollarsToCents(payment);
    if (!cents || cents <= 0) return;
    api
      .post('/billing/payments', { contactId, amountCents: cents, source: 'client' })
      .then(() => {
        setPayment('');
        load();
      })
      .catch(() => setStatus('error'));
  };

  if (status === 'loading') return <p className="muted">Loading billing…</p>;
  if (status === 'error' || !led) return <p className="muted">Could not load billing.</p>;

  return (
    <div className="notes-panel">
      <div className="stat" style={{ marginBottom: '1rem' }}>
        <div className="stat-value">{money(led.balanceCents)}</div>
        <div className="stat-label">Outstanding balance</div>
      </div>
      <div className="demo-grid">
        <label className="demo-field">
          <span className="muted">Add charge ($)</span>
          <input type="number" value={charge} onChange={(e) => setCharge(e.target.value)} />
        </label>
        <label className="demo-field">
          <span className="muted">Record payment ($)</span>
          <input type="number" value={payment} onChange={(e) => setPayment(e.target.value)} />
        </label>
      </div>
      <div className="modal-actions">
        <button className="btn" onClick={addCharge} disabled={!charge}>
          Add charge
        </button>
        <button className="btn" onClick={addPayment} disabled={!payment}>
          Record payment
        </button>
      </div>
      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {led.charges.map((c) => (
            <tr key={c.id}>
              <td>{c.description ?? 'Charge'}</td>
              <td style={{ textAlign: 'right' }}>{money(c.amountCents)}</td>
            </tr>
          ))}
          {led.payments.map((p) => (
            <tr key={p.id}>
              <td className="muted">Payment ({p.source})</td>
              <td style={{ textAlign: 'right' }} className="muted">
                −{money(p.amountCents)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface FileRow {
  id: string;
  name: string;
  mime: string;
  size: number;
  createdAt: string;
  /** ehr#209: roi | records | labs | other (null on older files). */
  category?: string | null;
  note?: string | null;
  /** alertshub#9: when it was shared with the client (the portal's Records → Documents); null = staff only. */
  sharedWithClientAt?: string | null;
  /** ehr#736: set on an archived file (listed only with Show archived). */
  archivedAt?: string | null;
}
/** ehr#209: a file chosen in the upload card, waiting for Upload (the portal's staging). */
interface PendingFile {
  file: File;
  url: string;
  kind: 'image' | 'pdf' | 'other';
}
const fileCategoryLabel = (c: string | null | undefined) =>
  c && c in FILE_CATEGORY_LABELS ? FILE_CATEGORY_LABELS[c as FileCategory] : '';
const fmtSize = (n: number) =>
  n < 1024
    ? `${n} B`
    : n < 1048576
      ? `${(n / 1024).toFixed(1)} KB`
      : `${(n / 1048576).toFixed(1)} MB`;

/** Files tab (Phase 17) — per-contact documents, encrypted at rest, PHI-gated. */
const DOC_ICON: Record<string, string> = {
  pdf: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>',
  image:
    '<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>',
  sheet:
    '<rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="9" x2="9" y2="21"/>',
  doc: '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/>',
  file: '<path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><polyline points="13 2 13 9 20 9"/>',
};
const docKind = (mime: string, name: string): string => {
  const m = (mime || '').toLowerCase();
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (m.includes('pdf') || ext === 'pdf') return 'pdf';
  if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext))
    return 'image';
  if (
    m.includes('sheet') ||
    m.includes('excel') ||
    m.includes('csv') ||
    ['xlsx', 'xls', 'csv'].includes(ext)
  )
    return 'sheet';
  if (m.includes('word') || m.includes('document') || ['doc', 'docx'].includes(ext)) return 'doc';
  return 'file';
};

function FilesPanel({
  contactId,
  contactName,
  isClient = false,
  refreshKey = 0,
  readOnly = false,
}: {
  contactId: string;
  contactName: string;
  /** alertshub#9: only a client's files can be shared with them. */
  isClient?: boolean;
  /** ehr#730: a View tab (Share & Access): preview and download only (no upload, share, archive). */
  readOnly?: boolean;
  /** ehr#635: the chart's live stamp (liveTick). A file the client uploads in the portal, or a
   * teammate's upload, share or delete, moves it and the list reloads in place. */
  refreshKey?: number;
}) {
  const [rows, setRows] = useState<FileRow[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [busy, setBusy] = useState(false);
  // Upload feedback (rejected / failed files) — #12.
  const [uploadMsg, setUploadMsg] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // ehr#209: the search box in the Files header (name, type, upload date, category, note).
  const [q, setQ] = useState('');
  useEffect(() => setQ(''), [contactId]);
  // ehr#209 (Chase 10-06): upload like the client portal's Documents. Pick a category and an
  // optional note, choose a file; it waits with Preview / Cancel / Upload until Upload is pressed.
  const [cat, setCat] = useState<FileCategory>('records');
  const [note, setNote] = useState('');
  const [pending, setPending] = useState<PendingFile | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  // ehr#584: an uploaded file open in the preview window
  const [viewing, setViewing] = useState<FileRow | null>(null);
  useEffect(() => setViewing(null), [contactId]);
  // ehr#736: Show archived lists the archived files (Preview, Download, Restore) under the rest.
  const [showArchived, setShowArchived] = useState(false);
  const [archivedRows, setArchivedRows] = useState<FileRow[] | null>(null);
  const loadArchived = () =>
    api
      .get<FileRow[]>(archivedUrl(`/contacts/${contactId}/files`))
      .then(setArchivedRows)
      .catch(() => setArchivedRows([]));
  useEffect(() => {
    if (showArchived) void loadArchived();
    else setArchivedRows(null);
  }, [showArchived, contactId]);
  const stage = (next: PendingFile | null) =>
    setPending((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return next;
    });
  useEffect(() => () => stage(null), [contactId]);

  // ehr#635: `quiet` is the live reload. A failed one keeps the list on screen (the next stamp
  // change tries again) instead of swapping it for an error.
  const load = (quiet = false) => {
    api
      .get<FileRow[]>(`/contacts/${contactId}/files`)
      .then((r) => {
        setRows(r);
        setStatus('ready');
      })
      .catch((e: unknown) => {
        if (quiet) return;
        setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error');
      });
  };
  useEffect(() => load(), [contactId]);
  useEffect(() => {
    if (refreshKey) load(true);
    // load reads only contactId, which the effect above already follows
  }, [refreshKey]);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    // ehr#209: copy the picked file out BEFORE clearing the input. Clearing it (so the same
    // file can be picked again) empties the live FileList.
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadMsg('');
    // Reject a type that isn't allowed or a file over 4.5 MB up front (#12, ehr#321).
    const problem = fileUploadProblem(file);
    if (problem) {
      setUploadMsg(problem);
      return;
    }
    const kind = file.type.startsWith('image/')
      ? 'image'
      : file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
        ? 'pdf'
        : 'other';
    stage({ file, url: URL.createObjectURL(file), kind });
  };

  const send = () => {
    if (!pending || busy) return;
    const { file } = pending;
    setBusy(true);
    setUploadMsg('');
    const reader = new FileReader();
    reader.onload = () => {
      const dataBase64 = String(reader.result).split(',')[1] ?? '';
      api
        .post(`/contacts/${contactId}/files`, {
          name: file.name,
          mime: file.type || 'application/octet-stream',
          dataBase64,
          category: cat,
          ...(note.trim() ? { note: note.trim() } : {}),
        })
        .then(() => {
          stage(null);
          setNote('');
          setUploadMsg(`Uploaded ${file.name}.`);
          load();
        })
        // ehr#321: the API says why (type not allowed, not a real .xlsx, macros, too large)
        .catch((e: unknown) =>
          setUploadMsg(
            e instanceof ApiError && e.status !== 500
              ? e.message
              : `${file.name}: upload failed. Try again.`,
          ),
        )
        .finally(() => setBusy(false));
    };
    reader.onerror = () => {
      setUploadMsg(`${file.name} couldn’t be read.`);
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  const download = (f: FileRow) =>
    api
      .get<{ name: string; mime: string; dataBase64: string }>(`/files/${f.id}/content`)
      .then((d) => {
        const a = document.createElement('a');
        a.href = `data:${d.mime};base64,${d.dataBase64}`;
        a.download = d.name;
        a.click();
      })
      .catch(() => undefined);

  // ehr#584 (Chase 10-21): asks first, naming the file. ehr#736 (Jay 10-31): Archive, never
  // Delete; Show archived brings it back into view and Restore puts it back.
  const archive = async (f: FileRow) => {
    if (
      !(await askConfirm({
        message: `Archive ${f.name}? It leaves the Files list${
          f.sharedWithClientAt ? ' and the client portal' : ''
        }. Turn on Show archived to see it again or restore it.`,
        confirmLabel: 'Archive',
        danger: false,
      }))
    )
      return;
    archiveItem({ kind: 'file', id: f.id })
      .then(() => {
        load();
        if (showArchived) void loadArchived();
      })
      .catch((e: unknown) => showError(errorText(e, 'Could not archive this file')));
  };
  const restore = (f: FileRow) =>
    restoreItem({ kind: 'file', id: f.id })
      .then(() => {
        load();
        void loadArchived();
      })
      .catch((e: unknown) => showError(errorText(e, 'Could not restore this file')));

  // alertshub#9 (Jay 10-13): share a file with the client: it shows in their portal's Records →
  // Documents ("Shared by your practice") and their bell says so. Asks first. ehr#584 (Chase
  // 10-23): stopping asks first too, since the file leaves the client's portal.
  const setShared = async (f: FileRow, shared: boolean) => {
    if (
      shared &&
      !(await askConfirm({
        message: `Share ${f.name} with ${contactName || 'this client'}? They'll see it in the client portal, in Records under Documents, and get a notice in their bell.`,
        confirmLabel: 'Share',
        danger: false,
      }))
    )
      return;
    if (!shared && !(await askConfirm(stopSharingConfirm(f.name, contactName)))) return;
    api
      .patch<{ id: string; sharedWithClientAt: string | null }>(`/files/${f.id}/share`, { shared })
      .then((r) =>
        setRows((rs) =>
          rs.map((x) => (x.id === f.id ? { ...x, sharedWithClientAt: r.sharedWithClientAt } : x)),
        ),
      )
      .catch((e: unknown) =>
        showError(e instanceof ApiError ? e.message : 'Could not change sharing for this file'),
      );
  };

  if (status === 'loading') return <p className="muted">Loading files…</p>;
  if (status === 'forbidden')
    return <p className="muted">Your role cannot view contact files (403).</p>;
  if (status === 'error') return <p className="muted">Could not load files.</p>;

  const totalBytes = rows.reduce((n, f) => n + (f.size ?? 0), 0);
  const searching = terms(q).length > 0;
  const shown = searching
    ? rows.filter((f) =>
        matchFile(f, docKind(f.mime ?? '', f.name), q, [fileCategoryLabel(f.category), f.note]),
      )
    : rows;

  return (
    <div className="docs-panel">
      <input
        ref={inputRef}
        id="docs-file-input"
        type="file"
        accept={FILE_ACCEPT}
        style={{ display: 'none' }}
        onChange={onPick}
        disabled={busy || readOnly}
      />

      <div className="docs-head">
        <span className="docs-head-label">Files</span>
        {/* ehr#209: always shown, top right (disabled until there are files to search). */}
        <label className={`docs-search${rows.length ? '' : ' is-empty'}`}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            value={q}
            placeholder="Search files by name, type, date or note…"
            aria-label="Search files"
            disabled={!rows.length}
            title={rows.length ? undefined : 'No files to search yet'}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setQ('');
            }}
          />
        </label>
      </div>

      {/* ehr#209: the client portal's Documents upload, for staff. */}
      <div className="fu-card" hidden={readOnly}>
        <div className="fu-top">
          <span className="fu-ic" aria-hidden>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </span>
          <div className="fu-copy">
            <div className="fu-t">Upload a document</div>
            <div className="fu-s">
              Documents, spreadsheets or images up to {FILE_MAX_LABEL}, kept in {contactName}
              &apos;s chart.
            </div>
          </div>
          <div className="fu-controls">
            <div className="fu-cat">
              <Dd
                value={cat}
                onChange={(v) => setCat(v as FileCategory)}
                options={FILE_CATEGORIES.map((c) => ({ value: c, label: FILE_CATEGORY_LABELS[c] }))}
                searchable={false}
                ariaLabel="Category"
              />
            </div>
            <button
              type="button"
              className="btn fu-choose"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
            >
              + Choose file
            </button>
          </div>
        </div>
        <label className="fu-note">
          <span>
            Note <em>(optional)</em>
          </span>
          <input
            type="text"
            maxLength={FILE_NOTE_MAX}
            value={note}
            placeholder="e.g. Lab results from Dr. Lee, March"
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        {pending && (
          <div className="fu-pending" role="group" aria-label="File ready to upload">
            <span className={`fu-thumb fu-thumb-${pending.kind}`}>
              {pending.kind === 'image' ? (
                <img src={pending.url} alt="" />
              ) : pending.kind === 'pdf' ? (
                'PDF'
              ) : (
                'FILE'
              )}
            </span>
            <div className="fu-pending-main">
              <div className="fu-pending-name">{pending.file.name}</div>
              <div className="fu-pending-sub">
                {fmtSize(pending.file.size)} · {FILE_CATEGORY_LABELS[cat]}
                {note.trim() ? ' · with a note' : ''}
              </div>
            </div>
            <div className="fu-pending-actions">
              <button type="button" className="btn sm" onClick={() => setPreviewOpen(true)}>
                Preview
              </button>
              <button type="button" className="btn sm" onClick={() => stage(null)} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="btn sm primary" onClick={send} disabled={busy}>
                {busy ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        )}
        {uploadMsg && <div className="fu-msg">{uploadMsg}</div>}
      </div>
      {previewOpen && pending && (
        <Modal
          title={pending.file.name}
          onClose={() => setPreviewOpen(false)}
          className="fu-preview-modal"
        >
          {pending.kind === 'image' ? (
            <img className="fu-preview-img" src={pending.url} alt={pending.file.name} />
          ) : pending.kind === 'pdf' ? (
            <iframe className="fu-preview-pdf" src={pending.url} title={pending.file.name} />
          ) : (
            <p className="muted">
              There&apos;s no preview for this kind of file. It uploads as it is.
            </p>
          )}
        </Modal>
      )}

      {rows.length === 0 ? (
        <div className="docs-empty">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <h4>No files yet</h4>
          <p>Attach a signed BAA, contract, or proposal for {contactName}.</p>
        </div>
      ) : (
        <>
          <div className="docs-meta-line">
            {searching
              ? `${shown.length} of ${rows.length} file${rows.length === 1 ? '' : 's'} match`
              : `${rows.length} file${rows.length === 1 ? '' : 's'} · ${fmtSize(totalBytes)}`}
            {searching && (
              <button className="docs-search-clear" onClick={() => setQ('')}>
                Clear search
              </button>
            )}
          </div>
          {searching && shown.length === 0 && (
            <div className="docs-noresults">No files match “{q.trim()}”.</div>
          )}
          {shown.map((f) => {
            const kind = docKind(f.mime ?? '', f.name);
            return (
              <div className="doc-row" key={f.id}>
                <span className={`doc-icon doc-icon-${kind}`}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    dangerouslySetInnerHTML={{ __html: DOC_ICON[kind] ?? DOC_ICON.file! }}
                  />
                </span>
                <div className="doc-main" onClick={() => download(f)}>
                  <div className="doc-name">{f.name}</div>
                  {f.note && <div className="doc-note">{f.note}</div>}
                  <div className="doc-sub">
                    {f.category && <span className="doc-cat">{fileCategoryLabel(f.category)}</span>}
                    {f.sharedWithClientAt && (
                      <span className="doc-cat doc-shared" title="The client sees it in the portal">
                        Shared with client
                      </span>
                    )}
                    {fmtSize(f.size)}
                    {f.createdAt
                      ? ` · ${new Date(f.createdAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`
                      : ''}
                  </div>
                </div>
                <div className="doc-actions">
                  {isClient && !readOnly && (
                    <button
                      className="doc-share-btn"
                      onClick={() => void setShared(f, !f.sharedWithClientAt)}
                    >
                      {f.sharedWithClientAt ? 'Stop sharing' : 'Share with client'}
                    </button>
                  )}
                  {/* ehr#584: Preview, as the client previews their files in the portal */}
                  <button
                    className="doc-btn"
                    onClick={() => setViewing(f)}
                    title="Preview"
                    aria-label={`Preview ${f.name}`}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                  <button
                    className="doc-btn"
                    onClick={() => download(f)}
                    title="Download"
                    aria-label={`Download ${f.name}`}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </button>
                  <button
                    className="doc-btn"
                    hidden={readOnly}
                    onClick={() => void archive(f)}
                    title="Archive"
                    aria-label={`Archive ${f.name}`}
                  >
                    <ArchiveIcon />
                  </button>
                </div>
              </div>
            );
          })}
        </>
      )}
      <div className="archived-bar">
        <ShowArchivedToggle
          on={showArchived}
          onChange={setShowArchived}
          count={archivedRows?.length}
        />
      </div>
      {showArchived && archivedRows && (
        <div className="archived-list" aria-label="Archived files">
          {archivedRows.length === 0 ? (
            <p className="muted">No archived files.</p>
          ) : (
            archivedRows.map((f) => (
              <div className="doc-row archived" key={f.id}>
                <div className="doc-main" onClick={() => download(f)}>
                  <div className="doc-name">{f.name}</div>
                  {f.note && <div className="doc-note">{f.note}</div>}
                  <div className="doc-sub">
                    <span className="doc-cat archived-tag">Archived</span>
                    {f.category && <span className="doc-cat">{fileCategoryLabel(f.category)}</span>}
                    {fmtSize(f.size)}
                  </div>
                </div>
                <div className="doc-actions">
                  <button
                    className="doc-btn"
                    onClick={() => setViewing(f)}
                    title="Preview"
                    aria-label={`Preview ${f.name}`}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </button>
                  {!readOnly && <RestoreButton onClick={() => void restore(f)} />}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {viewing && <FilePreviewModal file={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

interface Licensure {
  id: string;
  licenseType: string;
  licenseNumber: string | null;
  licenseState: string | null;
  npi: string | null;
  ceuRequired: number | null;
  ceuCompleted: number;
  expiresOn: string | null;
}
const LICENSE_TYPES = [
  'pre_licensed',
  'lpc_lpcc',
  'lcsw_lmsw',
  'lmft',
  'psychologist',
  'psychiatrist',
  'rn_lpn',
  'none',
  'other',
];

/** Days from today (local) to an ISO date; negative once it has passed. */
function daysUntil(iso: string): number {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((new Date(y!, m! - 1, d!).getTime() - today.getTime()) / 86_400_000);
}

/** alertshub#12 - how far off a license's expiry is, as the Alerts Hub's credentialing cards count it. */
function expiryNote(iso: string): { text: string; tone: 'bad' | 'warn' | 'ok' } {
  const n = daysUntil(iso);
  if (n < 0) return { text: `Expired ${-n} day${n === -1 ? '' : 's'} ago`, tone: 'bad' };
  if (n === 0) return { text: 'Expires today', tone: 'bad' };
  return { text: `In ${n} day${n === 1 ? '' : 's'}`, tone: n <= 90 ? 'warn' : 'ok' };
}

/** Licensure tab (Phase 16b) — credentialing tracker for staff + accruing clients.
 *  alertshub#12: each license has an expiry date (set when adding, changed in the table). The Alerts
 *  Hub's Credentialing cards come from it (90 / 60 / 30 days ahead, then expired). */
function LicensurePanel({
  contactId,
  readOnly = false,
}: {
  contactId: string;
  readOnly?: boolean;
}) {
  const [rows, setRows] = useState<Licensure[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [licenseType, setLicenseType] = useState('lcsw_lmsw');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseState, setLicenseState] = useState('');
  const [npi, setNpi] = useState('');
  const [expiresOn, setExpiresOn] = useState('');

  const load = () => {
    api
      .get<Licensure[]>(`/contacts/${contactId}/licensure`)
      .then((r) => {
        setRows(r);
        setStatus('ready');
      })
      .catch((e: unknown) =>
        setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error'),
      );
  };
  useEffect(load, [contactId]);

  const add = () => {
    api
      .post(`/contacts/${contactId}/licensure`, {
        licenseType,
        licenseNumber: licenseNumber.trim() || undefined,
        licenseState: licenseState.trim() || undefined,
        npi: npi.trim() || undefined,
        expiresOn: expiresOn || undefined,
      })
      .then(() => {
        setLicenseNumber('');
        setLicenseState('');
        setNpi('');
        setExpiresOn('');
        load();
      })
      .catch(() => setStatus('error'));
  };

  // alertshub#12: a new expiry date saves as soon as it's a whole date (the hub re-checks on its sweep).
  const setExpiry = (id: string, v: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
    if (rows.find((r) => r.id === id)?.expiresOn?.slice(0, 10) === v) return; // typed, then left the field
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, expiresOn: v } : r)));
    api.patch(`/licensure/${id}`, { expiresOn: v }).catch((e: unknown) => {
      showError(e instanceof ApiError ? e.message : 'Could not save the expiry date');
      load();
    });
  };

  if (status === 'loading') return <p className="muted">Loading licensure…</p>;
  if (status === 'forbidden')
    return <p className="muted">Your role cannot view licensure (403).</p>;
  if (status === 'error') return <p className="muted">Could not load licensure.</p>;

  return (
    <div className="notes-panel">
      {/* ehr#730: a View tab (Share & Access) has no Add license and no expiry edits */}
      <div className="demo-grid" hidden={readOnly}>
        <label className="demo-field">
          <span className="muted">License type</span>
          <Dd
            value={licenseType}
            searchable={false}
            options={LICENSE_TYPES.map((t) => ({ value: t, label: t.replace(/_/g, ' ') }))}
            onChange={setLicenseType}
          />
        </label>
        <label className="demo-field">
          <span className="muted">License #</span>
          <input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} />
        </label>
        <label className="demo-field">
          <span className="muted">State</span>
          <input value={licenseState} onChange={(e) => setLicenseState(e.target.value)} />
        </label>
        <label className="demo-field">
          <span className="muted">NPI</span>
          <input value={npi} onChange={(e) => setNpi(e.target.value)} />
        </label>
        <div className="demo-field">
          <span className="muted">Expires</span>
          <DateField value={expiresOn} onChange={setExpiresOn} />
        </div>
      </div>
      <div className="modal-actions" hidden={readOnly}>
        <button className="btn" onClick={add}>
          Add license
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="muted">No licensure on file.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Type</th>
              <th>Number</th>
              <th>State</th>
              <th>NPI</th>
              <th>CEU</th>
              <th>Expires</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id}>
                <td>{l.licenseType.replace(/_/g, ' ')}</td>
                <td className="muted">{l.licenseNumber ?? '—'}</td>
                <td className="muted">{l.licenseState ?? '—'}</td>
                <td className="muted">{l.npi ?? '—'}</td>
                <td className="muted">
                  {l.ceuRequired != null ? `${l.ceuCompleted}/${l.ceuRequired}` : '—'}
                </td>
                <td className="lic-expiry">
                  <DateField
                    value={l.expiresOn ? l.expiresOn.slice(0, 10) : ''}
                    onChange={(v) => setExpiry(l.id, v)}
                    disabled={readOnly}
                  />
                  {l.expiresOn &&
                    (() => {
                      const e = expiryNote(l.expiresOn);
                      return <span className={`lic-expiry-note ${e.tone}`}>{e.text}</span>;
                    })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * Contact drawer with the V27 Demographics form (Phase 13.1). The 9 sections
 * mirror the mockup's CDemo module; the whole payload round-trips through the
 * encrypted `GET/PUT /contacts/:id/demographics` endpoint (PHI-gated + audited).
 */
export interface DrawerContact {
  id: string;
  firstName: string;
  lastName: string;
  category: string;
  /** #59 - set when soft-deleted; toggles Archive vs Restore in the kebab. */
  archivedAt?: string | null;
}

/** ehr#208 - GET /contacts/:id/links. 'out': this chart's Family & Contacts entry names
 *  them (relationship as entered here); 'in': their chart names this one. */
interface ContactLinkView {
  contactId: string;
  name: string;
  direction: 'out' | 'in';
  relationship: string;
  linkCharts: boolean;
  portalDisplay: boolean;
}

type FieldType =
  | 'text'
  | 'date'
  | 'email'
  | 'tel'
  | 'select'
  | 'checkbox'
  | 'checkboxes'
  | 'file'
  | 'subhead'
  | 'linkhead'
  | 'payor'
  | 'provider'
  | 'contact';
type Field = {
  key: string;
  label: string;
  type?: FieldType;
  options?: string[];
  /** For `select`: the options come from this practice list instead of `options` (ehr#743:
   *  Referral source = Settings > Referral Sources, GET /lookups/referral-sources). */
  optionsFrom?: 'referral-sources';
  full?: boolean;
  /** For `file` fields: the two drop-zone captions (front / back). */
  pair?: [string, string];
  /** For the Demographics DOB field: render a live age chip beside the input. */
  ageChip?: boolean;
  /** Input placeholder (mockup parity — e.g. phone/SSN masks). Defaults to the
   *  phone mask for `tel` fields. */
  placeholder?: string;
  /** Native input hints ported from the mockup (SSN/ZIP numeric entry). */
  inputMode?: 'numeric';
  maxLength?: number;
  /** Free-text input with an autocomplete suggestion list (#11 payor picker). */
  datalist?: string[];
  /** For `select`: reveal a free-text write-in (stored under this key) when the
   *  chosen value is "Other" (V22 mockup — global Other write-in). */
  otherKey?: string;
  /** Checkboxes rendered beneath the input, left-aligned (V22 mockup — per-phone OK to
   *  leave VM / OK to text; ehr#207 — Email's portal switches). Each stored under its own
   *  key; `defaultChecked` ones are ON unless explicitly unchecked (stored 'false'). */
  checks?: { key: string; label: string; defaultChecked?: boolean; title?: string }[];
  /** ehr#570: a second row beneath `checks`, tagged "AI Calls" (the AI-call consent for this
   *  phone). Stored like `checks`; all off unless ticked. */
  aiChecks?: { key: string; label: string; defaultChecked?: boolean; title?: string }[];
  /** `checkbox` that is ON unless explicitly unchecked (V22 "…same" toggles). */
  defaultChecked?: boolean;
  /** `select` value shown until one is picked; the API's projection saves the same default
   *  (ehr#233: Preferred contact method = Mobile phone). */
  defaultValue?: string;
  /** `checkbox` on a repeatable section that only one entry may hold (ehr#191 — the
   *  Care Team's primary clinician): ticking it clears it on the other entries. */
  exclusive?: boolean;
  /** Hide this field while the named checkbox field is checked (V22 conditional
   *  groups — Portal email, Mailing address). */
  hideWhen?: string;
  /** Dim + disable this field (kept visible) while the named checkbox is checked
   *  (V22 Guarantor "Same as client" — mockup opacity:.5 + pointer-events:none). */
  disableWhen?: string;
  /** Checkboxes inline on this field's label row, right-aligned (V22; ehr#208 — Display in
   *  portal / Link charts beside Relationship to client). Each stored under its own key. */
  inlineChecks?: { key: string; label: string; defaultChecked?: boolean; title?: string }[];
  /** Show this field only while one of the named checkboxes is checked (ehr#208 — the
   *  Linked chart picker). */
  showWhenAny?: string[];
  /** For `contact`: where the picked client's name is kept, beside the id in `key`. */
  nameKey?: string;
  /** Collapsible anchor (V22). On a subhead/linkhead: a click toggles the fields
   *  under it (until the next head); collapsed by default. On a `checkboxes`
   *  field: its own group toggles. */
  collapsible?: boolean;
  /** Ends the current collapsible group so this field (and anything after it)
   *  sits OUTSIDE it — V22: the Insurance card block is a sibling of Claims
   *  address, not nested under its show/hide. */
  endsGroup?: boolean;
};
type Section = { key: string; label: string; fields: Field[] };

// ehr#176: the Insurance / Payor dropdown lists the payors configured in RCM >
// Chargemaster > Payors (GET /payors), not a built-in list.
/** ehr#176: the option an entry saved by name alone shows until a payor (state) is picked. */
const SAVED_PAYOR = '__saved_payor__';

// ── Shared option lists (1:1 with the DEMOGRAPHICS V17 reference) ────────────
const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];
const SALUTATION = ['Mr.', 'Ms.', 'Mrs.', 'Mx.', 'Dr.', 'Prof.'];
const SUFFIX = ['Jr.', 'Sr.', 'II', 'III', 'IV', 'Esq.'];
const PRONOUNS = [
  'She/her',
  'He/him',
  'They/them',
  'She/they',
  'He/they',
  'Ze/zir',
  'Prefer not to say',
  'Other',
];
const GENDER = [
  'Woman',
  'Man',
  'Non-binary',
  'Transgender woman',
  'Transgender man',
  'Genderqueer',
  'Two-spirit',
  'Other',
  'Prefer not to say',
];
const MARITAL = [
  'Single',
  'Married',
  'Partnered',
  'Divorced',
  'Separated',
  'Widowed',
  'Prefer not to say',
];
const LANGUAGE = [
  'English',
  'Spanish',
  'Mandarin',
  'Cantonese',
  'Vietnamese',
  'Tagalog',
  'Arabic',
  'French',
  'Korean',
  'Russian',
  'Other',
];
const CONTACT_METHOD = ['Mobile phone', 'Home phone', 'Work phone', 'Email', 'Text / SMS'];
const TIMEZONE = [
  'Eastern (ET)',
  'Central (CT)',
  'Mountain (MT)',
  'Mountain – no DST (AZ)',
  'Pacific (PT)',
  'Alaska (AKT)',
  'Hawaii (HT)',
  'Atlantic (AT)',
  'Eastern – Indiana',
  'Eastern – Kentucky',
  'Mountain – Idaho',
  'Eastern – Michigan',
];
const RACE = [
  'American Indian or Alaska Native',
  'Asian',
  'Black or African American',
  'Hispanic or Latino',
  'Middle Eastern or North African',
  'Multiracial / Mixed race',
  'Native Hawaiian or Pacific Islander',
  'White',
  'Other',
  'Prefer not to say',
];
const GUARDIAN_REL = [
  'Mother',
  'Father',
  'Step-mother',
  'Step-father',
  'Parent',
  'Step-parent',
  'Legal guardian',
  'Grandparent',
  'Grandparent (maternal)',
  'Grandparent (paternal)',
  'Aunt/Uncle',
  'Foster parent',
  'Adoptive parent',
  // ehr#208: couples / family counseling.
  'Husband',
  'Wife',
  'Partner',
  'Child',
  'Other',
];
const EMERGENCY_REL = ['Spouse', 'Partner', 'Parent', 'Child', 'Sibling', 'Friend', 'Other'];
const CARE_ROLE: string[] = [...CARE_TEAM_ROLES];
const PLAN_TYPE = ['Commercial', 'Medicaid', 'Medicare'];
const PLAN_TIER = ['HMO', 'PPO', 'EPO', 'POS', 'HDHP', 'Indemnity', 'Other'];
const SUBSCRIBER_REL = ['Self', 'Spouse', 'Child', 'Domestic partner', 'Other'];
const GUARANTOR_REL = ['Self', 'Spouse', 'Parent', 'Legal guardian', 'Other'];
const ID_TYPE = ['Passport', 'State ID', 'Military ID', 'Tribal ID', 'Other government ID'];
const PHARMACY_TYPE = ['Retail', 'Mail-order', 'Specialty', 'Compounding', 'Long-term care'];
// Common US pharmacies for the searchable Pharmacy name combo; free text allowed.
const PHARMACY_NAMES = [
  'CVS Pharmacy',
  'Walgreens',
  'Rite Aid',
  'Walmart Pharmacy',
  'Costco Pharmacy',
  'Kroger Pharmacy',
  'Publix Pharmacy',
  'Safeway Pharmacy',
  'Albertsons Pharmacy',
  "Sam's Club Pharmacy",
  'Express Scripts',
  'OptumRx',
  'Amazon Pharmacy',
  'Kaiser Permanente Pharmacy',
  'H-E-B Pharmacy',
  'Meijer Pharmacy',
  'Giant Eagle Pharmacy',
  'Hy-Vee Pharmacy',
  'Wegmans Pharmacy',
];
const EMPLOYMENT_STATUS = [
  'Employed full-time',
  'Employed part-time',
  'Self-employed',
  'Unemployed',
  'Student',
  'Retired',
  'Disabled',
  'Other',
];

// V22 (mockup DEMOGRAPHICS_08-27-V22): Contact Info is Tab 1, Demographics Tab 2.
// Name + pronouns/gender live in Contact Info; Demographics leads with DOB (age
// chip) then billing IDs, race/ethnicity, background, and ID documents. Old
// records that stored the name block under `identity.*` are migrated on load
// (see migrateContactNames) so the keys keep round-tripping through the payload.
/**
 * ehr#310 / #311 (Chase 10-13): clients verify by name, date of birth and ZIP for now, so a
 * client's Security Questions section is hidden (as are the portal's and the other verification
 * methods). Turn this on to bring the section back; its data and API are untouched.
 */
const SHOW_SECURITY_QUESTIONS = false;

const SECTIONS: Section[] = [
  {
    key: 'contact',
    label: 'Contact Info',
    fields: [
      // Name block (moved here from Demographics in V18).
      { key: 'salutation', label: 'Salutation', type: 'select', options: SALUTATION },
      { key: 'firstName', label: 'First name*' },
      { key: 'middleName', label: 'Middle name' },
      { key: 'lastName', label: 'Last name*' },
      { key: 'suffix', label: 'Suffix', type: 'select', options: SUFFIX },
      { key: 'preferredName', label: 'Preferred name' },
      { key: 'identityHead', label: 'Identity', type: 'subhead', full: true },
      {
        key: 'pronouns',
        label: 'Pronouns',
        type: 'select',
        options: PRONOUNS,
        otherKey: 'pronounsOther',
      },
      {
        key: 'genderIdentity',
        label: 'Gender identity',
        type: 'select',
        options: GENDER,
        otherKey: 'genderIdentityOther',
      },
      { key: 'phoneHead', label: 'Phone & Email', type: 'subhead', full: true },
      // ehr#233 (Jay 10-09): the reminder answers live here, no separate section. (D) = on by
      // default. Mobile OK to call / OK to text are also the dated Voice AI call / Text message
      // answers the Opt-Out Center and Reminders read.
      // ehr#570 (mock PHONE_EMAIL_AI_CALLS 10-20): each phone also gets an "AI Calls" row, its
      // own answer apart from the person-call boxes above it, off unless ticked.
      {
        key: 'mobilePhone',
        label: 'Mobile phone',
        type: 'tel',
        checks: [
          { key: 'mobileOkCall', label: 'OK to call', defaultChecked: true },
          { key: 'mobileOkVm', label: 'OK to leave VM', defaultChecked: true },
          { key: 'mobileOkText', label: 'OK to text' },
        ],
        aiChecks: [
          { key: 'mobileOkAiCall', label: 'OK to call' },
          { key: 'mobileOkAiVm', label: 'OK to leave VM' },
        ],
      },
      {
        key: 'phone',
        label: 'Home phone',
        type: 'tel',
        checks: [
          { key: 'homeOkCall', label: 'OK to call' },
          { key: 'homeOkVm', label: 'OK to leave VM' },
        ],
        aiChecks: [
          { key: 'homeOkAiCall', label: 'OK to call' },
          { key: 'homeOkAiVm', label: 'OK to leave VM' },
        ],
      },
      {
        key: 'workPhone',
        label: 'Work phone',
        type: 'tel',
        checks: [
          { key: 'workOkCall', label: 'OK to call' },
          { key: 'workOkVm', label: 'OK to leave VM' },
        ],
        aiChecks: [
          { key: 'workOkAiCall', label: 'OK to call' },
          { key: 'workOkAiVm', label: 'OK to leave VM' },
        ],
      },
      // Preferred contact method sits beside Work phone (it fills the grid cell the
      // full-width Email row would otherwise leave empty), before Email.
      {
        key: 'preferredContact',
        label: 'Preferred contact method',
        type: 'select',
        options: CONTACT_METHOD,
        defaultValue: 'Mobile phone', // ehr#233 (Jay 10-09)
      },
      // Email, with the client portal switches beneath it on the left (ehr#207), all
      // default on. "Portal email is same": the primary email is the portal login and
      // Portal email is hidden; unchecking reveals a distinct one. "Portal messaging" /
      // "Portal appointment booking" off hide and block those for this client in the
      // portal (the portal reads them from contact.portalMessaging / portalBooking).
      {
        key: 'email',
        label: 'Email',
        type: 'email',
        full: true,
        checks: [
          { key: 'portalEmailSame', label: 'Portal email is same', defaultChecked: true },
          {
            key: 'portalMessaging',
            label: 'Portal messaging',
            defaultChecked: true,
            title: 'Let this client message the practice from the client portal',
          },
          {
            key: 'portalBooking',
            label: 'Portal appointment booking',
            defaultChecked: true,
            title: 'Let this client book and reschedule appointments in the client portal',
          },
        ],
      },
      {
        key: 'portalEmail',
        label: 'Portal email',
        type: 'email',
        full: true,
        hideWhen: 'portalEmailSame',
      },
      { key: 'addressHead', label: 'Address', type: 'linkhead', full: true, collapsible: true },
      { key: 'address1', label: 'Street address', full: true },
      { key: 'address2', label: 'Apt / suite / unit', full: true },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', type: 'select', options: US_STATES },
      { key: 'zip', label: 'ZIP code' },
      { key: 'timezone', label: 'Time zone', type: 'select', options: TIMEZONE },
      {
        key: 'mailingSame',
        label: 'Mailing address same as above',
        type: 'checkbox',
        full: true,
        defaultChecked: true,
      },
      {
        key: 'mailingAddress1',
        label: 'Mailing street address',
        full: true,
        hideWhen: 'mailingSame',
      },
      { key: 'mailingAddress2', label: 'Apt / suite / unit', full: true, hideWhen: 'mailingSame' },
      { key: 'mailingCity', label: 'City', hideWhen: 'mailingSame' },
      {
        key: 'mailingState',
        label: 'State',
        type: 'select',
        options: US_STATES,
        hideWhen: 'mailingSame',
      },
      { key: 'mailingZip', label: 'ZIP code', hideWhen: 'mailingSame' },
    ],
  },
  {
    key: 'identity',
    label: 'Demographics',
    fields: [
      { key: 'dob', label: 'Date of birth*', type: 'date', full: true, ageChip: true },
      { key: 'mrn', label: 'MRN / chart number', placeholder: 'e.g. 100482' },
      {
        key: 'ssn',
        label: 'SSN (for billing / claims)*',
        placeholder: '###-##-####',
        inputMode: 'numeric',
        maxLength: 11,
      },
      {
        key: 'race',
        label: 'Race and/or ethnicity (select all that apply)',
        type: 'checkboxes',
        options: RACE,
        full: true,
        otherKey: 'raceOther',
        collapsible: true,
      },
      { key: 'bgHead', label: 'Background', type: 'subhead', full: true },
      { key: 'maritalStatus', label: 'Marital status', type: 'select', options: MARITAL },
      {
        key: 'language',
        label: 'Preferred language',
        type: 'select',
        options: LANGUAGE,
        otherKey: 'languageOther',
      },
      {
        key: 'needsInterpreter',
        label: 'Needs interpreter services',
        type: 'checkbox',
        full: true,
      },
      { key: 'idDocsHead', label: 'ID Documents', type: 'subhead', full: true },
      { key: 'dlNumber', label: "Driver's license / state ID number", full: true },
      { key: 'dlState', label: 'Issuing state', type: 'select', options: US_STATES },
      { key: 'dlExp', label: 'Expiration date', type: 'date' },
      {
        key: 'dlImages',
        label: "Driver's license / ID images",
        type: 'file',
        full: true,
        pair: ['Front of license / ID', 'Back of license / ID'],
      },
      { key: 'secIdHead', label: 'Secondary ID (optional)', type: 'subhead', full: true },
      { key: 'secIdType', label: 'ID type', type: 'select', options: ID_TYPE },
      { key: 'secIdNumber', label: 'ID number' },
      { key: 'secIdCountry', label: 'Issuing country / state', placeholder: 'e.g. USA, CA' },
      { key: 'secIdExp', label: 'Expiration date', type: 'date' },
      {
        key: 'secIdImages',
        label: 'Secondary ID images',
        type: 'file',
        full: true,
        pair: ['Front of ID', 'Back of ID'],
      },
    ],
  },
  {
    // ehr#648 - shown as "Family & Contacts"; the key and the saved fields stay 'guardian'.
    key: 'guardian',
    label: 'Family & Contacts',
    fields: [
      { key: 'isMinor', label: 'This client is a minor', type: 'checkbox' },
      { key: 'salutation', label: 'Salutation', type: 'select', options: SALUTATION },
      { key: 'firstName', label: 'First name' },
      { key: 'middleName', label: 'Middle name' },
      { key: 'lastName', label: 'Last name' },
      { key: 'suffix', label: 'Suffix', type: 'select', options: SUFFIX },
      { key: 'preferredName', label: 'Preferred name' },
      // ehr#208 linked charts: either switch reveals the Linked chart picker. Saved rows go
      // to contact_links (the API rebuilds them from these entries on every save).
      {
        key: 'relationship',
        label: 'Relationship to client',
        type: 'select',
        options: GUARDIAN_REL,
        otherKey: 'relationshipOther',
        inlineChecks: [
          {
            key: 'portalDisplay',
            label: 'Display in portal',
            title: 'This person’s portal login can open this client’s chart',
          },
          {
            key: 'linkCharts',
            label: 'Link charts',
            title: 'Show this person beside the client’s name, to jump between the two charts',
          },
        ],
      },
      {
        key: 'linkedContactId',
        label: 'Linked chart',
        type: 'contact',
        nameKey: 'linkedContactName',
        full: true,
        showWhenAny: ['portalDisplay', 'linkCharts'],
      },
      { key: 'legalGuardian', label: 'Has legal guardianship / custody', type: 'checkbox' },
      { key: 'dob', label: 'Date of birth', type: 'date', full: true },
      {
        key: 'pronouns',
        label: 'Pronouns',
        type: 'select',
        options: PRONOUNS,
        otherKey: 'pronounsOther',
      },
      {
        key: 'genderIdentity',
        label: 'Gender identity',
        type: 'select',
        options: GENDER,
        otherKey: 'genderIdentityOther',
      },
      {
        key: 'language',
        label: 'Preferred language',
        type: 'select',
        options: LANGUAGE,
        otherKey: 'languageOther',
      },
      {
        key: 'needsInterpreter',
        label: 'Needs interpreter services',
        type: 'checkbox',
        full: true,
      },
      {
        key: 'race',
        label: 'Race and/or ethnicity (select all that apply)',
        type: 'checkboxes',
        options: RACE,
        full: true,
        otherKey: 'raceOther',
        collapsible: true,
      },
      {
        key: 'mobilePhone',
        label: 'Mobile phone',
        type: 'tel',
        checks: [
          { key: 'mobileOkVm', label: 'OK to leave VM' },
          { key: 'mobileOkText', label: 'OK to text' },
        ],
      },
      {
        key: 'phone',
        label: 'Home phone',
        type: 'tel',
        checks: [{ key: 'homeOkVm', label: 'OK to leave VM' }],
      },
      {
        key: 'workPhone',
        label: 'Work phone',
        type: 'tel',
        checks: [{ key: 'workOkVm', label: 'OK to leave VM' }],
      },
      {
        key: 'preferredContact',
        label: 'Preferred contact method',
        type: 'select',
        options: CONTACT_METHOD,
      },
      { key: 'email', label: 'Email', type: 'email', full: true },
      { key: 'address1', label: 'Street address', full: true },
      { key: 'address2', label: 'Apt / suite / unit', full: true },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', type: 'select', options: US_STATES },
      { key: 'zip', label: 'ZIP code' },
    ],
  },
  {
    key: 'emergency',
    label: 'Emergency Contact',
    fields: [
      { key: 'salutation', label: 'Salutation', type: 'select', options: SALUTATION },
      { key: 'firstName', label: 'First name' },
      { key: 'middleName', label: 'Middle name' },
      { key: 'lastName', label: 'Last name' },
      { key: 'suffix', label: 'Suffix', type: 'select', options: SUFFIX },
      { key: 'preferredName', label: 'Preferred name' },
      {
        key: 'relationship',
        label: 'Relationship',
        type: 'select',
        options: EMERGENCY_REL,
        otherKey: 'relationshipOther',
      },
      { key: 'phone', label: 'Phone', type: 'tel' },
      { key: 'altPhone', label: 'Alternate phone', type: 'tel' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'address1', label: 'Street address', full: true },
      { key: 'address2', label: 'Apt / suite / unit', full: true },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', type: 'select', options: US_STATES },
      { key: 'zip', label: 'ZIP code' },
    ],
  },
  {
    key: 'careteam',
    label: 'Care Team',
    fields: [
      // ehr#191 — fill the entry from one of the practice's providers (typing stays open).
      { key: 'providerId', label: 'Practice provider', type: 'provider', full: true },
      {
        key: 'primary',
        label: 'Primary clinician (goes on new appointments)',
        type: 'checkbox',
        full: true,
        exclusive: true,
      },
      { key: 'salutation', label: 'Salutation', type: 'select', options: SALUTATION },
      { key: 'firstName', label: 'First name' },
      { key: 'lastName', label: 'Last name' },
      { key: 'preferredName', label: 'Preferred name' },
      { key: 'role', label: 'Role', type: 'select', options: CARE_ROLE, otherKey: 'roleOther' },
      { key: 'organization', label: 'Organization' },
      { key: 'npi', label: 'NPI' },
      { key: 'mobilePhone', label: 'Mobile phone', type: 'tel' },
      { key: 'workPhone', label: 'Work phone', type: 'tel' },
      { key: 'fax', label: 'Fax', type: 'tel' },
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'ctAddrHead', label: 'Address', type: 'linkhead', full: true, collapsible: true },
      { key: 'address1', label: 'Street address', full: true },
      { key: 'address2', label: 'Apt / suite / unit', full: true },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', type: 'select', options: US_STATES },
      { key: 'zip', label: 'ZIP code' },
      {
        key: 'mailingSame',
        label: 'Mailing address same as above',
        type: 'checkbox',
        full: true,
        defaultChecked: true,
      },
      {
        key: 'mailingAddress1',
        label: 'Mailing street address',
        full: true,
        hideWhen: 'mailingSame',
      },
      { key: 'mailingAddress2', label: 'Apt / suite / unit', full: true, hideWhen: 'mailingSame' },
      { key: 'mailingCity', label: 'City', hideWhen: 'mailingSame' },
      {
        key: 'mailingState',
        label: 'State',
        type: 'select',
        options: US_STATES,
        hideWhen: 'mailingSame',
      },
      { key: 'mailingZip', label: 'ZIP code', hideWhen: 'mailingSame' },
    ],
  },
  {
    key: 'insurance',
    label: 'Insurance / Payor',
    fields: [
      { key: 'payer', label: 'Payor / insurance company', type: 'payor' },
      { key: 'payerPhone', label: 'Payor phone', type: 'tel' },
      { key: 'memberId', label: 'Member ID' },
      { key: 'groupNum', label: 'Group number' },
      {
        key: 'planType',
        label: 'Plan type',
        type: 'select',
        options: PLAN_TYPE,
        otherKey: 'planTypeOther',
      },
      {
        key: 'planTier',
        label: 'Network / plan tier',
        type: 'select',
        options: PLAN_TIER,
        otherKey: 'networkTierOther',
      },
      { key: 'effectiveDate', label: 'Effective date', type: 'date' },
      { key: 'termDate', label: 'Termination date', type: 'date' },
      { key: 'authNumber', label: 'Authorization / pre-auth #' },
      { key: 'copay', label: 'Copay', placeholder: '$0.00' },
      { key: 'deductible', label: 'Deductible', placeholder: '$0.00' },
      { key: 'coinsurance', label: 'Co-insurance %', placeholder: 'e.g. 20%' },
      { key: 'oopMax', label: 'Out-of-pocket max', placeholder: '$0.00' },
      { key: 'clientResponsibility', label: 'Client Responsibility', placeholder: '$0.00' },
      {
        key: 'subscriberRel',
        label: 'Subscriber relationship',
        type: 'select',
        options: SUBSCRIBER_REL,
        otherKey: 'subscriberRelationshipOther',
      },
      {
        key: 'insAddrHead',
        label: 'Claims address',
        type: 'linkhead',
        full: true,
        collapsible: true,
      },
      { key: 'address1', label: 'Street address', full: true },
      { key: 'address2', label: 'Apt / suite / unit', full: true },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', type: 'select', options: US_STATES },
      { key: 'zip', label: 'ZIP code' },
      {
        key: 'insuranceCard',
        label: 'Insurance card',
        type: 'file',
        full: true,
        pair: ['Front of card', 'Back of card'],
        // Sits outside the Claims address collapse (V22 mockup).
        endsGroup: true,
      },
    ],
  },
  {
    key: 'guarantor',
    label: 'Guarantor',
    fields: [
      { key: 'sameAsClient', label: 'Same as client', type: 'checkbox' },
      {
        key: 'salutation',
        label: 'Salutation',
        type: 'select',
        options: SALUTATION,
        disableWhen: 'sameAsClient',
      },
      { key: 'firstName', label: 'First name', disableWhen: 'sameAsClient' },
      { key: 'lastName', label: 'Last name', disableWhen: 'sameAsClient' },
      {
        key: 'suffix',
        label: 'Suffix',
        type: 'select',
        options: SUFFIX,
        disableWhen: 'sameAsClient',
      },
      { key: 'preferredName', label: 'Preferred name', disableWhen: 'sameAsClient' },
      {
        key: 'relationship',
        label: 'Relationship',
        type: 'select',
        options: GUARANTOR_REL,
        otherKey: 'relationshipOther',
        disableWhen: 'sameAsClient',
      },
      { key: 'dob', label: 'Date of birth', type: 'date', disableWhen: 'sameAsClient' },
      {
        key: 'ssn',
        label: 'SSN',
        placeholder: '###-##-####',
        inputMode: 'numeric',
        maxLength: 11,
        disableWhen: 'sameAsClient',
      },
      { key: 'mobilePhone', label: 'Mobile phone', type: 'tel', disableWhen: 'sameAsClient' },
      { key: 'otherPhone', label: 'Other phone', type: 'tel', disableWhen: 'sameAsClient' },
      { key: 'email', label: 'Email', type: 'email', disableWhen: 'sameAsClient' },
      { key: 'employer', label: 'Employer', disableWhen: 'sameAsClient' },
      { key: 'occupation', label: 'Occupation', disableWhen: 'sameAsClient' },
      { key: 'address1', label: 'Street address', full: true, disableWhen: 'sameAsClient' },
      { key: 'address2', label: 'Apt / suite / unit', full: true, disableWhen: 'sameAsClient' },
      { key: 'city', label: 'City', disableWhen: 'sameAsClient' },
      {
        key: 'state',
        label: 'State',
        type: 'select',
        options: US_STATES,
        disableWhen: 'sameAsClient',
      },
      { key: 'zip', label: 'ZIP code', disableWhen: 'sameAsClient' },
    ],
  },
  {
    key: 'pharmacy',
    label: 'Pharmacy',
    fields: [
      {
        key: 'name',
        label: 'Pharmacy name',
        full: true,
        datalist: PHARMACY_NAMES,
        placeholder: 'Search or type a pharmacy…',
      },
      { key: 'type', label: 'Type', type: 'select', options: PHARMACY_TYPE },
      { key: 'ncpdp', label: 'NCPDP ID' },
      { key: 'phone', label: 'Phone', type: 'tel' },
      { key: 'fax', label: 'Fax', type: 'tel' },
      { key: 'address1', label: 'Street address', full: true },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State', type: 'select', options: US_STATES },
      { key: 'zip', label: 'ZIP code' },
    ],
  },
  // ehr#743 (V11 22b): Employment and Referral are split, Referral first. Records saved before the
  // split keep employment.referralSource; migrateReferral moves it here on load.
  {
    key: 'referral',
    label: 'Referral',
    fields: [
      { key: 'detailsHead', label: 'Referral details', type: 'subhead' },
      {
        key: 'referralSource',
        label: 'Referral source*',
        type: 'select',
        optionsFrom: 'referral-sources',
        otherKey: 'referralSourceOther',
      },
      { key: 'referralDate', label: 'Referral date', type: 'date' },
      { key: 'referringProvider', label: 'Referring provider' },
      {
        key: 'referringNpi',
        label: 'Referring provider NPI',
        placeholder: '10-digit NPI',
        inputMode: 'numeric',
        maxLength: 10,
      },
      { key: 'authNumber', label: 'Referral / authorization number', placeholder: 'Enter number' },
      {
        key: 'authorizedVisits',
        label: 'Authorized visits',
        placeholder: 'Number of visits',
        inputMode: 'numeric',
      },
      { key: 'reason', label: 'Reason for referral', placeholder: 'Short reason', full: true },
    ],
  },
  {
    key: 'employment',
    label: 'Employment',
    fields: [
      {
        key: 'status',
        label: 'Employment status',
        type: 'select',
        options: EMPLOYMENT_STATUS,
        otherKey: 'employmentStatusOther',
      },
      { key: 'occupation', label: 'Occupation' },
      { key: 'employer', label: 'Employer', full: true },
      { key: 'employerPhone', label: 'Employer phone', type: 'tel' },
      { key: 'employerEmail', label: 'Employer email', type: 'email' },
      { key: 'supervisorFirst', label: 'Supervisor first name' },
      { key: 'supervisorLast', label: 'Supervisor last name' },
    ],
  },
  // ehr#311: Security Questions (#11). Its body is SecurityQuestionsSection, which saves on its
  // own to the API (only a hash of each answer is kept), not into the Demographics record.
  // Hidden for now (SHOW_SECURITY_QUESTIONS).
  ...(SHOW_SECURITY_QUESTIONS
    ? [{ key: 'security', label: 'Security Questions', fields: [] }]
    : []),
];

// Repeatable sections (mirror the reference's "+ Add …" rows). style: rep-add =
// filled pill, add-btn = dashed. Insurance cycles secondary → tertiary labels.
// `entry` titles the extra entries ("<entry> #2"); the section label otherwise.
const REPEATABLE: Record<
  string,
  { style: 'rep-add' | 'add-btn'; label: string | string[]; entry?: string }
> = {
  guardian: {
    style: 'rep-add',
    label: 'Add family member or contact',
    entry: 'Family member / contact',
  },
  emergency: { style: 'add-btn', label: '+ Add contact' },
  careteam: { style: 'add-btn', label: '+ Add care team member' },
  insurance: { style: 'add-btn', label: ['+ Add secondary insurance', '+ Add tertiary insurance'] },
  pharmacy: { style: 'add-btn', label: '+ Add pharmacy' },
};

// Sections that never flag orange when empty (mockup: nav numbers turn orange
// for unfilled REQUIRED tabs only — Pharmacy + Employment are optional, and
// Family & Contacts is gated on the client being a minor, handled at the callsite).
// ehr#233: Appointment Reminders is optional too (a blank answer means okay to remind).
// ehr#311: Security Questions too (set when the client wants to verify by them).
const OPTIONAL_SECTIONS = new Set(['pharmacy', 'employment', 'security']);

// Tier 1 mandatory fields (mockup two-tier validation) and the format checks live in
// lib/profileTabs.ts (PROFILE_REQUIRED, profileProblems): ehr#743, the save and the section tabs'
// amber dots share them.

type Demographics = Record<string, Record<string, string>>;

/** ehr#743: number-only Referral fields (typed letters are dropped). */
const DIGITS_ONLY = new Set(['referringNpi', 'authorizedVisits']);

// Live age from an ISO (YYYY-MM-DD) date of birth — drives the Demographics DOB
// age chip (mockup .age-chip). Returns null for empty/invalid/future dates.
function ageFromDob(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 200 ? age : null;
}

// V22 moved the name block + pronouns/gender out of `identity` into `contact`.
// Records saved under the old layout keep those values under `identity.*`; on
// load we lift them into `contact.*` (without clobbering any newer contact
// value) and drop them from identity so the payload matches the new sections.
const MOVED_TO_CONTACT = [
  'salutation',
  'firstName',
  'middleName',
  'lastName',
  'suffix',
  'preferredName',
  'pronouns',
  'genderIdentity',
];
function migrateContactNames(d: Demographics): Demographics {
  const id = d.identity;
  if (!id || !MOVED_TO_CONTACT.some((k) => id[k])) return d;
  const contact = { ...(d.contact ?? {}) };
  const identity = { ...id };
  for (const k of MOVED_TO_CONTACT) {
    if (identity[k] && !contact[k]) contact[k] = identity[k];
    delete identity[k];
  }
  return { ...d, contact, identity };
}

/** Single image drop-zone (front or back) — ported 1:1 from the V17 reference. */
function UploadZone({
  label,
  value,
  busy,
  error,
  onPick,
  onRemove,
  onExpand,
}: {
  label: string;
  value: string;
  busy?: boolean;
  /** Validation / upload error to show beneath the zone (#12). */
  error?: string;
  onPick: (file: File) => void;
  onRemove: () => void;
  onExpand?: (src: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="upload">
      <div
        className={`upload-zone${error ? ' upload-zone-error' : ''}`}
        onClick={() => {
          if (!value && !busy) inputRef.current?.click();
        }}
      >
        {value ? (
          <div className="upload-thumb">
            <img
              src={value}
              alt={label}
              className="upload-thumb-img"
              title="Click to expand"
              onClick={(e) => {
                e.stopPropagation();
                onExpand?.(value);
              }}
            />
            <button
              type="button"
              className="upload-x"
              title="Remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              ×
            </button>
          </div>
        ) : (
          <div className="upload-ph">
            <span className="upload-ico" />
            <span>{busy ? 'Uploading…' : label}</span>
          </div>
        )}
      </div>
      {error && <div className="upload-err">{error}</div>}
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// ── Activity tab (Batch 4) — unified timeline: notes / tasks / appts / audit ──

interface ActivityItem {
  type: 'note' | 'task' | 'appointment' | 'audit';
  at: string;
  label: string;
  detail: string | null;
  actor: string | null;
}
const ACTIVITY_ICON: Record<ActivityItem['type'], string> = {
  note: '📝',
  task: '✓',
  appointment: '📅',
  audit: '•',
};

function ActivityPanel({ contactId }: { contactId: string }) {
  const [rows, setRows] = useState<ActivityItem[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    api
      .get<ActivityItem[]>(`/contacts/${contactId}/activity`)
      .then((r) => {
        setRows(r);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [contactId]);

  if (status === 'loading') return <p className="muted">Loading activity…</p>;
  if (status === 'error') return <p className="muted">Could not load activity.</p>;
  if (rows.length === 0) return <p className="muted">No activity yet.</p>;

  return (
    <ul className="activity-feed">
      {rows.map((it, i) => (
        <li key={i} className="activity-item">
          <span className="activity-icon" aria-hidden>
            {ACTIVITY_ICON[it.type]}
          </span>
          <div className="activity-body">
            <div className="activity-label">
              {it.label}
              {it.detail && <span className="badge normal">{it.detail}</span>}
            </div>
            <div className="activity-meta muted">
              {new Date(it.at).toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                ...timeOpts(),
              })}
              {it.actor ? ` · ${it.actor}` : ''}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ── Cadence tab (Phase 14c) — Touch Rules enrollment + per-step logging ──

interface CadenceStep {
  id: string;
  position: number;
  substatus: string | null;
  offsetDays: number;
  channels: string[];
}
interface TouchRuleLite {
  id: string;
  name: string;
  category: string;
  cadences: CadenceStep[];
}
interface Enrollment {
  id: string;
  ruleId: string;
  anchorAt: string;
  enrolled: boolean;
  rule: TouchRuleLite;
}
interface TouchLogRow {
  id: string;
  cadenceId: string;
  channel: string;
  loggedAt: string;
}

function CadencePanel({ contactId }: { contactId: string }) {
  const [rules, setRules] = useState<TouchRuleLite[]>([]);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [logs, setLogs] = useState<TouchLogRow[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([
      api.get<TouchRuleLite[]>('/touch-rules'),
      api.get<{ enrollment: Enrollment | null; logs: TouchLogRow[] }>(
        `/contacts/${contactId}/touch-rule`,
      ),
    ])
      .then(([r, e]) => {
        setRules(r);
        setEnrollment(e.enrollment);
        setLogs(e.logs);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  };
  useEffect(load, [contactId]);

  const enroll = () => {
    if (!pick) return;
    setBusy(true);
    api
      .post(`/contacts/${contactId}/touch-rule`, { ruleId: pick })
      .then(load)
      .catch(() => setStatus('error'))
      .finally(() => setBusy(false));
  };
  const unenroll = () => {
    setBusy(true);
    api
      .del(`/contacts/${contactId}/touch-rule`)
      .then(load)
      .catch(() => setStatus('error'))
      .finally(() => setBusy(false));
  };
  const logTouch = (cadenceId: string, channel: string) => {
    api
      .post(`/contacts/${contactId}/touch/${cadenceId}/log`, { channel })
      .then(load)
      .catch(() => undefined);
  };

  if (status === 'loading') return <p className="muted">Loading cadence…</p>;
  if (status === 'error') return <p className="muted">Could not load cadence.</p>;

  if (!enrollment) {
    return (
      <div className="notes-panel">
        <p className="muted">This contact isn’t enrolled in a touch cadence.</p>
        {rules.length === 0 ? (
          <p className="muted">No touch rules defined yet — create one in Settings.</p>
        ) : (
          <div className="modal-actions" style={{ gap: 8 }}>
            <div style={{ width: 220 }}>
              <Dd
                value={pick}
                searchable={false}
                placeholder="Choose a cadence…"
                options={rules.map((r) => ({ value: r.id, label: r.name }))}
                onChange={setPick}
              />
            </div>
            <button className="btn primary" onClick={enroll} disabled={!pick || busy}>
              {busy ? 'Enrolling…' : 'Enroll'}
            </button>
          </div>
        )}
      </div>
    );
  }

  const anchor = new Date(enrollment.anchorAt).getTime();
  const now = Date.now();
  const loggedBy = new Map(logs.map((l) => [l.cadenceId, l]));
  const steps = [...enrollment.rule.cadences].sort((a, b) => a.position - b.position);

  return (
    <div className="notes-panel">
      <div className="modal-actions" style={{ alignItems: 'center' }}>
        <div>
          <strong>{enrollment.rule.name}</strong>{' '}
          <span className="muted">
            · anchored{' '}
            {new Date(enrollment.anchorAt).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
            })}
          </span>
        </div>
        <span className="spacer" style={{ flex: 1 }} />
        <button className="btn sm" onClick={unenroll} disabled={busy}>
          Unenroll
        </button>
      </div>
      <table>
        <thead>
          <tr>
            <th>Step</th>
            <th>Due</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {steps.map((s) => {
            const done = loggedBy.get(s.id);
            const dueAt = anchor + s.offsetDays * 86_400_000;
            const isDue = !done && dueAt <= now;
            return (
              <tr key={s.id}>
                <td>
                  Day {s.offsetDays}
                  {s.substatus ? <span className="muted"> · {s.substatus}</span> : null}
                  <div className="muted" style={{ fontSize: 12 }}>
                    {s.channels.join(', ')}
                  </div>
                </td>
                <td className="muted">
                  {new Date(dueAt).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </td>
                <td>
                  {done ? (
                    <span className="badge normal">Logged · {done.channel}</span>
                  ) : isDue ? (
                    <span className="badge urgent">Due</span>
                  ) : (
                    <span className="muted">Upcoming</span>
                  )}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {!done &&
                    s.channels.map((ch) => (
                      <button
                        key={ch}
                        className="btn sm"
                        onClick={() => logTouch(s.id, ch)}
                        title={`Log ${ch}`}
                        style={{ marginLeft: 4 }}
                      >
                        {ch}
                      </button>
                    ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────── V27 drawer tabs (mockup parity) ───────────────
// The mockup (legacy/EHR-7-30-V27.html · drawer-tabs) exposes Follow-up,
// Appointments, Forms, Stage Actions, Tasks, Email and Messages tabs the SPA was
// missing. Each is wired to the existing per-contact API where one exists; Email
// and Stage Actions are faithful stubs (the mockup stubs those too).

const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtDateTime = (s: string) =>
  new Date(s).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    ...timeOpts(),
  });

interface FollowupRow {
  id: string;
  dueAt: string;
  note: string | null;
  clearedAt: string | null;
  /** When this row was saved (each Save makes a new open row): the "last saved" line. */
  createdAt?: string;
}

// ── Follow-up helpers — ported 1:1 from EHR_08-08-V34.html (renderFollowupTab,
//    followupStatus, recurrencePresets). All dates are handled as a local
//    calendar day (YYYY-MM-DD); stored dueAt is anchored at local noon so the
//    day never shifts across time zones on the round-trip.
const pad2 = (n: number) => String(n).padStart(2, '0');
/** Local calendar day (YYYY-MM-DD) of a stored dueAt timestamp. */
function localDayISO(v: string): string {
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
// Live input masks + validation (#5 phone, #3d SSN, #9): lib/contactFields (shared with Settings → Locations).

/** A Date at local noon for a calendar day (avoids TZ off-by-one on display). */
function localNoon(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}
type FuStatus = 'overdue' | 'due-today' | 'due-soon' | 'scheduled';
function followupStatus(iso: string): FuStatus | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const due = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'due-today';
  if (diff <= 2) return 'due-soon';
  return 'scheduled';
}
const FU_PRESETS: { label: string; days: number }[] = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 3 days', days: 3 },
  { label: 'Next week', days: 7 },
  { label: 'In 2 weeks', days: 14 },
  { label: 'In a month', days: 30 },
];
function todayPlus(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Follow-up tab — V34 pixel-parity (EHR_08-08-V34.html · renderFollowupTab):
 * a single next follow-up with MM/DD/YYYY entry, an inline calendar, quick
 * presets, a repeat dropdown, a status banner, and reminder notes.
 *
 * Mapped onto the existing /followups collection API (frontend-only per plan):
 * the "next follow-up" is the one open row; changing it clears the open row and
 * creates a fresh one (no PATCH on the collection). Recurrence is client-side
 * only — the backend has no recurrence column yet — so it resets on reload.
 */
function FollowupPanel({ contactId, readOnly = false }: { contactId: string; readOnly?: boolean }) {
  const [rows, setRows] = useState<FollowupRow[]>([]);
  const [openRow, setOpenRow] = useState<FollowupRow | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [tab, setTab] = useState<'new' | 'history'>('new');
  const [dateISO, setDateISO] = useState('');
  const [noteText, setNoteText] = useState('');
  const [rule, setRule] = useState<Recurrence>(NO_RECURRENCE);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  // ehr#681 follow-up: the "Follow-up saved" (or "Not saved") card above Save, and the
  // "Follow-up last saved X ago" line (the open row's createdAt), refreshed every 15 s.
  const [saveNote, setSaveNote] = useState<SaveNotice | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const rowSavedAt = openRow?.createdAt ? Date.parse(openRow.createdAt) : NaN;
  // the line moves to the new time with the card, after the 2 s "Saving…" (as in Demographics)
  const [savedAt, setSavedAt] = useState(NaN);
  useEffect(() => {
    if (!busy) setSavedAt(rowSavedAt);
  }, [rowSavedAt, busy]);
  useEffect(() => {
    if (!Number.isFinite(savedAt)) return;
    setNowTick(Date.now());
    const t = window.setInterval(() => setNowTick(Date.now()), LAST_SAVED_TICK_MS);
    return () => window.clearInterval(t);
  }, [savedAt]);

  const load = async () => {
    try {
      const all = await api.get<FollowupRow[]>(`/followups?contact=${contactId}`);
      setRows(all);
      const open = all.find((r) => !r.clearedAt) ?? null;
      setOpenRow(open);
      const iso = open ? localDayISO(open.dueAt) : '';
      setDateISO(iso);
      setNoteText(open?.note ?? '');
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };
  useEffect(() => {
    void load();
  }, [contactId]);

  // Persist a (date, note) pair onto the single open follow-up. With no PATCH on
  // the collection API, "change" = clear the open row then create a fresh one.
  const persist = async (nextISO: string, nextNote: string) => {
    if (readOnly || busyRef.current) return;
    const curISO = openRow ? localDayISO(openRow.dueAt) : '';
    const curNote = openRow?.note ?? '';
    if (nextISO === curISO && nextNote.trim() === curNote.trim()) return;
    busyRef.current = true;
    setBusy(true);
    setSaveNote(null);
    // ehr#681 follow-up: Save reads "Saving…" for at least 2 s; the tab itself updates as soon as
    // the server answers. A failure leaves the current state (a reload will resync).
    const work = (async () => {
      if (openRow) await api.post(`/followups/${openRow.id}/clear`, {});
      if (nextISO) {
        await api.post('/followups', {
          contactId,
          dueAt: `${nextISO}T12:00:00`,
          note: nextNote.trim() || undefined,
        });
      }
      await load();
    })();
    const r = await runSave(work, { ok: followupSavedText(nextISO), err: followupErrText });
    busyRef.current = false;
    setBusy(false);
    setSaveNote(r.notice);
  };

  // Recurrence is client-side only (the backend has no recurrence column yet), so it
  // resets on reload. Clearing the date also clears any repeat.
  const applyDate = (iso: string) => {
    setDateISO(iso);
    if (!iso) setRule(NO_RECURRENCE);
  };

  const advance = () => {
    if (!dateISO || !rule) return;
    const next = nextOccurrence(dateISO, rule);
    setDateISO(next);
    // Series complete → drop the repeat; otherwise count this occurrence.
    setRule(
      recurrenceEnded(rule, next)
        ? NO_RECURRENCE
        : { ...rule, countDone: (rule.countDone || 0) + 1 },
    );
  };

  if (status === 'loading') return <p className="muted">Loading follow-up…</p>;
  if (status === 'error') return <p className="muted">Could not load follow-up.</p>;

  // Draft model (V38 .fu-save-bar): edits stay local until Save; Cancel reverts.
  const savedISO = openRow ? localDayISO(openRow.dueAt) : '';
  const savedNote = openRow?.note ?? '';
  const dirty = dateISO !== savedISO || noteText.trim() !== savedNote.trim();
  const saveDraft = () => void persist(dateISO, noteText);
  const cancelDraft = () => {
    setDateISO(savedISO);
    setNoteText(savedNote);
    if (!savedISO) setRule(NO_RECURRENCE);
  };

  const history = rows
    .filter((r) => r.clearedAt)
    .sort((a, b) => (a.clearedAt! < b.clearedAt! ? 1 : -1));

  const fu = followupStatus(dateISO);
  const bannerClass = fu === 'overdue' ? 'overdue' : fu === 'due-soon' ? 'due-soon' : 'scheduled';
  const bannerLabel = fu === 'overdue' ? 'Overdue' : fu === 'due-soon' ? 'Due soon' : 'Scheduled';
  const bannerDate = dateISO
    ? localNoon(dateISO).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  return (
    <div className="detail-section">
      <div className="fu-seg" role="tablist" aria-label="Follow-up view">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'new'}
          className={`fu-seg-btn${tab === 'new' ? ' on' : ''}`}
          onClick={() => setTab('new')}
        >
          New follow-up
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'history'}
          className={`fu-seg-btn${tab === 'history' ? ' on' : ''}`}
          onClick={() => setTab('history')}
        >
          Follow-up history
        </button>
      </div>

      {tab === 'history' ? (
        history.length === 0 ? (
          <p className="muted">No past follow-ups yet.</p>
        ) : (
          <div>
            {history.map((r) => (
              <div key={r.id} className="fu-history-row">
                <span className="fu-h-date">
                  {localNoon(localDayISO(r.dueAt)).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
                {r.note && <span className="fu-h-note">{r.note}</span>}
                <span className="fu-h-when">
                  cleared{' '}
                  {new Date(r.clearedAt!).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                  })}
                </span>
              </div>
            ))}
          </div>
        )
      ) : (
        <>
          <div className="detail-section-title">
            Next follow-up
            {dateISO && !readOnly && (
              <button
                className="btn"
                style={{ fontSize: 11, padding: '5px 10px', minHeight: 0 }}
                onClick={() => applyDate('')}
                disabled={busy}
              >
                Clear
              </button>
            )}
          </div>

          <div className="field" style={{ marginBottom: 16 }}>
            <label>Date</label>
            <DateField
              value={dateISO}
              onChange={applyDate}
              disabled={readOnly}
              placeholder="Select a follow-up date"
            />
          </div>

          <div className="note-type-pills" style={{ marginBottom: 18 }}>
            {FU_PRESETS.map((pr) => (
              <button
                key={pr.label}
                type="button"
                className="note-type-pill"
                disabled={readOnly}
                onClick={() => applyDate(todayPlus(pr.days))}
              >
                {pr.label}
              </button>
            ))}
          </div>

          <div className="field" style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Repeat
            </label>
            {!dateISO ? (
              <div className="fu-repeat-disabled">
                Pick a follow-up date first to enable repeats.
              </div>
            ) : (
              <RecurrenceField
                rule={rule}
                onChange={setRule}
                anchorISO={dateISO}
                disabled={readOnly}
              />
            )}
          </div>

          {dateISO ? (
            <>
              <div className={`fu-status-banner ${bannerClass}`}>
                <strong>{bannerLabel}:</strong> {bannerDate}
              </div>
              {rule && !readOnly && (
                <div className="followup-sync-row">
                  <button
                    className="btn"
                    onClick={advance}
                    disabled={busy}
                    title="Mark this follow-up done and roll to the next occurrence"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Done — advance to next
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="fu-empty-hint">No follow-up scheduled.</p>
          )}

          <div className="detail-section-title" style={{ marginTop: 30 }}>
            Reminder notes
          </div>
          <div className="field">
            <textarea
              rows={3}
              placeholder="What's the next step? What do you want to remember when this comes up?"
              disabled={readOnly}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              aria-label="Reminder notes"
            />
          </div>
          {!readOnly && (
            <div
              className={`fu-save-bar save-host${dirty ? ' dirty' : ''}${busy ? ' saving' : ''}${Number.isFinite(savedAt) ? ' has-save-last' : ''}`}
            >
              <span className="fu-save-hint">Unsaved changes</span>
              <button
                type="button"
                className="btn"
                id="fu-cancel-btn"
                onClick={cancelDraft}
                disabled={!dirty || busy}
              >
                Cancel
              </button>
              <SaveButton
                id="fu-save-btn"
                label="Save"
                busy={busy}
                disabled={!dirty}
                notice={saveNote}
                onCloseNotice={() => setSaveNote(null)}
                onClick={saveDraft}
              />
              {Number.isFinite(savedAt) && (
                <span className="save-last">{savedLine('Follow-up', savedAt, nowTick)}</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface ApptRow {
  id: string;
  startsAt: string;
  modality: string;
  status: string;
  location: string | null;
  /** ehr#414: the appointment's own video room (the Calendar's link), telehealth only. */
  videoUrl?: string | null;
  /** portal-credify#179: the note the client left when booking from the portal, if any. */
  clientNote?: string | null;
}

/** ehr#430 (ruled 10-17): copies an appointment's own video link (the Calendar's room). */
function CopyLinkButton({ url, className = 'btn sm' }: { url: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked: the link is still shown and selectable */
    }
  }
  return (
    <button
      type="button"
      className={className}
      onClick={() => void copy()}
      title={`Copy the video room link: ${url}`}
    >
      {copied ? 'Copied' : 'Copy link'}
    </button>
  );
}

/**
 * ehr#430 (ruled 10-17 by the operator for Jay): the Meeting room link card shows the next
 * video appointment's own room, the link the Calendar made (ehr#414), instead of minting a
 * new room that matched no appointment.
 */
function ApptRoomLink({ next }: { next: ApptRow | null }) {
  return (
    <div className="card panel" style={{ marginBottom: 12 }}>
      <div className="panel-item" style={{ alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div>
            <strong>Meeting room link</strong>
          </div>
          {next?.videoUrl ? (
            <>
              <div className="muted">
                The video room for the next appointment, {fmtDateTime(next.startsAt)}. Every
                telehealth appointment has its own room, made by the Calendar.
              </div>
              <a
                href={next.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ overflowWrap: 'anywhere' }}
              >
                {next.videoUrl}
              </a>
            </>
          ) : (
            <div className="muted">
              No upcoming video appointment. A telehealth appointment booked in the Calendar gets
              its own room, and its link shows here and on the appointment.
            </div>
          )}
        </div>
        {next?.videoUrl && <CopyLinkButton url={next.videoUrl} className="btn" />}
      </div>
    </div>
  );
}

/** #112 - the client's primary appointment location, chosen from the practice's
 *  Locations (Data & settings; GET /appointments/locations, calendar#33). Sits at
 *  the top of the Appointments tab, above the meeting-room card. Saves on change. */
function PrimaryLocationPicker({
  contactId,
  readOnly = false,
}: {
  contactId: string;
  readOnly?: boolean;
}) {
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [value, setValue] = useState('');
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    Promise.all([
      api.get<{ id: string; name: string }[]>('/appointments/locations').catch(() => []),
      api.get<{ primaryLocationId: string | null }>(`/contacts/${contactId}`).catch(() => null),
    ]).then(([locs, c]) => {
      if (!live) return;
      setLocations(Array.isArray(locs) ? locs : []);
      setValue(c?.primaryLocationId ?? '');
      setReady(true);
    });
    return () => {
      live = false;
    };
  }, [contactId]);

  const save = (v: string) => {
    setValue(v);
    setSaving(true);
    api
      .patch(`/contacts/${contactId}`, { primaryLocationId: v || null })
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  if (!ready) return null;

  return (
    // #112 - overflow:visible so the location dropdown panel isn't clipped by
    // .panel{overflow:hidden} (same clip that hid the Manage Users dropdown, #60).
    <div className="card panel" style={{ marginBottom: 12, overflow: 'visible' }}>
      <div className="panel-item" style={{ alignItems: 'center' }}>
        <div>
          <div>
            <strong>Primary appointment location</strong>
          </div>
          <div className="muted">
            {locations.length > 0
              ? 'The client’s usual location for appointments.'
              : 'No locations yet. Add them in Data & settings → Locations.'}
          </div>
        </div>
        {locations.length > 0 && (
          <div style={{ minWidth: 240 }}>
            <Dd
              value={value}
              onChange={save}
              disabled={saving || readOnly}
              searchable={locations.length > 6}
              ariaLabel="Primary appointment location"
              placeholder="Select a location…"
              options={[
                { value: '', label: 'Select a location…' },
                ...locations.map((l) => ({ value: l.id, label: l.name })),
              ]}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const APPT_MODALITY_OPTS = [
  { value: 'in_person', label: 'In-person' },
  { value: 'telehealth', label: 'Telehealth' },
  { value: 'hybrid', label: 'Hybrid' },
];
const APPT_PAST_PREVIEW = 5;
const modalityLabel = (m: string) =>
  APPT_MODALITY_OPTS.find((o) => o.value === m)?.label ?? m.replace('_', '-');
// calendar#61: the pill reads the Calendar's own status words (and the older core ones).
const APPT_PILL_CLASS: Record<ApptTone, string> = {
  scheduled: 's-scheduled',
  confirmed: 's-scheduled',
  checkedin: 's-checkedin',
  checkedout: 's-checkedout',
  completed: 's-completed',
  noshow: 's-noshow',
  cancelled: 's-cancelled',
};
const apptPillClass = (status: string) => APPT_PILL_CLASS[apptStatusInfo(status).tone];

/**
 * Appointments tab — per-contact history split into Upcoming / Past. Scheduling,
 * rescheduling, cancelling and outcomes all happen in the Calendar module, which owns
 * appointments (#138). ehr#317 (Jay): Schedule appointment opens the Calendar's New
 * appointment with this client filled in (?newAppt=<contactId>, Calendar V218) instead of
 * a short form here; the list reloads when this tab gets focus back.
 */
function AppointmentsPanel({
  contactId,
  readOnly = false,
}: {
  contactId: string;
  readOnly?: boolean;
}) {
  // ehr#730: a View tab (Share & Access) has no New appointment and no location change.
  // ehr#909: anyone else who can change the tab gets Schedule appointment (before, only an admin or
  // manager did, left over from #138's form here); it only opens the Calendar, which decides who
  // may book.
  const canEdit = !readOnly;
  const [rows, setRows] = useState<ApptRow[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [showAllPast, setShowAllPast] = useState(false);

  const load = () => {
    api
      .get<ApptRow[]>(`/contacts/${contactId}/appointments`)
      .then((r) => {
        setRows(r);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  };
  useEffect(load, [contactId]);
  // ehr#317 - an appointment booked in the Calendar shows up on returning to this tab.
  useEffect(() => {
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [contactId]);

  const now = Date.now();
  const sorted = [...rows].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const upcoming = sorted.filter((a) => new Date(a.startsAt).getTime() >= now);
  const past = sorted.filter((a) => new Date(a.startsAt).getTime() < now).reverse();
  const shownPast = showAllPast ? past : past.slice(0, APPT_PAST_PREVIEW);
  const nextVideo = upcoming.find((a) => a.videoUrl && !apptStatusInfo(a.status).cancelled) ?? null;

  const apptRow = (a: ApptRow) => (
    <div
      key={a.id}
      className={`appt-row${apptStatusInfo(a.status).cancelled ? ' is-cancelled' : ''}`}
    >
      <div className="appt-main">
        <div className="appt-when">
          {fmtDateTime(a.startsAt)}
          {a.clientNote && <ApptClientNoteIcon />}
        </div>
        <div className="appt-meta">
          {modalityLabel(a.modality)}
          {a.location ? ` · ${a.location}` : ''}
        </div>
      </div>
      <div className="appt-side">
        <span className={`appt-pill ${apptPillClass(a.status)}`}>
          {apptStatusInfo(a.status).label}
        </span>
        {a.videoUrl && !apptStatusInfo(a.status).cancelled && (
          // ehr#414: the room the Calendar made for this appointment, the same link everywhere
          <a
            className="btn sm primary"
            href={a.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={`Join the video room: ${a.videoUrl}`}
          >
            Join video
          </a>
        )}
        {a.videoUrl && !apptStatusInfo(a.status).cancelled && <CopyLinkButton url={a.videoUrl} />}
        <a
          className="btn sm"
          href={calendarApptHref(a.id)}
          target="_blank"
          rel="noopener noreferrer"
          title="Open in the Calendar to reschedule, cancel, or set the outcome"
        >
          Open in Calendar
        </a>
      </div>
      {a.clientNote && <ApptClientNote note={a.clientNote} />}
    </div>
  );

  return (
    <>
      <PrimaryLocationPicker contactId={contactId} readOnly={readOnly} />
      {status === 'ready' && <ApptRoomLink next={nextVideo} />}

      <div className="tab-toolbar">
        <div className="tab-toolbar-label">
          {upcoming.length} upcoming · {past.length} past
        </div>
        {canEdit && (
          <a
            className="auth-add-btn"
            href={calendarNewApptHref(contactId)}
            target="_blank"
            rel="noopener noreferrer"
            title="Opens the Calendar's New appointment with this client filled in"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Schedule appointment
          </a>
        )}
      </div>

      {status === 'loading' && <p className="muted">Loading appointments…</p>}
      {status === 'error' && <p className="muted">Could not load appointments.</p>}
      {status === 'ready' && rows.length === 0 && <p className="muted">{apptEmptyText(canEdit)}</p>}
      {status === 'ready' && upcoming.length > 0 && (
        <>
          <div className="appt-section-head">
            <span className="appt-section-label">Upcoming</span>
          </div>
          {upcoming.map(apptRow)}
        </>
      )}
      {status === 'ready' && past.length > 0 && (
        <>
          <div className="appt-section-head">
            <span className="appt-section-label">Past</span>
            {past.length > APPT_PAST_PREVIEW && (
              <button className="btn sm" onClick={() => setShowAllPast((v) => !v)}>
                {showAllPast ? 'Show less' : `Show all (${past.length})`}
              </button>
            )}
          </div>
          {shownPast.map(apptRow)}
        </>
      )}
    </>
  );
}

/** A saved per-contact form submission (P2). */
interface FormSubmissionRow {
  id: string;
  formId: string;
  formName: string;
  submittedBy: string;
  sharedWith: string[];
  /** credifyfbs#203: saved with required fields still blank. */
  draft?: boolean;
  createdAt: string;
  updatedAt: string;
  /** ehr#736: set on an archived fill (listed only with Show archived). */
  archivedAt?: string | null;
}

interface LibraryResponse {
  system: LibForm[];
  custom: LibForm[];
  meta: { customOk: boolean; customError?: string; total: number; formsBaseUrl: string };
}

// Fuzzy-match an EHR contact category (e.g. "client", "reseller") to a Form
// Builder contact-type string (e.g. "Client", "Reseller Partner"). Forms with no
// contact types apply to everyone.
function normType(s: string): string {
  return s.toLowerCase().replace(/[^a-z]/g, '');
}
function formMatchesCategory(f: LibForm, category: string): boolean {
  const cts = f.contactTypes ?? [];
  if (cts.length === 0) return true;
  const c = normType(category);
  return cts.some((t) => {
    const n = normType(t);
    return n.includes(c) || c.includes(n);
  });
}

const FileIcon = () => (
  <svg
    className="form-icon"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
  </svg>
);
const EyeIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const SearchIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/**
 * Forms tab — the ecosystem forms library (System screeners + Admin/custom forms
 * from the Form Builder), filtered by the contact's type like the CRM, with
 * View / Fill / Edit Fields opening an in-app popup (rendered inside the EHR —
 * no jump to the Form Builder).
 *
 * ehr#165 (Chase's AVAILABLE_FORMS_F): one table of every form with its status for this
 * client, grouped Filled for this client / Waiting on client / Not filled yet, with a filter
 * tab per group. Filled covers staff fills in the EHR and forms the client sent back through
 * the portal (portal#61); History lists both. Bundles get rows of their own. No Remind yet:
 * nothing reaches a client about a form until texting / email to clients runs.
 *
 * Access is global by clinic/org for now (any signed-in user, every org form the
 * proxy can resolve); per-user/role gating comes later.
 */
export function FormsPanel({
  patientId,
  contactCategory,
  contactName,
  openSubmissionId,
  initialKind = 'all',
  refreshKey,
  readOnly = false,
}: {
  patientId: string;
  contactCategory: string;
  contactName: string;
  /** ehr#412: the chart's live stamp; the filled forms and the sends reload when it moves. */
  refreshKey?: number;
  /** ehr#252: open this filled form read-only once it loads ("Form shared with you"). */
  openSubmissionId?: string;
  /** ehr#640: the kind filter to open on (an old link to the retired Screeners tab opens on
   *  Screeners). ehr#363's separate Screeners tab is folded into this one. */
  initialKind?: FormKind;
  /** ehr#730: a View tab (Share & Access): see and print fills; no New, Finish, Edit, Share or Archive. */
  readOnly?: boolean;
}) {
  const [lib, setLib] = useState<LibraryResponse | null>(null);
  const [libStatus, setLibStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  useEffect(() => {
    api
      .get<LibraryResponse>('/forms-library')
      .then((r) => {
        setLib(r);
        setLibStatus('ready');
      })
      .catch(() => setLibStatus('error'));
  }, []);

  // Saved form submissions for this contact (P2 — CRM "Submitted" section).
  const { user } = useSession();
  const [subs, setSubs] = useState<FormSubmissionRow[]>([]);
  // ehr#255: the submission waiting on the Credify-styled delete confirm.
  const [delSubId, setDelSubId] = useState<string | null>(null);
  const [subUsers, setSubUsers] = useState<NoteUser[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const loadSubs = () => {
    api
      .get<FormSubmissionRow[]>(`/contacts/${patientId}/form-submissions`)
      .then((r) => {
        setSubs(r);
        setStatus('ready');
      })
      .catch((e: unknown) =>
        setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error'),
      );
  };
  useEffect(loadSubs, [patientId]);
  useEffect(() => {
    // ehr#623: names for clinicians too (they can't read /settings/users).
    void fetchNoteUsers().then(setSubUsers);
  }, []);
  const subUserName = (id: string) => {
    // ehr#310: a client who checked in with the form's QR code sent this one themselves.
    if (id === QR_FORM_SUBMITTER) return 'the client (QR code)';
    const u = subUsers.find((x) => x.id === id);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : 'Unknown';
  };
  const canModifySub = (s: FormSubmissionRow) =>
    !readOnly && Boolean(user && (s.submittedBy === user.id || canManage(user.role)));
  // #133 - submissions for one form (drives each row's History button + popup).
  const subsFor = (formId: string) => subs.filter((s) => s.formId === formId);
  // ehr#736: Show archived (in a form's History) lists its archived fills, with Restore.
  const [showArchivedSubs, setShowArchivedSubs] = useState(false);
  const [archivedSubs, setArchivedSubs] = useState<FormSubmissionRow[] | null>(null);
  const loadArchivedSubs = () =>
    api
      .get<FormSubmissionRow[]>(archivedUrl(`/contacts/${patientId}/form-submissions`))
      .then(setArchivedSubs)
      .catch(() => setArchivedSubs([]));
  useEffect(() => {
    if (showArchivedSubs) void loadArchivedSubs();
    else setArchivedSubs(null);
  }, [showArchivedSubs, patientId]);
  const restoreSub = (subId: string) =>
    restoreItem({ kind: 'form', contactId: patientId, id: subId })
      .then(() => {
        loadSubs();
        void loadArchivedSubs();
      })
      .catch((e: unknown) => showError(errorText(e, 'Could not restore this form')));

  // portal#61 - the forms and bundles sent to this client through the portal (Forms Hub).
  // The client's answers live on the send's submission, not in form_submissions above, so
  // they are listed on their own and open in the Signoff Queue's preview (answers by form).
  const [sent, setSent] = useState<SentFormRow[]>([]);
  const [sentStatusState, setSentStatusState] = useState<
    'loading' | 'ready' | 'forbidden' | 'error'
  >('loading');
  const [sentOpen, setSentOpen] = useState<SentFormRow | null>(null);
  // ehr#412: a form the client finishes in the portal moves its row from waiting to filled without
  // reopening the drawer: the sends reload with the chart's stamp, every 20 s and on coming back
  // to the tab (a portal submission isn't on this chart's stamp).
  const sentFor = useRef(patientId);
  sentFor.current = patientId;
  const loadSent = useCallback(() => {
    const pid = patientId;
    api
      .get<SentFormRow[]>(`/dispatch/history?patientId=${pid}`)
      .then((r) => {
        if (sentFor.current !== pid) return;
        setSent(Array.isArray(r) ? r : []);
        setSentStatusState('ready');
      })
      .catch((e: unknown) => {
        if (sentFor.current !== pid) return;
        setSentStatusState(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error');
      });
  }, [patientId]);
  useEffect(loadSent, [loadSent]);
  useLiveRefresh(loadSent);
  useEffect(() => {
    if (!refreshKey) return;
    loadSent();
    loadSubs();
    // loadSubs reads only patientId, which loadSent already follows
  }, [refreshKey]);
  const sentTask = (row: SentFormRow): SignoffTaskRow => {
    const sub = row.submissions[0];
    return {
      id: row.id,
      stage: '',
      signedAt: null,
      dueAt: row.dueDate,
      submittedAt: sub?.submittedAt ?? null,
      patient: contactName,
      form: row.form?.name ?? row.packet?.name ?? 'Form',
      formId: row.formId ?? row.form?.id ?? null,
      packetId: row.packetId ?? row.packet?.id ?? null,
      submissionId: sub?.id ?? null,
      assignedTo: null,
    };
  };

  // Contact-type filter (CRM parity). 'auto' = narrow to this contact; 'all' =
  // everything; or a specific Form Builder contact type.
  const [ctFilter, setCtFilter] = useState<string>('auto');
  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    (lib?.custom ?? []).forEach((f) => (f.contactTypes ?? []).forEach((t) => set.add(t)));
    return [...set].sort();
  }, [lib]);

  // In-app popup state.
  const [openModal, setOpenModal] = useState<{
    form: LibForm;
    mode: FormMode;
    subId?: string;
    answers?: Record<string, string>;
  } | null>(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [shareSubId, setShareSubId] = useState<string | null>(null);

  // #133 - "New" opens a fresh fill of the form in-EHR (demographics autofilled
  // where mapped). `fillBusy` = the form id in flight.
  const [fillBusy, setFillBusy] = useState<string | null>(null);
  // #133 - per-form History popup: the form whose past submissions are shown.
  const [historyForm, setHistoryForm] = useState<LibForm | null>(null);
  // ehr#481: History on a bundle (or a send of a form no longer in the library).
  const [historySend, setHistorySend] = useState<FormRow | null>(null);

  // #54 - open a form to fill in the EHR, pre-filling each field that carries a
  // Form Builder dataKey from the contact's demographics (all fields sharing a key
  // fill). Prefill is best-effort: if demographics are unavailable (e.g. PHI-gated),
  // the form opens blank.
  const openFill = async (f: LibForm) => {
    if (fillBusy) return;
    setFillBusy(f.id);
    let answers: Record<string, string> = {};
    try {
      const r = await api.get<{ values: Record<string, string> }>(
        `/contacts/${patientId}/prefill-values`,
      );
      const values = r.values ?? {};
      for (const fld of f.fields ?? []) {
        const v = fld.dataKey ? values[fld.dataKey] : undefined;
        if (v != null && v !== '') answers[fld.id] = String(v);
      }
    } catch {
      answers = {};
    } finally {
      setFillBusy(null);
    }
    setOpenModal({ form: f, mode: 'fill', answers });
  };

  // Free-text search over the library (name + description).
  const [q, setQ] = useState('');

  // ehr#165 - the type filter (CRM parity): system forms apply to everyone; custom forms
  // narrowed to this contact ('auto'), to one Form Builder type, or not at all ('all').
  const typePass = useCallback(
    (f: LibForm) =>
      ctFilter === 'all' ||
      f.source === 'system' ||
      (ctFilter === 'auto'
        ? formMatchesCategory(f, contactCategory)
        : (f.contactTypes ?? []).length === 0 || (f.contactTypes ?? []).includes(ctFilter)),
    [ctFilter, contactCategory],
  );
  const [filter, setFilter] = useState<FormsFilter>('all');
  const [showAllUnfilled, setShowAllUnfilled] = useState(false);
  // ehr#640 (Chase 10-24): one Forms tab; the kind filter shows just the screeners.
  const [kind, setKind] = useState<FormKind>(initialKind);
  const allRows: FormRow[] = useMemo(
    () =>
      buildFormRows({
        forms: lib ? [...lib.system, ...lib.custom] : [],
        shown: typePass,
        fills: status === 'ready' ? subs : [],
        sent: sentStatusState === 'ready' ? sent : [],
        userName: subUserName,
      }),
    // subUserName reads subUsers, so a name lands once the user list loads.
    [lib, typePass, status, subs, sentStatusState, sent, subUsers],
  );
  const rows = useMemo(() => kindRows(allRows, kind), [allRows, kind]);
  const allFound = searchRows(allRows, q);
  const nScreeners = allFound.filter(rowIsScreener).length;
  const KINDS: { id: FormKind; label: string; n: number }[] = [
    { id: 'all', label: 'All', n: allFound.length },
    { id: 'forms', label: 'Forms', n: allFound.length - nScreeners },
    { id: 'screeners', label: 'Screeners', n: nScreeners },
  ];
  const found = searchRows(rows, q);
  const groups = grouped(found, q.trim() !== ''); // ehr#276: best matches first while searching
  // #133 + portal#61 - the forms this client sent back through the portal, per form.
  const portalBack = (formId: string) =>
    sent
      .filter((r) => !r.packetId && r.formId === formId && sendState(r) === 'done')
      .sort((a, b) => new Date(sentBackAt(b)).getTime() - new Date(sentBackAt(a)).getTime());
  // History: the client's portal fills and the staff fills of one form, newest first.
  type HistEntry =
    | { kind: 'portal'; at: number; row: SentFormRow }
    | { kind: 'staff'; at: number; s: FormSubmissionRow };
  const historyEntries = (formId: string): HistEntry[] =>
    [
      ...portalBack(formId).map((row): HistEntry => ({
        kind: 'portal',
        at: new Date(sentBackAt(row)).getTime(),
        row,
      })),
      ...subsFor(formId).map((s): HistEntry => ({
        kind: 'staff',
        at: new Date(s.createdAt).getTime(),
        s,
      })),
    ].sort((a, b) => b.at - a.at);

  // credifyfbs#129 - a scored form's History popup opens with the client's score table:
  // each fill's answers (staff fills and portal fills, up to the latest 24) are loaded
  // when the popup opens, and again if a fill is added or deleted while it's open.
  const SCORE_FILLS_MAX = 24;
  const [scoreFills, setScoreFills] = useState<{
    formId: string;
    status: 'loading' | 'ready';
    fills: ScoreFill[];
  } | null>(null);
  const historyScored = historyForm
    ? Boolean(historyForm.scoring?.sections.length) || // ehr#429
      scoredItems(historyForm.fields ?? []).items.length > 0
    : false;
  useEffect(() => {
    if (!historyForm || !historyScored) {
      setScoreFills(null);
      return;
    }
    const formId = historyForm.id;
    // credifyfbs#203: a draft's blank answers aren't a score.
    const entries = historyEntries(formId)
      .filter((e) => !(e.kind === 'staff' && e.s.draft))
      .slice(0, SCORE_FILLS_MAX);
    let live = true;
    setScoreFills((prev) =>
      prev && prev.formId === formId ? prev : { formId, status: 'loading', fills: [] },
    );
    void Promise.all(
      entries.map(async (e): Promise<ScoreFill | null> => {
        try {
          if (e.kind === 'staff') {
            const s = await api.get<{
              answers: Record<string, unknown>;
              layout?: FilledLayout | null;
              score?: { total: number; band: string | null } | null;
            }>(`/contacts/${patientId}/form-submissions/${e.s.id}`);
            return {
              id: e.s.id,
              at: e.s.createdAt,
              by: subUserName(e.s.submittedBy),
              answers: s.answers ?? {},
              // ehr#592: read on the questions it was filled on, with the score saved with it
              layout: s.layout?.fields ?? null,
              saved: s.score ? { total: s.score.total, band: s.score.band ?? null } : null,
            };
          }
          const subId = e.row.submissions[0]?.id;
          if (!subId) return null;
          const a = await api.get<SubmissionAnswers>(`/submissions/${subId}/answers`);
          const f = a.forms.find((x) => x.formId === formId) ?? a.forms[0];
          if (!a.hasAnswers || !f) return null;
          return {
            id: e.row.id,
            at: sentBackAt(e.row),
            by: 'the client, in the portal',
            answers: portalAnswers(f.fields),
            // ehr#592 / ehr#593: the questions the client saw, when the portal kept them
            questions: f.asFilled
              ? f.fields.map((x) => ({ id: x.fieldId, label: x.label, type: x.fieldType }))
              : null,
          };
        } catch {
          return null; // e.g. a role without PHI access: that fill is left out
        }
      }),
    ).then((r) => {
      if (live) setScoreFills({ formId, status: 'ready', fills: r.filter((x) => x !== null) });
    });
    return () => {
      live = false;
    };
    // historyEntries / subUserName read subs, sent and subUsers.
  }, [historyForm, historyScored, subs, sent, subUsers, patientId]);
  const scoreHistory =
    historyForm &&
    scoreFills &&
    scoreFills.formId === historyForm.id &&
    scoreFills.status === 'ready'
      ? buildScoreHistory(
          historyForm.name,
          historyForm.fields ?? [],
          scoreFills.fills,
          historyForm.scoring, // ehr#429
        )
      : null;

  const findForm = (formId: string) =>
    [...(lib?.custom ?? []), ...(lib?.system ?? [])].find((f) => f.id === formId);
  // Backend answer values are string | string[]; the modal fields take strings.
  const normalizeAnswers = (a: Record<string, unknown>): Record<string, string> =>
    Object.fromEntries(
      Object.entries(a).map(([k, v]) => [k, Array.isArray(v) ? v.join(', ') : String(v ?? '')]),
    );

  // Fill (create) or Edit (update) a submission — persisted via the modal's Save.
  const saveSubmission = (answers: Record<string, string>, { draft }: { draft: boolean }) => {
    if (!openModal) return;
    setModalSaving(true);
    const req = openModal.subId
      ? api.patch(`/contacts/${patientId}/form-submissions/${openModal.subId}`, {
          answers,
          draft,
        })
      : api.post(`/contacts/${patientId}/form-submissions`, {
          formId: openModal.form.id,
          formName: openModal.form.name,
          answers,
          draft,
          // credifyfbs#241: the questions it's filled on, so it reopens that way after form edits
          layout: layoutOf(openModal.form),
        });
    req
      .then(() => {
        setOpenModal(null);
        loadSubs();
      })
      // ehr#412: a save that failed said nothing; the form stays open with what was typed
      .catch((e: unknown) => showError(`Could not save this form. ${errorText(e)}`))
      .finally(() => setModalSaving(false));
  };
  // Open a saved submission for view (read-only) or edit (fill w/ its answers).
  const openSub = (subId: string, mode: FormMode) => {
    api
      .get<{
        formId: string;
        formName: string;
        answers: Record<string, unknown>;
        layout?: FilledLayout | null;
      }>(`/contacts/${patientId}/form-submissions/${subId}`)
      .then((s) => {
        // credifyfbs#241: View and Edit draw the questions it was filled on
        const form = formAsFilled(findForm(s.formId), s);
        setOpenModal({
          form,
          mode,
          subId: mode === 'fill' ? subId : undefined,
          answers: normalizeAnswers(s.answers),
        });
      })
      .catch(() => undefined);
  };
  // ehr#252 - opened from a "Form shared with you" notice: show that fill straight away, once.
  // A fill this person can no longer see (unshared since) just leaves the Forms tab open.
  const openedFromNotice = useRef(false);
  useEffect(() => {
    if (!openSubmissionId || openedFromNotice.current) return;
    if (status !== 'ready' || libStatus === 'loading') return;
    openedFromNotice.current = true;
    if (subs.some((s) => s.id === openSubmissionId)) openSub(openSubmissionId, 'view');
    // openSub reads lib + patientId, both settled by the time status and libStatus are.
  }, [openSubmissionId, status, libStatus, subs]);
  const delSub = (subId: string) => setDelSubId(subId);
  const confirmDelSub = () => {
    const subId = delSubId;
    setDelSubId(null);
    if (!subId) return;
    // ehr#736 (Jay 10-31): archived, never deleted
    archiveItem({ kind: 'form', contactId: patientId, id: subId })
      .then(() => {
        loadSubs();
        if (showArchivedSubs) void loadArchivedSubs();
      })
      .catch((e: unknown) => showError(errorText(e, 'Could not archive this form')));
  };
  const shareSub = (subId: string, userIds: string[]) => {
    api
      .post(`/contacts/${patientId}/form-submissions/${subId}/share`, { userIds })
      .then(() => {
        setShareSubId(null);
        loadSubs();
      })
      .catch(() => undefined);
  };
  // Export a submission as PDF via the browser's print-to-PDF (no dependency).
  const pdfSub = (subId: string) => {
    api
      .get<{
        formId: string;
        formName: string;
        submittedBy: string;
        createdAt: string;
        answers: Record<string, unknown>;
        layout?: FilledLayout | null;
      }>(`/contacts/${patientId}/form-submissions/${subId}`)
      .then((s) => {
        // ehr#253: laid out like the clinician View (rating grid, chosen answers, score);
        // credifyfbs#241: on the questions it was filled on.
        const form = formAsFilled(findForm(s.formId), s);
        const at = new Date(s.createdAt);
        const date = isNaN(at.getTime())
          ? ''
          : at.toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            });
        const html = formPrintHtml({
          title: s.formName,
          meta: [
            contactName,
            date,
            s.submittedBy ? `Submitted by ${subUserName(s.submittedBy)}` : '',
          ]
            .filter(Boolean)
            .join(' · '),
          description: form.description,
          fields: form.fields,
          answers: s.answers,
        });
        const w = window.open('', '_blank', 'width=900,height=1000');
        if (!w) return;
        w.document.write(html);
        w.document.close();
        w.focus();
        setTimeout(() => w.print(), 300);
      })
      .catch(() => undefined);
  };

  const ICONS: Record<FormRow['state'], ReactNode> = {
    done: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
    sent: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
      </svg>
    ),
    inc: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        aria-hidden
      >
        <path d="M12 2a10 10 0 1 0 10 10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    none: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
    ),
  };
  const TABS: { id: FormsFilter; label: string; dot?: string; n: number }[] = [
    {
      id: 'all',
      label: kind === 'screeners' ? 'All screeners' : 'All forms',
      n: found.length,
    },
    { id: 'done', label: 'Filled', dot: 'done', n: groups.done.length },
    // Forms Hub sends are front desk / managers only (403): no Waiting tab rather than a 0.
    ...(sentStatusState === 'forbidden'
      ? []
      : [{ id: 'pend' as const, label: 'Waiting on client', dot: 'pend', n: groups.pend.length }]),
    { id: 'none', label: 'Not filled', dot: 'none', n: groups.none.length },
  ];
  // ehr#481: on every row, even at 0; a bundle's lists the client's returns of that bundle.
  const historyBtn = (r: FormRow) => (
    <button
      className="ftb-btn"
      title={
        r.form ? 'Past fills of this form for this client' : 'What the client sent back before'
      }
      disabled={r.historyCount === 0}
      onClick={() => (r.form ? setHistoryForm(r.form) : setHistorySend(r))}
    >
      History <span className="ftb-cnt">{r.historyCount}</span>
    </button>
  );
  const previewBtn = (r: FormRow) => (
    <button
      className="ftb-btn ftb-btn-i"
      title={r.form ? 'Preview this form' : 'See what the client sent back'}
      aria-label={r.form ? `Preview ${r.name}` : `See what the client sent back for ${r.name}`}
      disabled={!r.form && !r.send?.submissions[0]}
      onClick={() =>
        r.form ? setOpenModal({ form: r.form, mode: 'view' }) : r.send && setSentOpen(r.send)
      }
    >
      <EyeIcon />
    </button>
  );
  // The first action: New / Fill again on a library form; View on a send waiting on the
  // client (what it saved so far) or on a bundle the client sent back.
  const firstBtn = (r: FormRow) => {
    // ehr#730: a View tab has no New, Fill again or Finish (seeing what the client sent stays)
    if (readOnly && (r.draftId || (r.group !== 'pend' && r.form))) return null;
    // credifyfbs#203: the latest fill is a draft: open it to finish.
    if (r.draftId) {
      const id = r.draftId;
      return (
        <button
          className="ftb-btn ftb-btn-p"
          title="Finish the draft"
          onClick={() => openSub(id, 'fill')}
        >
          Finish
        </button>
      );
    }
    if (r.group === 'pend' || !r.form) {
      const canView = r.state === 'inc' || r.state === 'done';
      return (
        <button
          className="ftb-btn"
          title={canView ? "See the client's answers" : 'Nothing sent back yet'}
          disabled={!canView || !r.send?.submissions[0]}
          onClick={() => r.send && setSentOpen(r.send)}
        >
          View
        </button>
      );
    }
    const f = r.form;
    return (
      <button
        className={`ftb-btn ${r.group === 'done' ? 'ftb-btn-o' : 'ftb-btn-p'}`}
        title={
          r.group === 'done'
            ? 'Fill out this form again for this client'
            : 'Fill out this form for this client'
        }
        disabled={fillBusy === f.id}
        onClick={() => void openFill(f)}
      >
        {fillBusy === f.id ? 'Opening…' : r.group === 'done' ? 'Fill again' : 'New'}
      </button>
    );
  };
  const formRow = (r: FormRow) => (
    <div key={r.key} className={`ftb-row s-${r.state}`} role="row">
      <div className="ftb-ico" role="cell">
        {ICONS[r.state]}
      </div>
      <div className="ftb-form" role="cell">
        <div className="ftb-name" title={r.name}>
          {r.name}
        </div>
        <div className="ftb-meta" title={r.meta}>
          {r.meta}
        </div>
      </div>
      <div className="ftb-src" role="cell">
        {r.source}
      </div>
      <div className="ftb-st" role="cell">
        <span className="ftb-st-l">{r.status}</span>
        {r.detail && <span className="ftb-st-s">{r.detail}</span>}
      </div>
      <div className="ftb-acts" role="cell">
        {firstBtn(r)}
        {historyBtn(r)}
        {previewBtn(r)}
      </div>
    </div>
  );
  const groupRows = (g: RowGroup) => {
    const list = groups[g];
    if (!list.length) return null;
    const cap = g === 'none' && !showAllUnfilled && !q.trim() ? UNFILLED_PAGE : list.length;
    return (
      <div key={g} role="rowgroup">
        <div className="ftb-grp" role="row">
          <span role="columnheader">{GROUP_LABELS[g]}</span>
          <span className="ftb-n">{list.length}</span>
        </div>
        {list.slice(0, cap).map(formRow)}
        {cap < list.length && (
          <div className="ftb-foot">
            Showing {cap} of {list.length} unfilled forms.{' '}
            <button className="link-btn" onClick={() => setShowAllUnfilled(true)}>
              Show all
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="forms-toolbar">
        <div className="forms-search">
          <SearchIcon />
          <input
            type="search"
            placeholder={
              lib
                ? `Search ${rows.length} ${kind === 'screeners' ? 'screeners' : 'forms'} by name or description`
                : 'Search forms…'
            }
            aria-label="Search forms by name or description"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div style={{ minWidth: 190, flexShrink: 0 }}>
          <Dd
            value={ctFilter}
            searchable={false}
            options={[
              { value: 'auto', label: `This contact (${contactCategory.replace('_', ' ')})` },
              { value: 'all', label: 'All types' },
              ...availableTypes.map((t) => ({ value: t, label: t })),
            ]}
            onChange={setCtFilter}
          />
        </div>
      </div>

      {libStatus === 'loading' && <p className="muted">Loading forms…</p>}
      {libStatus === 'error' && <p className="muted">Could not load the forms library.</p>}
      {libStatus === 'ready' && lib && (
        <>
          {!lib.meta.customOk && lib.meta.customError && (
            <p className="muted" style={{ marginTop: 0 }}>
              <span className="badge warn">custom unavailable</span> {lib.meta.customError}
            </p>
          )}
          <div className="ftb-filters" role="tablist" aria-label="Show forms">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={filter === t.id}
                className={`ftb-ftab${filter === t.id ? ' on' : ''}`}
                onClick={() => setFilter(t.id)}
              >
                {t.dot && <span className={`ftb-dot ${t.dot}`} aria-hidden />}
                {t.label} <span className="ftb-n">{t.n}</span>
              </button>
            ))}
            {/* ehr#640: the kind filter, in the same row and style as the status tabs. */}
            <span className="ftb-kinds" role="group" aria-label="Form or screener">
              {KINDS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  aria-pressed={kind === k.id}
                  className={`ftb-ftab${kind === k.id ? ' on' : ''}`}
                  onClick={() => setKind(k.id)}
                >
                  {k.label} <span className="ftb-n">{k.n}</span>
                </button>
              ))}
            </span>
          </div>
          {found.length === 0 ? (
            <p className="muted">
              {q.trim()
                ? `No ${kind === 'screeners' ? 'screeners' : 'forms'} match “${q.trim()}”.`
                : kind === 'screeners'
                  ? 'No screeners for this contact type.'
                  : 'No forms for this contact type.'}
            </p>
          ) : (
            <div className="ftb-tbl" role="table" aria-label="Forms for this client">
              <div className="ftb-row ftb-thead" role="row">
                <div role="columnheader" />
                <div role="columnheader">Form</div>
                <div role="columnheader">Source</div>
                <div role="columnheader">Status for this client</div>
                <div role="columnheader">Actions</div>
              </div>
              {(filter === 'all' ? (['done', 'pend', 'none'] as RowGroup[]) : [filter]).map((g) =>
                groupRows(g as RowGroup),
              )}
              {filter !== 'all' && groups[filter as RowGroup].length === 0 && (
                <div className="ftb-empty">
                  {filter === 'done'
                    ? 'Nothing filled for this client yet.'
                    : filter === 'pend'
                      ? 'Nothing waiting on the client.'
                      : 'Every form has been filled for this client.'}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {sentStatusState === 'error' && (
        <p className="muted">Could not load the forms sent through the portal.</p>
      )}
      {sentOpen && <FormPreviewModal task={sentTask(sentOpen)} onClose={() => setSentOpen(null)} />}
      {status === 'forbidden' && (
        <p className="muted">Your role cannot view form submissions (403).</p>
      )}
      {status === 'error' && <p className="muted">Could not load form submissions.</p>}

      {/* ehr#481 - a bundle's History: each time the client sent it back. */}
      {historySend && (
        <Modal
          title={`${historySend.name} · History`}
          className="forms-history-modal"
          onClose={() => setHistorySend(null)}
          actions={
            <button className="btn" onClick={() => setHistorySend(null)}>
              Close
            </button>
          }
        >
          {(historySend.historySends ?? []).length === 0 ? (
            <p className="muted">Nothing sent back yet.</p>
          ) : (
            <div className="forms-card-list">
              {(historySend.historySends ?? []).map((row) => (
                <div key={row.id} className="form-card">
                  <FileIcon />
                  <div className="form-card-body">
                    <div className="form-card-title">{historySend.name}</div>
                    <div className="form-card-meta">
                      {fmtDay(sentBackAt(row))} · by the client, in the portal · sent{' '}
                      {fmtDay(row.sentAt)}
                    </div>
                  </div>
                  <div className="form-card-actions">
                    <button
                      className="btn sm"
                      title="See what the client sent back"
                      onClick={() => {
                        setHistorySend(null);
                        setSentOpen(row);
                      }}
                    >
                      <EyeIcon />
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {/* #133 - per-form History popup: this contact's past submissions of one form. */}
      {historyForm && (
        <Modal
          title={`${historyForm.name} · History`}
          className={`forms-history-modal${historyScored ? ' fhm-scores' : ''}`}
          onClose={() => setHistoryForm(null)}
          actions={
            <>
              <button className="btn" onClick={() => setHistoryForm(null)}>
                Close
              </button>
              {!readOnly && (
                <button
                  className="btn primary"
                  disabled={fillBusy === historyForm.id}
                  onClick={() => {
                    const f = historyForm;
                    setHistoryForm(null);
                    void openFill(f);
                  }}
                >
                  {fillBusy === historyForm.id ? 'Opening…' : 'New'}
                </button>
              )}
            </>
          }
        >
          {/* ehr#252 (Chase 10-10): the fills first, then the score table built from them */}
          {historyScored &&
            (subsFor(historyForm.id).length > 0 || portalBack(historyForm.id).length > 0) && (
              <div className="sh-fills-label">Fills</div>
            )}
          {subsFor(historyForm.id).length === 0 && portalBack(historyForm.id).length === 0 ? (
            <p className="muted">No fills yet — use New to complete this form.</p>
          ) : (
            <div className="forms-card-list">
              {historyEntries(historyForm.id).map((e) =>
                e.kind === 'portal' ? (
                  <div key={e.row.id} className="form-card">
                    <FileIcon />
                    <div className="form-card-body">
                      <div className="form-card-title">{historyForm.name}</div>
                      <div className="form-card-meta">
                        {fmtDay(sentBackAt(e.row))} · by the client, in the portal ·{' '}
                        {sentStatus(e.row).text}
                      </div>
                    </div>
                    <div className="form-card-actions">
                      <button
                        className="btn sm"
                        title="See the form with the client's answers"
                        onClick={() => {
                          setHistoryForm(null);
                          setSentOpen(e.row);
                        }}
                      >
                        <EyeIcon />
                        View
                      </button>
                    </div>
                  </div>
                ) : (
                  <div key={e.s.id} className="form-card">
                    <FileIcon />
                    <div className="form-card-body">
                      <div className="form-card-title">
                        {e.s.formName}
                        {e.s.draft && (
                          <span
                            className="badge draft"
                            title="Saved as a draft. Edit to finish it."
                          >
                            Draft
                          </span>
                        )}
                      </div>
                      <div className="form-card-meta">
                        {fmtDate(e.s.createdAt)} · {subUserName(e.s.submittedBy)} ·{' '}
                        {daysAgo(e.s.createdAt)}
                        {e.s.submittedBy === user?.id && e.s.sharedWith.length > 0 && (
                          <span className="fs-shared">
                            {' '}
                            · Shared with{' '}
                            {e.s.sharedWith.map((id) => subUserName(id).split(' ')[0]).join(', ')}
                          </span>
                        )}
                        {e.s.submittedBy !== user?.id &&
                          e.s.submittedBy !== QR_FORM_SUBMITTER &&
                          e.s.sharedWith.includes(user?.id ?? '') && (
                            <span className="fs-shared">
                              {' '}
                              · Shared by {subUserName(e.s.submittedBy).split(' ')[0]}
                            </span>
                          )}
                      </div>
                    </div>
                    <div className="form-card-actions">
                      <button
                        className="btn sm"
                        title="View submission"
                        onClick={() => {
                          setHistoryForm(null);
                          openSub(e.s.id, 'view');
                        }}
                      >
                        <EyeIcon />
                        View
                      </button>
                      <button
                        className="btn sm"
                        title="Export as PDF"
                        onClick={() => pdfSub(e.s.id)}
                      >
                        PDF
                      </button>
                      {canModifySub(e.s) && (
                        <>
                          <div className="share-wrap">
                            <button
                              className="btn sm"
                              title="Share with others"
                              aria-label="Share with others"
                              onClick={() => setShareSubId((x) => (x === e.s.id ? null : e.s.id))}
                            >
                              <ShareIcon />
                            </button>
                            {shareSubId === e.s.id && (
                              <SharePicker
                                users={subUsers}
                                current={e.s.sharedWith}
                                myId={user?.id}
                                onSave={(ids) => shareSub(e.s.id, ids)}
                                onClose={() => setShareSubId(null)}
                              />
                            )}
                          </div>
                          <button
                            className="btn sm"
                            title="Edit submission"
                            onClick={() => {
                              setHistoryForm(null);
                              openSub(e.s.id, 'fill');
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn sm"
                            title="Archive submission"
                            onClick={() => delSub(e.s.id)}
                          >
                            <ArchiveIcon />
                            Archive
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ),
              )}
            </div>
          )}
          <div className="archived-bar">
            <ShowArchivedToggle
              on={showArchivedSubs}
              onChange={setShowArchivedSubs}
              count={archivedSubs?.filter((x) => x.formId === historyForm.id).length}
            />
          </div>
          {showArchivedSubs && archivedSubs && (
            <div className="forms-card-list archived-list" aria-label="Archived fills">
              {archivedSubs.filter((x) => x.formId === historyForm.id).length === 0 ? (
                <p className="muted">No archived fills of this form.</p>
              ) : (
                archivedSubs
                  .filter((x) => x.formId === historyForm.id)
                  .map((a) => (
                    <div key={a.id} className="form-card archived">
                      <FileIcon />
                      <div className="form-card-body">
                        <div className="form-card-title">
                          {a.formName}
                          <span className="badge archived-tag">Archived</span>
                        </div>
                        <div className="form-card-meta">
                          {fmtDate(a.createdAt)} · {subUserName(a.submittedBy)}
                        </div>
                      </div>
                      <div className="form-card-actions">
                        <button
                          className="btn sm"
                          title="View submission"
                          onClick={() => {
                            setHistoryForm(null);
                            openSub(a.id, 'view');
                          }}
                        >
                          <EyeIcon />
                          View
                        </button>
                        {canModifySub(a) && <RestoreButton onClick={() => void restoreSub(a.id)} />}
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}
          {/* credifyfbs#129 - the score table built from the fills above */}
          {historyScored &&
            (subsFor(historyForm.id).length > 0 || portalBack(historyForm.id).length > 0) && (
              <section className="sh-section sh-after-fills" aria-label="Score history">
                <div className="sh-head">
                  <span className="sh-title">Score history</span>
                  {scoreHistory && (
                    <span className="sh-sub">
                      {scoreHistory.fills.length} fill{scoreHistory.fills.length === 1 ? '' : 's'}
                      {historyEntries(historyForm.id).length > SCORE_FILLS_MAX &&
                        ` · the latest ${SCORE_FILLS_MAX}`}
                    </span>
                  )}
                </div>
                {!scoreHistory ? (
                  <p className="muted sh-loading">Loading scores…</p>
                ) : scoreHistory.fills.length === 0 ? (
                  <p className="muted">No answers to score in these fills.</p>
                ) : (
                  <ScoreHistoryTable h={scoreHistory} />
                )}
              </section>
            )}
        </Modal>
      )}
      {delSubId && (
        <ConfirmDialog
          message="Archive this form submission? It leaves the form's History and its scores. Turn on Show archived to see it again or restore it."
          confirmLabel="Archive"
          danger={false}
          onConfirm={confirmDelSub}
          onClose={() => setDelSubId(null)}
        />
      )}

      {openModal && (
        <FormModal
          form={openModal.form}
          mode={openModal.mode}
          initialAnswers={openModal.answers}
          saving={modalSaving}
          onSave={openModal.mode === 'fill' ? saveSubmission : undefined}
          onClose={() => setOpenModal(null)}
        />
      )}
    </>
  );
}

interface TaskRow {
  id: string;
  title: string;
  status: TaskViewStatus;
  priority: string;
  dueAt: string | null;
  doneAt: string | null;
  contactId: string | null;
  createdById: string;
  assignees: { userId: string }[];
  sharedWith?: string[];
}

/** Tasks tab — CRM-parity list (open/completed, overdue, assignee) + full drawer. */
function ContactTasksPanel({
  contactId,
  refreshKey = 0,
  openTaskId,
  readOnly = false,
}: {
  contactId: string;
  /** ehr#342: bumps when the chart's stamp moves (a teammate's change). */
  refreshKey?: number;
  /** alertshub#7: a task to open on arrival (an Alerts Hub card's link, ?contact&view=tasks&record). */
  openTaskId?: string;
  /** ehr#730: a View tab (Share & Access): no New Task; only their own tasks can be ticked or moved. */
  readOnly?: boolean;
}) {
  const { user } = useSession();
  const [rows, setRows] = useState<TaskRow[]>([]);
  // ehr#730: on a View tab someone else's task says why it can't be changed (the server refuses too)
  const mayChange = (taskId: string) => {
    const t = rows.find((r) => r.id === taskId);
    if (!readOnly || (t && ownTask(t, user?.id))) return true;
    showError(ACCESS_DENIED_MESSAGE);
    return false;
  };
  const [users, setUsers] = useState<NoteUser[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [q, setQ] = useState(''); // #27 — search tasks by title
  const [view, setView] = useState<'list' | 'board'>('list'); // #27 — List / Board
  const [drawer, setDrawer] = useState<{ open: boolean; editId: string | null; fresh?: boolean }>(
    () => (openTaskId ? { open: true, editId: openTaskId } : { open: false, editId: null }),
  );
  const load = () => {
    api
      .get<TaskRow[]>(`/tasks?contact=${contactId}`)
      .then((r) => {
        setRows(r);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  };
  useEffect(load, [contactId, refreshKey]);
  useEffect(() => {
    // ehr#623: names for clinicians too (they can't read /settings/users).
    void fetchNoteUsers().then(setUsers);
  }, []);

  // Create-on-open, pre-linked to this contact, then open the full drawer on it.
  const newTask = () => {
    api
      .post<{ id: string }>('/tasks', { title: '', priority: 'normal', contactId, draft: true })
      .then((t) => setDrawer({ open: true, editId: t.id, fresh: true }))
      .catch((e: unknown) =>
        showError(e instanceof ApiError ? e.message : 'Could not create task'),
      );
  };
  const toggle = (t: TaskRow) => {
    if (!mayChange(t.id)) return;
    api
      .post(`/tasks/${t.id}/${t.doneAt ? 'reopen' : 'done'}`, {})
      .then(load)
      .catch(() => undefined);
  };

  if (status === 'loading') return <p className="muted">Loading tasks…</p>;
  if (status === 'error') return <p className="muted">Could not load tasks.</p>;

  const ql = q.trim().toLowerCase();
  const matchQ = (t: TaskRow) => !ql || t.title.toLowerCase().includes(ql);
  // ehr#411 / ehr#659: closed = not open, by the rule the Tasks page and the Dashboard use
  const closed = (t: TaskRow) => !isOpenTask(t);
  const openCount = rows.filter((t) => !closed(t)).length;
  const completedCount = rows.length - openCount;
  const shown = rows
    .filter(matchQ)
    .slice()
    .sort((a, b) => {
      const ac = closed(a) ? 1 : 0;
      const bc = closed(b) ? 1 : 0;
      if (ac !== bc) return ac - bc; // open before completed
      const ad = a.dueAt ? new Date(a.dueAt).getTime() : Infinity;
      const bd = b.dueAt ? new Date(b.dueAt).getTime() : Infinity;
      return ad - bd; // then soonest due first
    });
  const openTask = (id: string) => setDrawer({ open: true, editId: id });
  const moveStatus = (taskId: string, s: TaskViewStatus) => {
    if (!mayChange(taskId)) return;
    api
      .patch(`/tasks/${taskId}`, { status: s })
      .then(load)
      .catch(() => undefined);
  };

  return (
    <div className="drawer-panel">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-main)' }}>{openCount}</strong> open · {completedCount}{' '}
          completed
        </p>
        <button
          className="btn primary"
          hidden={readOnly}
          style={{
            fontSize: 12,
            padding: '7px 12px',
            minHeight: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
          onClick={newTask}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Task
        </button>
      </div>
      {rows.length === 0 ? (
        <div
          style={{
            padding: '32px 16px',
            textAlign: 'center',
            background: 'var(--surface)',
            border: '1px dashed var(--border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-muted)',
            fontSize: 13,
          }}
        >
          <p style={{ margin: '0 0 8px' }}>
            <strong style={{ color: 'var(--text-main)', fontWeight: 600 }}>No tasks yet</strong>
          </p>
          <p style={{ margin: 0, fontSize: 12, lineHeight: 1.55 }}>
            Add to-dos for this contact — they&apos;ll show up here and on the Tasks page.
          </p>
        </div>
      ) : (
        <>
          <div className="ct-tasks-toolbar">
            <input
              type="text"
              className="ct-task-search"
              placeholder="Search tasks…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search tasks"
            />
            <div className="ct-tasks-viewtoggle" role="tablist" aria-label="Task view">
              <button
                type="button"
                className={view === 'list' ? 'active' : ''}
                aria-pressed={view === 'list'}
                onClick={() => setView('list')}
              >
                List
              </button>
              <button
                type="button"
                className={view === 'board' ? 'active' : ''}
                aria-pressed={view === 'board'}
                onClick={() => setView('board')}
              >
                Board
              </button>
            </div>
          </div>
          {shown.length === 0 ? (
            <p className="muted" style={{ fontSize: 12.5, padding: '12px 2px' }}>
              No tasks match your search.
            </p>
          ) : view === 'board' ? (
            <TaskBoardView
              tasks={shown}
              users={users}
              onOpen={openTask}
              onMove={moveStatus}
              scroll
            />
          ) : (
            <TaskListView tasks={shown} users={users} onOpen={openTask} onToggle={toggle} compact />
          )}
        </>
      )}
      {/* ehr#736: archived tasks (never deleted), with Restore */}
      <ArchivedTasks
        query={`contact=${contactId}`}
        onOpen={openTask}
        onRestored={load}
        canRestore={readOnly ? (t) => ownTask(t, user?.id) : undefined}
      />
      {drawer.open && (
        <TaskDrawer
          editId={drawer.editId}
          fresh={drawer.fresh}
          viewOnlyTab={readOnly}
          onClose={() => setDrawer({ open: false, editId: null })}
          onSaved={() => {
            setDrawer({ open: false, editId: null });
            load();
          }}
        />
      )}
    </div>
  );
}

/** Stage Actions tab — stage-based workflow actions (faithful stub; mockup stubs too). */
function StageActionsPanel() {
  return (
    <div className="coming-soon">
      <h2>Stage Actions</h2>
      <p className="muted">
        Stage-based workflow actions (touch rules and status transitions) surface here. Configure
        them under <strong>Settings → Touch Rules</strong>.
      </p>
    </div>
  );
}

export type DrawerView =
  | 'summary'
  | 'demographics'
  | 'followup'
  | 'appointments'
  | 'clinical_notes'
  | 'notes'
  | 'forms'
  | 'screeners'
  | 'stage_actions'
  | 'tasks'
  | 'email'
  | 'messages'
  | 'eligibility'
  | 'authorizations'
  | 'treatment_plans'
  | 'billing'
  | 'files'
  | 'licensure'
  | 'cadence'
  | 'activity';

/** Email tab — Email/SMS threads (faithful stub; the mockup wires this to Cluster Email later). */
function EmailPanel() {
  return (
    <div className="coming-soon">
      <h2>Email</h2>
      <p className="muted">
        Email &amp; SMS threads integrate with the Cluster Email module. Live send/receive wires in
        a future phase.
      </p>
    </div>
  );
}

// ── Touches popover (V34 · CRM-parity) ────────────────────────────────────
// The drawer's "+ Touches" button opens ACTIONS → CURRENT STATUS → History,
// logging free-form touches to POST /contacts/:id/touches (append-only).
interface Touch {
  id: string;
  channel: string;
  outcome: string;
  label: string | null;
  note: string | null;
  occurredAt: string;
  actorId: string | null;
}
interface StatusChange {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  label: string | null;
  actorName: string | null;
  changedAt: string;
}
const STATUS_HIST_COLORS = ['#1a8a66', '#d97706', '#0e7490', '#7c3aed', '#c0392b', '#527060'];
const statusHistColor = (name: string): string => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return STATUS_HIST_COLORS[h % STATUS_HIST_COLORS.length]!;
};
/**
 * Contact context menu (V34 `.ctx-menu`) — Touches count · Actions (log a touch,
 * with an optional note) · Current Status · History (Action & Status History
 * modal). Rendered anchored under the drawer's + Touches button, or fixed at the
 * cursor on a right-click. Owns its own guarded click-outside + touch logging.
 */
export function ContactActionsMenu({
  contactId,
  contactName,
  positionStyle,
  onClose,
  onLogged,
}: {
  contactId: string;
  contactName: string;
  /** Accepted for call-site compatibility; the menu now reads live status itself. */
  statusLabel?: string;
  positionStyle?: CSSProperties;
  onClose: () => void;
  onLogged?: () => void;
}) {
  const [menu, setMenu] = useState<ContactStatusMenu | null>(null);
  const [touches, setTouches] = useState<Touch[]>([]);
  const [statusChanges, setStatusChanges] = useState<StatusChange[]>([]);
  const [histOpen, setHistOpen] = useState(false);
  const [noteAction, setNoteAction] = useState<{ id: string; label: string } | null>(null);
  const [noteText, setNoteText] = useState('');
  // Form-fill reminder surfaced after firing an action that carries one.
  const [reminder, setReminder] = useState<FireActionReminder | null>(null);
  const [busy, setBusy] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // Cursor-anchored (right-click) menus are placed at the raw click point, then
  // fitted to the viewport: the height is first capped to a compact size that
  // also fits the screen, then the top is pulled up if it would still spill. With
  // the capped height + `.ctx-menu { overflow-y:auto }`, a long Statuses list
  // scrolls inside the menu instead of running off the bottom.
  useLayoutEffect(() => {
    if (!positionStyle) return;
    const el = menuRef.current;
    if (!el) return;
    const M = 10; // viewport margin
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    // Compact cap that never exceeds the viewport, so the measured height fits.
    el.style.maxHeight = `${Math.min(460, vh - M * 2)}px`;
    const r = el.getBoundingClientRect();
    if (r.right > vw - M) {
      el.style.left = `${Math.max(M, vw - r.width - M)}px`;
    }
    if (r.top + r.height > vh - M) {
      el.style.top = `${Math.max(M, vh - r.height - M)}px`;
    }
  }, [positionStyle]);
  const load = () => {
    api
      .get<Touch[]>(`/contacts/${contactId}/touches`)
      .then(setTouches)
      .catch(() => setTouches([]));
    api
      .get<ContactStatusMenu>(`/contacts/${contactId}/status-menu`)
      .then(setMenu)
      .catch(() => setMenu(null));
  };
  useEffect(load, [contactId]);
  // Status-change history for the "Action & Status History" modal (loaded when opened).
  useEffect(() => {
    if (!histOpen) return;
    api
      .get<StatusChange[]>(`/contacts/${contactId}/status-history`)
      .then(setStatusChanges)
      .catch(() => setStatusChanges([]));
  }, [histOpen, contactId]);
  // Close on outside click — but not while a note/history modal is open (the
  // modal owns its own dismissal). Deferred a tick so the opening click is missed.
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (noteAction || histOpen || reminder) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', h);
    };
  }, [noteAction, histOpen, reminder, onClose]);
  const reloadHistory = () => {
    if (!histOpen) return;
    api
      .get<StatusChange[]>(`/contacts/${contactId}/status-history`)
      .then(setStatusChanges)
      .catch(() => undefined);
  };
  // Counter tally for a status: direct sets + action-fires (mockup _statusTouchCount).
  const statusCount = (id: string) =>
    (menu?.statusCounts[id] ?? 0) + (menu?.statusCounts[`${id}__fire`] ?? 0);
  // Touches total = sum of every counter-mode status's tally.
  const touchesTotal = (menu?.statuses ?? []).reduce(
    (sum, s) => (s.mode === 'counter' ? sum + statusCount(s.id) : sum),
    0,
  );
  // Fire a saved action → logs a touch, bumps the target counter, moves the ladder.
  // ehr#614: one save at a time per contact, across every open menu (runOnce), so a second click
  // (or a left + right click together) while the first is saving does nothing.
  const fire = (actionId: string, note?: string) => {
    if (busy) return;
    const run = runOnce(`touch:${contactId}`, () =>
      api.post<FireActionResult>(`/contacts/${contactId}/fire-action`, {
        actionId,
        note: note?.trim() || undefined,
      }),
    );
    if (!run) return;
    setBusy(true);
    run
      .then((res) => {
        load();
        onLogged?.();
        reloadHistory();
        if (res?.reminder) setReminder(res.reminder); // pop the form-fill reminder
      })
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };
  const noteIcon = (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
  const body = (
    <>
      {!noteAction && !histOpen && (
        <div
          className="ctx-menu"
          ref={menuRef}
          style={positionStyle}
          // ehr#614: a right-click inside the menu must not open the drawer's own right-click
          // menu on top of it (a second copy whose items could log the touch again).
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="ctx-menu-touches">
            <span className="ctx-menu-touches-label">Touches</span>
            <span className="ctx-menu-touches-count">{touchesTotal}</span>
          </div>
          <div className="ctx-menu-sep" />
          <div className="ctx-menu-header">All Actions</div>
          {menu && menu.actions.length === 0 && (
            <div className="ctx-menu-empty">No actions apply to this contact.</div>
          )}
          {(menu?.actions ?? []).map((a) => {
            const n = a.toContactStatusId ? statusCount(a.toContactStatusId) : 0;
            return (
              <button
                key={a.id}
                type="button"
                className="ctx-menu-item"
                disabled={busy}
                aria-busy={busy || undefined}
                // ehr#614: only a plain left click / tap / Enter logs it.
                onClick={(e) => {
                  if (isPrimaryClick(e)) fire(a.id);
                }}
                title="Log this action"
              >
                <span className="ctx-dot" style={{ background: a.color }} />
                <span className="ctx-label">{a.label}</span>
                {n > 0 && <span className="ctx-count">{n}</span>}
                <span
                  className="ctx-note-quick"
                  role="button"
                  aria-label={`Log ${a.label} with a note`}
                  title="Log with a note"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isPrimaryClick(e)) return;
                    setNoteText('');
                    setNoteAction({ id: a.id, label: a.label });
                  }}
                >
                  {noteIcon}
                </span>
              </button>
            );
          })}
          <div className="ctx-menu-sep" />
          <div className="ctx-menu-header">Current Status</div>
          {/* #50 — show only the contact's current status here (not the full ladder);
              status is changed via the header Status pill or by firing an action. */}
          {(() => {
            const cur = menu?.statuses.find((s) => s.id === menu.currentStatusId) ?? null;
            if (!cur) return <div className="ctx-menu-empty">No status set.</div>;
            const n = cur.mode === 'counter' ? statusCount(cur.id) : 0;
            return (
              <div className="ctx-menu-item ctx-status-active" aria-current="true">
                <span className="ctx-dot" style={{ background: cur.color || '#1a8a66' }} />
                <span className="ctx-label">{cur.label}</span>
                {n > 0 && <span className="ctx-count">{n}</span>}
                <span className="ctx-status-check" aria-hidden>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
              </div>
            );
          })()}
          <div className="ctx-menu-sep" />
          <button
            type="button"
            className="ctx-menu-item"
            onClick={(e) => {
              e.stopPropagation();
              setHistOpen(true);
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15 14" />
            </svg>
            <span className="ctx-label">History</span>
          </button>
        </div>
      )}

      {noteAction && (
        <Modal
          title={`Log: ${noteAction.label}`}
          onClose={() => setNoteAction(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setNoteAction(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={busy}
                onClick={(e) => {
                  if (!isPrimaryClick(e)) return; // ehr#614
                  const a = noteAction;
                  setNoteAction(null);
                  if (a) fire(a.id, noteText);
                }}
              >
                Log touch
              </button>
            </>
          }
        >
          <div className="field">
            <label>Note (optional)</label>
            <textarea
              rows={3}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note for this touch…"
              autoFocus
            />
          </div>
        </Modal>
      )}

      {reminder && (
        <Modal
          title="Form Reminder"
          onClose={() => setReminder(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setReminder(null)}>
                Dismiss
              </button>
              <a
                className="btn primary"
                href={reminder.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setReminder(null)}
              >
                Open Form
              </a>
            </>
          }
        >
          <div className="sa-remind-modal">
            <div className="sa-remind-modal-head">Complete before moving on</div>
            <div className="sa-remind-modal-form">{reminder.formName}</div>
            {reminder.note && <p className="sa-remind-modal-note">{reminder.note}</p>}
            <p className="sa-remind-modal-body">
              This form should be collected as part of this status change.
            </p>
          </div>
        </Modal>
      )}

      {histOpen &&
        createPortal(
          <div className="modal-backdrop" onClick={() => setHistOpen(false)}>
            <div
              className="modal-lg"
              role="dialog"
              aria-modal="true"
              aria-label="Action & Status History"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-head">
                <div>
                  <h2>Action &amp; Status History</h2>
                  <p>
                    {contactName} — {touches.length + statusChanges.length}{' '}
                    {touches.length + statusChanges.length === 1 ? 'entry' : 'entries'}
                  </p>
                </div>
                <button
                  type="button"
                  className="modal-close"
                  aria-label="Close"
                  onClick={() => setHistOpen(false)}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="modal-body status-hist-list">
                <div className="status-hist-section-head">Actions</div>
                {touches.length === 0 ? (
                  <div className="ctx-menu-empty">None recorded yet.</div>
                ) : (
                  touches.map((t) => (
                    <div key={t.id} className="status-hist-row">
                      <span
                        className="status-hist-dot"
                        style={{ background: statusHistColor(t.label ?? t.channel) }}
                      />
                      <div className="status-hist-body">
                        <div className="status-hist-head">
                          <span className="status-hist-label">{t.label ?? t.channel}</span>
                          <span className="status-hist-time">
                            {new Date(t.occurredAt).toLocaleString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              ...timeOpts(),
                            })}
                          </span>
                        </div>
                        {t.note && <div className="status-hist-transition">{t.note}</div>}
                      </div>
                    </div>
                  ))
                )}
                <div className="status-hist-section-head">
                  Status Changes
                  {statusChanges.length > 0 && (
                    <span style={{ fontWeight: 400, color: 'var(--text-muted-2)' }}>
                      {' '}
                      ({statusChanges.length})
                    </span>
                  )}
                </div>
                {statusChanges.length === 0 ? (
                  <div className="ctx-menu-empty">None recorded yet.</div>
                ) : (
                  statusChanges.map((sc) => (
                    <div key={sc.id} className="status-hist-row">
                      <span
                        className="status-hist-dot"
                        style={{ background: statusHistColor(sc.toStatus) }}
                      />
                      <div className="status-hist-body">
                        <div className="status-hist-head">
                          <span className="status-hist-label">{sc.label ?? sc.toStatus}</span>
                          <span className="status-hist-time">
                            {new Date(sc.changedAt).toLocaleString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              ...timeOpts(),
                            })}
                          </span>
                        </div>
                        <div className="status-hist-transition">
                          {sc.fromStatus ? `${sc.fromStatus} → ${sc.toStatus}` : sc.toStatus}
                          {sc.actorName && (
                            <span className="status-hist-by"> · {sc.actorName}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="modal-foot">
                <button type="button" className="btn" onClick={() => setHistOpen(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
  return positionStyle ? createPortal(body, document.body) : body;
}

/** Drawer header "+ Touches" button — opens the ContactActionsMenu anchored below. */
function TouchesControl({
  contactId,
  contactName,
  statusLabel,
}: {
  contactId: string;
  contactName: string;
  statusLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const loadCount = () => {
    api
      .get<Touch[]>(`/contacts/${contactId}/touches`)
      .then((r) => setCount(Array.isArray(r) ? r.length : 0))
      .catch(() => setCount(0));
  };
  useEffect(loadCount, [contactId]);
  return (
    <div className="touches-wrap">
      <button
        type="button"
        className={`touches-btn ${count ? 'has' : ''}`}
        onMouseDown={(e) => {
          e.stopPropagation();
          if (e.button !== 0) return; // ehr#614: the right button doesn't toggle it
          setOpen((o) => !o);
        }}
        aria-expanded={open}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="8" x2="12" y2="16" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
        Touches
        {count > 0 && <span className="touches-btn-count">{count}</span>}
      </button>
      {open && (
        <ContactActionsMenu
          contactId={contactId}
          contactName={contactName}
          statusLabel={statusLabel}
          onClose={() => setOpen(false)}
          onLogged={loadCount}
        />
      )}
    </div>
  );
}

/**
 * Drawer-head Status control — the current pool status as a pill (dot + label +
 * counter tally), matching the mockup. For managers it opens the STATUSES ladder;
 * picking a rung sets the contact's status (PATCH /contacts/:id/contact-status).
 */
function StatusControl({
  contactId,
  manager,
  refreshKey = 0,
}: {
  contactId: string;
  manager: boolean;
  /** ehr#389: bumps when the chart's stamp moves (a status set in another tab or by a teammate). */
  refreshKey?: number;
}) {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState<ContactStatusMenu | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const load = () => {
    api
      .get<ContactStatusMenu>(`/contacts/${contactId}/status-menu`)
      .then(setMenu)
      .catch(() => setMenu(null));
  };
  // ehr#389: reload when the chart's stamp moves too, or another tab's status change never shows.
  useEffect(load, [contactId, refreshKey]);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const t = setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', h);
    };
  }, [open]);
  const rungs = (menu?.statuses ?? []).slice().sort((a, b) => a.position - b.position);
  const current = rungs.find((s) => s.id === menu?.currentStatusId) ?? null;
  const count = menu?.currentStatusId ? (menu.statusCounts[menu.currentStatusId] ?? 0) : 0;
  // ehr#614: picking a counter rung counts a touch too, so it saves once per pick: a second click
  // while it's saving does nothing (and the options are disabled meanwhile).
  const [saving, setSaving] = useState(false);
  const setStatusRung = (statusId: string) => {
    const run = runOnce(`status:${contactId}`, () =>
      api.patch(`/contacts/${contactId}/contact-status`, { statusId }),
    );
    if (!run) return;
    setSaving(true);
    run
      .then(load)
      .catch(() => undefined)
      .finally(() => {
        setSaving(false);
        setOpen(false);
      });
  };
  return (
    <div className="drawer-status-control" ref={ref}>
      <span className="drawer-status-lead">Status</span>
      <button
        type="button"
        className={`status-pill-btn${current ? ' has' : ''}`}
        onMouseDown={(e) => {
          e.stopPropagation();
          if (manager) setOpen((o) => !o);
        }}
        disabled={!manager}
        aria-expanded={open}
        title={manager ? 'View or change status' : 'Status'}
      >
        <span className="status-pill-dot" style={{ background: current?.color ?? '#8aaa9a' }} />
        {current ? current.label : 'No status'}
        {count > 0 && <span className="status-pill-count">{count}</span>}
      </button>
      {open && (
        <div className="status-pill-menu" role="menu">
          {rungs.length === 0 ? (
            <div className="status-pill-empty">No statuses in this pool.</div>
          ) : (
            rungs.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`status-pill-opt${s.id === menu?.currentStatusId ? ' sel' : ''}`}
                disabled={saving}
                onClick={(e) => {
                  if (isPrimaryClick(e)) setStatusRung(s.id);
                }}
              >
                <span className="status-pill-dot" style={{ background: s.color }} />
                <span className="status-pill-opt-label">{s.label}</span>
                {s.id === menu?.currentStatusId && <span className="status-pill-check">✓</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function ContactDrawer({
  contact,
  onClose,
  initialView,
  initialRecord,
  onOpenContact,
}: {
  contact: DrawerContact;
  onClose: () => void;
  /** Open straight to this tab (e.g. deep-linked from the Dashboard). */
  initialView?: DrawerView;
  /** ehr#252: a record to open inside that tab (Forms: a filled form, e.g. one just shared;
   * Clinical Notes: a progress note, from an Alerts Hub card). */
  initialRecord?: string;
  /** ehr#208: switch the drawer to another contact (a linked chart in the header). */
  onOpenContact?: (id: string) => void;
}) {
  const isClient = contact.category === 'client';
  // ehr#342 - a teammate's change to this client (details, Demographics, tasks, notes, forms) shows
  // in the open chart: the chart's stamp moves and `liveTick` reloads what it shows.
  const [liveTick, setLiveTick] = useState(0);
  useLiveVersion(
    () => api.get<{ version: string }>(`/contacts/${contact.id}/version`),
    () => setLiveTick((n) => n + 1),
  );
  // ehr#208: charts linked to this one (both directions), shown beside the name.
  const [links, setLinks] = useState<ContactLinkView[]>([]);
  const loadLinks = useCallback(() => {
    if (!isClient) return;
    api
      .get<ContactLinkView[]>(`/contacts/${contact.id}/links`)
      .then((l) => setLinks(Array.isArray(l) ? l : []))
      .catch(() => setLinks([]));
  }, [contact.id, isClient]);
  useEffect(loadLinks, [loadLinks, liveTick]);
  const chartLinks = links.filter((l) => l.linkCharts);
  // ehr#636: the open chart's own role under its name + each linked chart's role.
  const related = relatedHeader(chartLinks);
  // Lock the page behind the drawer while it's open. The drawer is a full-height
  // fixed overlay with its own overflow-y:auto scrollbar; without this the main
  // page keeps its scrollbar too, so two vertical scrollbars show at once.
  useScrollLock(true);
  // An old link to the retired Treatment Plans tab lands on Clinical Notes, where plans list;
  // ehr#640: one to the retired Screeners tab lands on Forms, filtered to Screeners; ehr#743 (V15):
  // one to the retired Authorizations tab on Profile › Authorizations. ehr#230: clients open on
  // Summary.
  const [view, setView] = useState<DrawerView>(
    () => startView(initialView, isClient).view as DrawerView,
  );
  const [maximized, setMaximized] = useState(true);
  const [section, setSection] = useState(() => startView(initialView, isClient).section);
  const [data, setData] = useState<Demographics>({});
  // Image previews for the ID / insurance drop-zones (keyed `section.slotKey`).
  // Bytes persist via the encrypted files API; the returned fileId is stored in
  // the demographics record under the slot key (e.g. identity.dlImages0).
  const [uploads, setUploads] = useState<Record<string, string>>({});
  const [uploadBusy, setUploadBusy] = useState<Record<string, boolean>>({});
  // Per-slot upload error (too big / wrong type / upload failed) — #12.
  const [uploadErr, setUploadErr] = useState<Record<string, string>>({});
  const [lightbox, setLightbox] = useState<{ src: string; label: string } | null>(null);
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [lightbox]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // ehr#681: the "Record saved" / "Draft saved" (or "Not saved") card above the clicked button.
  const [saveNote, setSaveNote] = useState<SaveNotice | null>(null);
  // ehr#681 (Taras): which button shows "Saving…" (at least SAVE_MIN_MS), and the
  // "Record / Draft last saved X ago" line under the buttons, refreshed every LAST_SAVED_TICK_MS.
  const [savingAt, setSavingAt] = useState<'draft' | 'record' | null>(null);
  const [lastSaved, setLastSaved] = useState<LastSaved | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!lastSaved) return;
    setNowTick(Date.now());
    const t = window.setInterval(() => setNowTick(Date.now()), LAST_SAVED_TICK_MS);
    return () => window.clearInterval(t);
  }, [lastSaved]);
  const [dirty, setDirty] = useState(false);
  // ehr#375: the client's sliding scale (Demographics → Insurance / Payor). Its draft lives here so it
  // survives switching sections; it counts as unsaved and saves with Save draft / Save record.
  const ss = useSlidingScale(contact.id, isClient);
  const anyDirty = dirty || ss.dirty;
  // Extra entry ids per repeatable section (base entry lives under data[sec];
  // extra entry n lives under data[`${sec}#${n}`]).
  const [extras, setExtras] = useState<Record<string, number[]>>({});
  // ehr#191 — the practice's providers for the Care Team picker (and the primary clinician).
  const [directory, setDirectory] = useState<CareTeamProvider[]>([]);
  // ehr#176: RCM > Chargemaster > Payors (active), for the Insurance / Payor dropdown.
  // null = still loading; payorsErr = the list couldn't be read.
  const [rcmPayors, setRcmPayors] = useState<RcmPayorOption[] | null>(null);
  const [payorsErr, setPayorsErr] = useState(false);
  // ehr#743: Settings > Referral Sources, for the Referral source dropdown. null = not loaded.
  const [referralSources, setReferralSources] = useState<{ id: string; name: string }[] | null>(
    null,
  );
  // ehr#743 (Q3): the contact record's referral source (new-client form), from GET /contacts/:id.
  const [contactReferralId, setContactReferralId] = useState<string | null>(null);
  const contactReferralName = contactReferralId
    ? referralSources?.find((r) => r.id === contactReferralId)?.name
    : undefined;
  const contactReferralNameRef = useRef(contactReferralName);
  contactReferralNameRef.current = contactReferralName;
  // Which collapsible heads are open, keyed `${dataKey}.${headKey}`. Empty =
  // everything collapsed (V22 — Race, Address, Claims address start hidden).
  const [openHeads, setOpenHeads] = useState<Set<string>>(() => new Set());
  const toggleHead = (k: string) =>
    setOpenHeads((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const [counts, setCounts] = useState<{
    appointments?: number;
    tasks?: number;
    notes?: number;
    forms?: number;
  }>({});
  // Whether the contact's open follow-up is past due — drives the pulsing dot on
  // the Follow-up drawer tab (V34 .drawer-tab.has-overdue).
  const [followupOverdue, setFollowupOverdue] = useState(false);
  // Right-click anywhere on the drawer → the same Touches/Actions/Status menu as
  // the + Touches button, anchored at the cursor (V34 openDrawerContextMenu).
  const [drawerCtx, setDrawerCtx] = useState<{ x: number; y: number } | null>(null);
  // Preserve the native right-click where users genuinely need it (paste in
  // inputs, OS dropdown, open-link, media context) — mirrors V34's target guard.
  // ehr#614: and never inside a window opened over the drawer (filling a form), which keeps the
  // browser's own menu.
  const onDrawerContextMenu = (e: ReactMouseEvent) => {
    if (!drawerMenuAllowed(e.target as HTMLElement, e.currentTarget)) return;
    e.preventDefault();
    setDrawerCtx({ x: e.clientX, y: e.clientY });
  };

  // Tab count-badges (V27) — light counts fetched when the drawer opens, and again on a
  // teammate's change (ehr#342).
  useEffect(() => {
    let live = true;
    const put = (k: keyof typeof counts, n: number) => live && setCounts((c) => ({ ...c, [k]: n }));
    const len = (r: unknown) => (Array.isArray(r) ? r.length : 0);
    api
      .get<unknown[]>(`/contacts/${contact.id}/appointments`)
      .then((r) => put('appointments', len(r)))
      .catch(() => undefined);
    api
      .get<{ doneAt: string | null; status?: string }[]>(`/tasks?contact=${contact.id}`)
      // ehr#659: open tasks by the one rule (a cancelled task counted here as open)
      .then((r) => put('tasks', Array.isArray(r) ? r.filter((t) => isOpenTask(t)).length : 0))
      .catch(() => undefined);
    api
      .get<{ source?: string }[]>(`/contacts/${contact.id}/notes`)
      // ehr#31: imported notes count with Progress Notes, not Contact Notes.
      .then((r) =>
        put('notes', Array.isArray(r) ? r.filter((x) => x.source !== 'legacy').length : 0),
      )
      .catch(() => undefined);
    api
      .get<FollowupRow[]>(`/followups?contact=${contact.id}`)
      .then((r) => {
        if (!live) return;
        const open = Array.isArray(r) ? r.find((f) => !f.clearedAt) : undefined;
        setFollowupOverdue(!!open && followupStatus(localDayISO(open.dueAt)) === 'overdue');
      })
      .catch(() => undefined);
    if (isClient)
      api
        .get<unknown[]>(`/dispatch/history?patientId=${contact.id}`)
        .then((r) => put('forms', len(r)))
        .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [contact.id, isClient, liveTick]);

  // ehr#342: a live reload of the same client never overwrites edits not yet saved.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const demoLoadedFor = useRef<string | null>(null);
  // ehr#743 (V11): the Profile as last loaded or saved (what shows while nothing is unsaved), for
  // the footer's Cancel. An SSN revealed by its Edit button is not an unsaved change, so it can land
  // here; it is kept masked (ehr#712: masked once saved), so Cancel never brings the number back.
  const savedSnap = useRef<{ data: Demographics; extras: Record<string, number[]> } | null>(null);
  useEffect(() => {
    if (!dirty) savedSnap.current = { data: savedProfile(data), extras };
  }, [data, extras, dirty]);
  // ehr#661: Contact Info as stored, so a save can tell a new preferred-contact gap (blocked)
  // from one the chart already had (allowed, with the warning on the chart).
  const storedContact = useRef<Record<string, string> | undefined>(undefined);
  // ehr#661: the field marked red with the preferred method (mobilePhone, phone, workPhone, email).
  const preferMark = useRef('');
  useEffect(() => {
    if (!isClient) {
      setStatus('ready');
      return;
    }
    const refresh = demoLoadedFor.current === contact.id;
    if (refresh && dirtyRef.current) return;
    let live = true;
    api
      .get<Demographics | null>(`/contacts/${contact.id}/demographics`)
      .then((raw) => {
        if (!live || (refresh && dirtyRef.current)) return;
        demoLoadedFor.current = contact.id;
        storedContact.current = raw?.contact ? { ...raw.contact } : undefined;
        // ehr#681: the server's last save time (read only; never sent back).
        setLastSaved(lastSavedFromServer(raw));
        // ehr#743: a referral source saved under Employment moves to Referral.
        // ehr#743 (Q3): and a blank one takes the contact record's source, when already known.
        const d = fillReferralSource(
          migrateReferral(migrateContactNames(raw ?? {})),
          contactReferralNameRef.current,
        );
        delete (d as Record<string, unknown>)._savedAt;
        // ehr#712: which sections have an SSN on file (their values arrive masked); not saved.
        delete (d as Record<string, unknown>)._ssnOnFile;
        // #71 — seed the required name fields from the contact record so existing
        // clients (whose name lives on the contact and shows in the header) don't
        // open Demographics > Contact Info with a blank First/Last. Staff can edit.
        d.contact = { ...(d.contact ?? {}) };
        if (!(d.contact.firstName ?? '').trim() && contact.firstName)
          d.contact.firstName = contact.firstName;
        if (!(d.contact.lastName ?? '').trim() && contact.lastName)
          d.contact.lastName = contact.lastName;
        // #3c — default MR# to the contact's short id (what the dashboard/list shows)
        // so the drawer isn't blank; staff can still override it.
        if (!(d.identity?.mrn ?? '').trim()) {
          d.identity = { ...(d.identity ?? {}), mrn: contact.id.slice(0, 8).toUpperCase() };
        }
        setData(d);
        setStatus('ready');
        setDirty(false);
        // Rebuild extra-entry ids from any `${sec}#${n}` keys already saved.
        const ex: Record<string, number[]> = {};
        for (const k of Object.keys(d ?? {})) {
          const m = /^(.+)#(\d+)$/.exec(k);
          if (m && REPEATABLE[m[1]!]) (ex[m[1]!] = ex[m[1]!] ?? []).push(Number(m[2]));
        }
        for (const s of Object.keys(ex)) ex[s]!.sort((a, b) => a - b);
        setExtras(ex);
        // Prefetch previews for any stored ID / insurance images (once per client, not on every
        // live reload).
        if (refresh) return;
        for (const sec of SECTIONS) {
          for (const f of sec.fields) {
            if (f.type !== 'file') continue;
            (f.pair ?? ['', '']).forEach((_, idx) => {
              const fileId = d?.[sec.key]?.[`${f.key}${idx}`];
              if (!fileId) return;
              api
                .get<{ mime: string; dataBase64: string }>(`/files/${fileId}/content`)
                .then((c) => {
                  if (live)
                    setUploads((p) => ({
                      ...p,
                      [`${sec.key}.${f.key}${idx}`]: `data:${c.mime};base64,${c.dataBase64}`,
                    }));
                })
                .catch(() => undefined);
            });
          }
        }
      })
      .catch((e: unknown) => {
        if (!live || refresh) return; // a missed live reload keeps what's shown
        setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error');
      });
    return () => {
      live = false;
    };
  }, [contact.id, isClient, liveTick]);

  useEffect(() => {
    if (!isClient) return;
    let live = true;
    api
      .get<RcmPayorOption[]>('/payors')
      .then((rows) => live && setRcmPayors(rows))
      .catch(() => {
        if (!live) return;
        setRcmPayors([]);
        setPayorsErr(true);
      });
    return () => {
      live = false;
    };
  }, [isClient]);
  // ehr#743: the practice's referral sources. If the list can't be read, V11's defaults stand in.
  useEffect(() => {
    if (!isClient) return;
    let live = true;
    api
      .get<{ id: string; name: string }[]>('/lookups/referral-sources')
      .then((rows) => live && setReferralSources(Array.isArray(rows) ? rows : []))
      .catch(
        () =>
          live && setReferralSources(DEFAULT_REFERRAL_SOURCES.map((name) => ({ id: '', name }))),
      );
    return () => {
      live = false;
    };
  }, [isClient]);
  // ehr#743 (Q3): a client added with a referral source (saved on the contact) shows it on a blank
  // Referral source once the chart has loaded, so the required check does not stop them. Not a
  // change by the user (the record stays clean); it is saved with the next save. Never replaces a
  // value already on the chart.
  useEffect(() => {
    if (status !== 'ready' || !contactReferralName) return;
    setData((d) => fillReferralSource(d, contactReferralName));
  }, [status, contactReferralName, contact.id]);
  // calendar#37 (Chase 10-12): a payor added, renamed or turned off in Revenue Cycle or Data &
  // settings shows in Insurance / Payor without a refresh. A missed re-read keeps the list shown.
  useLiveRefresh(() => {
    if (!isClient) return;
    api
      .get<RcmPayorOption[]>('/payors')
      .then((rows) => {
        setRcmPayors(rows);
        setPayorsErr(false);
      })
      .catch(() => {});
  }, 15_000);

  useEffect(() => {
    if (!isClient) return;
    let live = true;
    api
      .get<CareTeamProvider[]>('/providers/directory')
      .then((rows) => live && setDirectory(rows))
      .catch(() => live && setDirectory([]));
    return () => {
      live = false;
    };
  }, [isClient]);

  // Drawer-head metadata row (mockup .drawer-stage-row): Stage · Owner · Status · Added.
  const [meta, setMeta] = useState<{
    stage?: string;
    owner?: string;
    status?: string;
    added?: string;
  }>({});
  // ── Drawer-head parity (V34): owner chip, ⋮ kebab menu, editable name, category change.
  // ehr#730: Share, Reassign and Who has access are the Share & Access V11 windows; who may use
  // them is the server's (the caller's own access, below).
  const { user } = useSession();
  const manager = canManage(user?.role);
  const [ownerUserId, setOwnerUserId] = useState<string | null>(null);
  // ehr#623 (reopened 10-24): which chart the header's owner has been read for. Until then the
  // owner chip is blank, not "Unassigned".
  const [metaFor, setMetaFor] = useState<string | null>(null);
  const [category, setCategory] = useState(contact.category);
  const [nameDraft, setNameDraft] = useState(`${contact.firstName} ${contact.lastName}`.trim());
  // ehr#403 - the name as saved now (a Demographics save here or a teammate's change); null until read.
  const [liveName, setLiveName] = useState<{ firstName: string; lastName: string } | null>(null);
  const nameFocused = useRef(false);
  // ehr#403 (follow-up to #425): the panels (Forms, Notes, Clinical Notes, Files, Messages…) name
  // the client by the live name too, not the one the drawer opened with.
  const shownName = `${(liveName ?? contact).firstName} ${(liveName ?? contact).lastName}`.trim();
  // ehr#403: Demographics → Contact Info follows a rename made in the header (or by a teammate)
  // while it still holds the old name and nothing there is being edited. Saving it keeps the two in step.
  const prevName = useRef({ firstName: contact.firstName, lastName: contact.lastName });
  useEffect(() => {
    if (!liveName) return;
    const prev = prevName.current;
    prevName.current = liveName;
    if (dirtyRef.current) return;
    setData((d) => {
      const c = d.contact ?? {};
      if ((c.firstName ?? '') !== prev.firstName || (c.lastName ?? '') !== prev.lastName) return d;
      return {
        ...d,
        contact: { ...c, firstName: liveName.firstName, lastName: liveName.lastName },
      };
    });
  }, [liveName]);
  // #104 - static Client Balance in the header meta row (Σcharges − Σpayments),
  // from the existing billing ledger. Clients only; hidden if PHI-gated/absent.
  const [balanceCents, setBalanceCents] = useState<number | null>(null);
  // rcm#91 - the client's Unapplied in RCM (client payment money not applied to a claim or held
  // for an appointment), after Client Balance. Hidden when 0, when RCM is absent, or on error.
  const [unappliedCents, setUnappliedCents] = useState<number | null>(null);
  // ehr#565 - "Auth Remaining" after Unapplied: what's left on the client's current authorizations
  // (counted from checked-out sessions). Hidden when no authorization has a count, or PHI-gated.
  const [authRemaining, setAuthRemaining] = useState<number | null>(null);
  const [authTick, setAuthTick] = useState(0);
  const [kebabOpen, setKebabOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ portalUrl: string; email: string } | null>(
    null,
  );
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const headActionsRef = useRef<HTMLDivElement>(null);
  // ehr#730 (V11): Share / Reassign / Who has access for this chart; a save reloads the header
  // (owner) and the caller's own access.
  const access = useContactAccessDialogs({ onChanged: () => setLiveTick((n) => n + 1) });
  const accessTarget = () => ({
    id: contact.id,
    name: shownName,
    category,
    ownerUserId,
  });
  // ehr#730: the caller's own access on this chart. Hidden tabs leave the strip, a View tab says
  // so, and the ⋮ menu offers what they may do (until it loads, the menu follows the old rule).
  const [myAccess, setMyAccess] = useState<MyContactAccess | null>(null);
  useEffect(() => {
    let live = true;
    fetchMyAccess(contact.id)
      .then((a) => live && setMyAccess(a))
      .catch(() => live && setMyAccess(null));
    return () => {
      live = false;
    };
  }, [contact.id, liveTick]);
  const canShare = myAccess ? myAccess.canShare : manager;
  const canReassign = myAccess ? myAccess.canReassign : manager;
  const canArchive = myAccess ? myAccess.canArchive : manager;
  const tabLevel = (k: string) =>
    myAccess && !myAccess.unrestricted ? (myAccess.tabs[k] ?? 'edit') : 'edit';
  // ehr#730 (V11: View tabs lose Add and Edit): each panel hides its write buttons on a View tab.
  const readOnlyTab = (k: string) => !tabWritable(k, tabLevel(k));
  // One tab list (@ehr/shared CONTACT_TABS) for this strip and the Share window. The strip goes by
  // the kind of chart that opened (the panels below do too); staff charts add Licensure.
  const stripTabs = drawerTabs(
    isClient ? 'client' : category === 'staff' ? 'staff' : 'referral_source',
    // ehr#743 (V15): Authorizations is a Profile section now, not a top tab
  ).filter((t) => t.key !== 'authorizations' && tabLevel(t.key) !== 'hidden');
  const viewHidden = tabLevel(view) === 'hidden';
  const firstTab = stripTabs[0]?.key;
  useEffect(() => {
    if (viewHidden && firstTab) setView(firstTab as DrawerView);
  }, [viewHidden, firstTab]);
  // #104 - client balance for the header (derived ledger). Refetch when a
  // charge/payment could have changed it (billing tab is one surface).
  useEffect(() => {
    if (contact.category !== 'client') {
      setBalanceCents(null);
      return;
    }
    api
      .get<{ balanceCents: number }>(`/contacts/${contact.id}/billing`)
      .then((r) => setBalanceCents(typeof r?.balanceCents === 'number' ? r.balanceCents : null))
      .catch(() => setBalanceCents(null));
  }, [contact.id, contact.category]);
  useEffect(() => {
    if (contact.category !== 'client') {
      setUnappliedCents(null);
      return;
    }
    api
      .get<{ unappliedCents: number | null }>(`/contacts/${contact.id}/rcm-unapplied`)
      .then((r) =>
        setUnappliedCents(typeof r?.unappliedCents === 'number' ? r.unappliedCents : null),
      )
      .catch(() => setUnappliedCents(null));
  }, [contact.id, contact.category]);
  useEffect(() => {
    if (contact.category !== 'client') {
      setAuthRemaining(null);
      return;
    }
    api
      .get<{ remaining: number | null }>(`/contacts/${contact.id}/authorizations/summary`)
      .then((r) => setAuthRemaining(typeof r?.remaining === 'number' ? r.remaining : null))
      .catch(() => setAuthRemaining(null));
  }, [contact.id, contact.category, authTick]);
  // Close the kebab on outside click.
  useEffect(() => {
    if (!kebabOpen) return;
    const h = (e: MouseEvent) => {
      if (headActionsRef.current && !headActionsRef.current.contains(e.target as Node)) {
        setKebabOpen(false);
      }
    };
    const t = setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', h);
    };
  }, [kebabOpen]);
  const saveName = () => {
    const parts = nameDraft.trim().split(/\s+/);
    const firstName = parts[0] ?? '';
    const lastName = parts.slice(1).join(' ');
    if (!firstName || !lastName) return; // require both; ignore partial edits
    const saved = liveName ?? contact;
    if (firstName === saved.firstName && lastName === saved.lastName) return;
    // ehr#405: a failed rename says so (it used to fail silently)
    api.patch(`/contacts/${contact.id}`, { firstName, lastName }).catch((e) => {
      // ehr#405 (follow-up to #425): put the saved name back, so an unsaved name doesn't look saved.
      setNameDraft(`${saved.firstName} ${saved.lastName}`.trim());
      showError(`Could not rename this contact. ${errorText(e)}`);
    });
  };
  const changeCategory = (cat: string) => {
    if (cat === category) return;
    setCategory(cat);
    api.patch(`/contacts/${contact.id}`, { category: cat }).catch((e) => {
      setCategory(contact.category); // ehr#405: put it back and say why
      showError(`Could not change the category. ${errorText(e)}`);
    });
    setKebabOpen(false);
  };
  const archiveContact = () => {
    api
      .post(`/contacts/${contact.id}/archive`)
      .then(() => {
        setKebabOpen(false);
        onClose();
      })
      .catch((e: unknown) => showError(`Could not archive this contact. ${errorText(e)}`));
  };
  // #59 - restore a soft-deleted contact (inverse of Archive).
  const unarchiveContact = () => {
    api
      .post(`/contacts/${contact.id}/unarchive`)
      .then(() => {
        setKebabOpen(false);
        onClose();
      })
      .catch((e: unknown) => showError(`Could not restore this contact. ${errorText(e)}`));
  };
  // Email this client the portal link. Access is auto-provisioned on OAuth sign-in (portal side),
  // so there is no token — the message just points them at the portal, which kicks them to
  // login.credifyfast.com to sign in with Google. Server ensures the care-provider tie and returns
  // the client's email + portal URL; we open the staff's mail client prefilled (server-sent email
  // lands later). The modal is a fallback + confirmation.
  const inviteToPortal = async () => {
    if (inviting) return;
    setKebabOpen(false);
    setInviteError(null);
    setInviting(true);
    try {
      const r = await api.post<{ portalUrl: string; email: string }>(
        `/contacts/${contact.id}/portal-invite`,
        {},
      );
      setInviteResult(r);
      const name = `${contact.firstName} ${contact.lastName}`.trim();
      const subject = 'Your Credify client portal';
      const body = [
        `Hi ${name || 'there'},`,
        '',
        'You now have access to your secure client portal. Open the link below and sign in with',
        'Google using this email address:',
        '',
        r.portalUrl,
        '',
        'See you there,',
        'Your care team',
      ].join('\n');
      window.location.href = `mailto:${encodeURIComponent(r.email)}?subject=${encodeURIComponent(
        subject,
      )}&body=${encodeURIComponent(body)}`;
    } catch (e) {
      setInviteError(e instanceof ApiError ? e.message : 'Could not prepare the portal email.');
    } finally {
      setInviting(false);
    }
  };
  // ehr#623 (reopened): blank until this chart's owner is read; then the name, or Unassigned.
  const chip = ownerChip(metaFor === contact.id, meta.owner);
  const kebabIcon = (inner: string) => (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      dangerouslySetInnerHTML={{ __html: inner }}
    />
  );
  // ehr#743 (Q3): another chart's referral source never fills this one.
  useEffect(() => setContactReferralId(null), [contact.id]);
  useEffect(() => {
    let live = true;
    Promise.all([
      api
        .get<{
          stageId: string | null;
          statusId: string | null;
          ownerUserId: string | null;
          /** ehr#623 (reopened): the owner's name, sent with the chart. */
          ownerName?: string | null;
          createdAt: string;
          firstName?: string | null;
          lastName?: string | null;
          /** ehr#743 (Q3): fills a blank Referral source on the chart. */
          referralSourceId?: string | null;
        }>(`/contacts/${contact.id}`)
        .catch(() => null),
      api.get<{ id: string; name: string }[]>('/settings/stages').catch(() => []),
      api.get<{ id: string; name: string }[]>('/settings/statuses').catch(() => []),
    ]).then(async ([c, stages, statuses]) => {
      if (!live || !c) return;
      // ehr#623 (reopened 10-24): the owner's name comes with the chart. It used to wait for the
      // people list (two requests for a clinician), and the chip read "Unassigned" meanwhile. An
      // older API without it still looks the name up.
      const owner =
        c.ownerName !== undefined
          ? (c.ownerName ?? undefined)
          : personLabel(await fetchNoteUsers(), c.ownerUserId);
      if (!live) return;
      // ehr#403: the header follows a name change (Demographics, another tab), not just the
      // name the drawer opened with; a name being typed in the header is left alone.
      if (typeof c.firstName === 'string') {
        const n = { firstName: c.firstName, lastName: c.lastName ?? '' };
        setLiveName(n);
        if (!nameFocused.current) setNameDraft(`${n.firstName} ${n.lastName}`.trim());
      }
      setOwnerUserId(c.ownerUserId);
      setContactReferralId(c.referralSourceId ?? null);
      setMeta({
        stage: stages.find((s) => s.id === c.stageId)?.name,
        owner,
        status: statuses.find((s) => s.id === c.statusId)?.name,
        added: c.createdAt
          ? (() => {
              const d = new Date(c.createdAt);
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const dd = String(d.getDate()).padStart(2, '0');
              return `${mm}-${dd}-${d.getFullYear()}`;
            })()
          : undefined,
      });
      setMetaFor(contact.id);
    });
    return () => {
      live = false;
    };
    // ehr#403: and again whenever the chart's stamp moves (liveTick), so owner, stage and name follow
  }, [contact.id, liveTick]);

  // Tier-1 validation state: `${sec}.${field}` keys currently blocking Save
  // record, plus the footer message. Filling a flagged field clears its
  // highlight immediately (mockup: highlights clear as fields are filled).
  const [invalid, setInvalid] = useState<Set<string>>(new Set());
  const [valErr, setValErr] = useState('');
  // ehr#482 / #500: the contact that already has the phone or email this save tried to use.
  const [dupHit, setDupHit] = useState<DuplicateContact | null>(null);
  // ehr#482 (Chase, 10-18): that refusal as a red pop-up, not only a line in the save bar.
  const [dupOpen, setDupOpen] = useState(false);

  const set = (sec: string, field: string, value: string) => {
    setSaved(false);
    setDirty(true);
    setData((d) => ({ ...d, [sec]: { ...(d[sec] ?? {}), [field]: value } }));
    // ehr#661: once the preferred method has something to reach, both its marks come off.
    if (
      sec === 'contact' &&
      invalid.has('contact.preferredContact') &&
      !preferredContactBlock(storedContact.current, { ...(data.contact ?? {}), [field]: value })
    ) {
      setInvalid((prev) => {
        const n = new Set(prev);
        for (const k of [...n])
          if (k === 'contact.preferredContact' || k === `contact.${preferMark.current}`)
            n.delete(k);
        if (n.size === 0) setValErr('');
        return n;
      });
    }
    // ehr#733: unticking a Home / Work phone's last box lifts its requirement, so its mark goes.
    if (sec === 'contact') {
      const lifted = ['phone', 'workPhone'].filter(
        (k) =>
          invalid.has(`contact.${k}`) &&
          k !== preferMark.current &&
          !contactSaveMissing({ ...(data.contact ?? {}), [field]: value }, false).some(
            (m) => m.field === k,
          ),
      );
      if (lifted.length)
        setInvalid((prev) => {
          const n = new Set(prev);
          for (const k of lifted) n.delete(`contact.${k}`);
          if (n.size === 0) setValErr('');
          return n;
        });
    }
    if (value.trim()) dropMark(`${sec}.${field}`);
  };

  /** Takes one red mark off (and the save bar's message with the last one). */
  const dropMark = (key: string) => {
    if (invalid.has(key)) {
      setInvalid((prev) => {
        const n = new Set(prev);
        n.delete(key);
        if (n.size === 0) setValErr('');
        return n;
      });
    }
  };

  // ehr#233: Contact Info's mobile "OK to call" / "OK to text" also record the dated Voice AI
  // call / Text message answer (the one the Opt-Out Center and Reminders read).
  const setChannelAnswer = (box: string, okay: boolean) => {
    const ch = CONTACT_CHANNEL_KEYS[box];
    if (ch)
      channelAnswer(ch, okay, new Date().toISOString()).forEach(([k, v]) => set('reminders', k, v));
    set('contact', box, okay ? 'true' : 'false');
  };

  // Add / remove a repeatable entry (Family & Contacts, Emergency, Care Team,
  // Insurance, Pharmacy). Extra entries persist under `${sec}#${id}` keys.
  const addEntry = (sec: string) => {
    setExtras((e) => {
      const cur = e[sec] ?? [];
      const nextId = (cur.length ? Math.max(...cur) : 0) + 1;
      return { ...e, [sec]: [...cur, nextId] };
    });
    setDirty(true);
  };
  // ehr#191 — fill a Care Team entry from a practice provider. Picking "type in manually"
  // only unlinks it and keeps what is in the fields. The first provider picked on a chart
  // with no primary clinician becomes the primary one.
  const pickProvider = (dataKey: string, id: string) => {
    setSaved(false);
    setDirty(true);
    const p = directory.find((x) => x.id === id);
    setData((d) => {
      const cur = d[dataKey] ?? {};
      if (!p) return { ...d, [dataKey]: { ...cur, providerId: '' } };
      const hasPrimary = Object.keys(d).some(
        (k) => (k === 'careteam' || k.startsWith('careteam#')) && d[k]?.primary === 'true',
      );
      const next: Record<string, string> = {
        ...cur,
        providerId: p.id,
        firstName: p.firstName,
        lastName: p.lastName,
        organization: p.organization,
        npi: p.npi,
        mobilePhone: p.mobilePhone,
        workPhone: p.workPhone,
        fax: p.fax,
        email: p.email,
        address1: p.address1,
        address2: p.address2,
        city: p.city,
        state: p.state,
        zip: p.zip,
      };
      if (p.role) {
        next.role = p.role;
        next.roleOther = '';
      }
      if (!hasPrimary) next.primary = 'true';
      return { ...d, [dataKey]: next };
    });
    setInvalid((prev) => {
      const n = new Set([...prev].filter((k) => !k.startsWith(`${dataKey}.`)));
      if (n.size === 0) setValErr('');
      return n;
    });
  };
  const removeEntry = (sec: string, id: number) => {
    setExtras((e) => ({ ...e, [sec]: (e[sec] ?? []).filter((x) => x !== id) }));
    setData((d) => {
      const n = { ...d };
      delete n[`${sec}#${id}`];
      return n;
    });
    setDirty(true);
  };

  // Pick an ID/insurance image: preview immediately, upload the bytes to the
  // encrypted files API, then store the returned fileId in the demographics
  // record (persisted on Save). slotKey is e.g. "dlImages0" / "dlImages1".
  const pickImage = (sec: string, slotKey: string, file: File) => {
    const uk = `${sec}.${slotKey}`;
    // Reject oversized / wrong-type files up front with a clear message (#12).
    const bad = uploadError(file, true);
    if (bad) {
      setUploadErr((p) => ({ ...p, [uk]: bad }));
      return;
    }
    setUploadErr((p) => ({ ...p, [uk]: '' }));
    setUploadBusy((p) => ({ ...p, [uk]: true }));
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = String(reader.result);
      const base64 = dataUrl.split(',')[1] ?? '';
      try {
        const res = await api.post<{ id: string }>(`/contacts/${contact.id}/files`, {
          name: file.name,
          mime: file.type || 'image/jpeg',
          dataBase64: base64,
        });
        if (res?.id) {
          // Only show the preview once it actually persisted, so a saved image
          // and a shown image never disagree (#12).
          setUploads((p) => ({ ...p, [uk]: dataUrl }));
          set(sec, slotKey, res.id);
        } else {
          throw new Error('no id');
        }
      } catch {
        // Surface the failure instead of leaving a preview that never saved.
        setUploads((p) => {
          const n = { ...p };
          delete n[uk];
          return n;
        });
        setUploadErr((p) => ({
          ...p,
          [uk]: 'Upload failed — the image was not saved. Please try again.',
        }));
      } finally {
        setUploadBusy((p) => ({ ...p, [uk]: false }));
      }
    };
    reader.readAsDataURL(file);
  };
  const removeImage = (sec: string, slotKey: string) => {
    const uk = `${sec}.${slotKey}`;
    const fileId = data[sec]?.[slotKey];
    setUploads((p) => {
      const n = { ...p };
      delete n[uk];
      return n;
    });
    setUploadErr((p) => ({ ...p, [uk]: '' }));
    set(sec, slotKey, '');
    // ehr#736: the card image is archived (it stays in Files, under Show archived), never deleted
    if (fileId) archiveItem({ kind: 'file', id: fileId }).catch(() => undefined);
  };

  // ehr#179 - a save that is blocked (required fields empty, or a value in the wrong
  // format) says so in a pop-up: what is missing, with a link to each field, and an offer
  // to save as a draft instead (only when nothing is malformed: a draft refuses those too).
  // thenClose: the save came from the "unsaved changes" prompt, so a draft save closes.
  type Blocked = { sec: string; field: string; label: string };
  const [saveBlock, setSaveBlock] = useState<{
    missing: Blocked[];
    bad: Blocked[];
    /** ehr#661: the preferred contact method this save would leave with nothing to reach. */
    prefer?: PreferredContactGap | null;
    thenClose: boolean;
  } | null>(null);
  const goToField = (sec: string, field: string) => {
    setSaveBlock(null);
    setView('demographics'); // ehr#743: the save may have started on another tab
    setSection(sec);
    // Open the collapsible group that holds the field, if any.
    const s = SECTIONS.find((x) => x.key === sec);
    let head: string | null = null;
    for (const f of s?.fields ?? []) {
      if (f.type === 'subhead' || f.type === 'linkhead') head = f.collapsible ? f.key : null;
      else if (f.endsGroup) head = null;
      if (f.key === field) break;
    }
    if (head) setOpenHeads((prev) => new Set(prev).add(`${sec}.${head}`));
    window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>(`.demo-grid [data-field="${field}"]`);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      // ehr#758: a write-in box carries its own data-field, so it is the one to focus.
      const box = el.matches('input')
        ? el
        : el.querySelector<HTMLElement>('input, textarea, button');
      box?.focus({ preventScroll: true });
    }, 60);
  };

  const save = (draft = false, thenClose = false): Promise<boolean> => {
    // ehr#375: a bad sliding scale entry stops either save, red at its field on Insurance / Payor.
    const ssBad = ss.dirty ? ss.validate() : null;
    setSaveNote(null);
    if (ssBad) {
      setValErr(`Sliding scale: ${ssBad}`);
      setSaveNote(failedNotice(draft, `Sliding scale: ${ssBad}`));
      setSection('insurance');
      focusSlidingError();
      return Promise.resolve(false);
    }
    // ehr#681 (Taras): the clicked button reads "Saving…" (both disabled) for at least
    // SAVE_MIN_MS; the form's own state updates as soon as the server answers, only the button,
    // the card and the "last saved" line wait for the minimum.
    let note: SaveNotice | null = null;
    const run = (p: Promise<boolean>): Promise<boolean> => {
      setSaving(true);
      setSavingAt(draft ? 'draft' : 'record');
      return atLeast(
        p.catch(() => false),
        SAVE_MIN_MS,
      ).then((ok) => {
        setSaving(false);
        setSavingAt(null);
        if (note) setSaveNote(note);
        setLastSaved((prev) => afterSave(prev, ok, draft, Date.now()));
        return ok;
      });
    };
    const saveSliding = (): Promise<boolean> =>
      ss.save().then((err) => {
        if (err) {
          setValErr(`Could not save the sliding scale: ${err}`);
          note = failedNotice(draft, `Could not save the sliding scale: ${err}`);
        } else {
          setSaved(true);
          note = savedNotice(draft);
        }
        return !err;
      });
    // Only the sliding scale changed: save it on its own (the record's required fields don't block it).
    if (!dirty && ss.dirty) {
      setValErr('');
      return run(saveSliding());
    }
    // Tier 1 (mockup): Save record blocks on missing mandatory fields; Save
    // draft skips the mandatory check. But a malformed value (a half-typed SSN,
    // a bad email/phone) is never valid, so those FORMAT errors are flagged on
    // BOTH Save draft and Save record (#3). Flag the fields red, jump to the
    // first offending section, and say what's wrong. ehr#743 (V15): the same rules
    // (lib/profileTabs.ts) put the amber dot on the section tabs as you type.
    // ehr#661: picking a preferred method with nothing to reach, or clearing the one kept, is
    // refused on either save; a chart that already had the gap and keeps it saves (warned).
    const { missing, bad, prefer } = profileProblems(SECTIONS, data, {
      draft,
      stored: storedContact.current,
    });
    preferMark.current = prefer?.field ?? '';
    if (missing.length > 0 || bad.length > 0 || prefer) {
      setInvalid(
        new Set([
          ...missing.map((t) => `${t.sec}.${t.field}`),
          ...bad.map((b) => `${b.sec}.${b.field}`),
          ...(prefer ? ['contact.preferredContact', `contact.${prefer.field}`] : []),
        ]),
      );
      const parts: string[] = [];
      if (missing.length) parts.push(`Required: ${missing.map((t) => t.label).join(', ')}`);
      if (bad.length) parts.push(`Check: ${bad.map((b) => b.label).join(', ')}`);
      if (prefer) parts.push(prefer.fillIn);
      setValErr(parts.join(' · '));
      setSection(missing[0]?.sec ?? bad[0]?.sec ?? 'contact');
      setSaveBlock({ missing, bad, prefer, thenClose });
      return Promise.resolve(false);
    }
    setInvalid(new Set());
    setValErr('');
    setSaved(false);
    setValErr('');
    const payload = { ...data, _status: draft ? 'draft' : 'record' };
    setDupHit(null);
    setDupOpen(false);
    return run(
      api
        .put(`/contacts/${contact.id}/demographics`, payload)
        .then(() => {
          storedContact.current = data.contact ? { ...data.contact } : undefined; // ehr#661
          // ehr#712: once saved, a typed SSN shows masked again (last 4), as on the next load.
          setData((d) => maskBlobSsn(d).data);
          setSaved(true);
          setDirty(false);
          setValErr('');
          loadLinks(); // ehr#208: the save rebuilt this chart's linked charts
          if (ss.dirty) return saveSliding(); // its card says how both went
          note = savedNotice(draft);
          return true;
        })
        // A save failure must NOT wipe the form (was flipping to the "Could not
        // load demographics" screen and losing every entered field — #3). Keep the
        // form on screen and surface the real error in the save bar instead.
        .catch((e: unknown) => {
          // ehr#661: the API refused a preferred contact method with nothing to reach.
          const refused = preferredContactRefusal(e);
          if (refused) {
            preferMark.current = (refused[1] ?? '').replace('contact.', '');
            setInvalid(new Set(refused));
            setSection('contact');
            setValErr((e as ApiError).message);
            note = failedNotice(draft, (e as ApiError).message);
            return false;
          }
          // ehr#733 / #734: the API refused a Save record with a required phone blank.
          const needed = requiredContactRefusal(e);
          if (needed) {
            setInvalid(new Set(needed));
            setSection('contact');
            setValErr((e as ApiError).message);
            note = failedNotice(draft, (e as ApiError).message);
            return false;
          }
          const d = duplicateOf(e);
          if (d) {
            // Contact Info's field in red; ehr#482: a red pop-up names the contact and opens it
            // (the save bar says it too). The phone in red is the one taken (mobile or home).
            // ehr#711: an SSN another chart has is Identity's SSN in red; the chart is named and
            // offered only to someone who may open it.
            setDupHit(d);
            setDupOpen(true);
            setSection(d.field === 'ssn' ? 'identity' : 'contact');
            const sec = (data.contact ?? {}) as Record<string, unknown>;
            const phones = ['mobilePhone', 'phone'].filter((k) =>
              isDupPhone(d, typeof sec[k] === 'string' ? (sec[k] as string) : ''),
            );
            setInvalid(
              new Set(
                d.field === 'ssn'
                  ? ['identity.ssn']
                  : d.field === 'email'
                    ? ['contact.email']
                    : (phones.length ? phones : ['mobilePhone']).map((k) => `contact.${k}`),
              ),
            );
            setValErr(
              openableDup(d)
                ? `${(e as ApiError).message} Each chart needs its own phone number, email and SSN.`
                : NO_ACCESS_MESSAGE,
            );
            return false;
          }
          const why =
            e instanceof ApiError
              ? `Could not save demographics: ${e.message}`
              : 'Could not save demographics. Please try again.';
          setValErr(why);
          note = failedNotice(draft, why);
          return false;
        }),
    );
  };

  // ehr#180 - leaving the record with unsaved demographics asks first: Save / Don't Save /
  // Cancel. Covers the close button and a click outside the drawer; a page refresh or
  // closing the browser tab gets the browser's own warning.
  const [closeAsk, setCloseAsk] = useState(false);
  // Where leaving goes once answered: closing, or (ehr#208) a linked chart.
  const leaveTo = useRef<() => void>(onClose);
  const requestLeave = (then: () => void) => {
    leaveTo.current = then;
    if (anyDirty && !saving) setCloseAsk(true);
    else then();
  };
  const requestClose = () => requestLeave(onClose);

  // ehr#743 (V11): the footer's Cancel puts back the Profile as last loaded or saved (and the
  // sliding scale), clears the red flags, and says "Changes discarded" above the button.
  const cancelEdits = () => {
    if (!anyDirty || saving) return;
    const snap = savedSnap.current;
    if (dirty && snap) {
      const imageFields = SECTIONS.flatMap((x) =>
        x.fields.filter((f) => f.type === 'file').map((f) => f.key),
      );
      const { keep, refetch } = previewsAfterCancel(uploads, data, snap.data, imageFields);
      setData(snap.data);
      setExtras(snap.extras);
      setUploads(keep);
      setUploadErr({});
      for (const r of refetch)
        api
          .get<{ mime: string; dataBase64: string }>(`/files/${r.fileId}/content`)
          .then((c) =>
            setUploads((u) => ({ ...u, [r.key]: `data:${c.mime};base64,${c.dataBase64}` })),
          )
          .catch(() => undefined);
    }
    if (ss.dirty) ss.revert();
    setDirty(false);
    setInvalid(new Set());
    setValErr('');
    preferMark.current = '';
    setSaveBlock(null);
    setDupHit(null);
    setDupOpen(false);
    setSaveNote(okNotice('Changes discarded', { at: 'cancel' }));
  };
  // ehr#743 (V11): the save bar stays on other tabs while the Profile has unsaved changes. A save
  // from there that stops on a field (required, wrong format) opens the Profile, where it is.
  const saveFromBar = (draft: boolean) => {
    const from = view;
    void save(draft).then((ok) => {
      if (!ok && from !== 'demographics') setView('demographics');
    });
  };
  // ehr#730: a Profile at View has no Save bar (and its fields can't be changed, below)
  const profileBar =
    !readOnlyTab('demographics') &&
    showProfileSaveBar({
      view,
      isClient,
      ready: status === 'ready',
      dirty: anyDirty,
      busy: saving || !!saveNote || !!saveBlock,
    });
  useEffect(() => {
    if (!anyDirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyDirty]);

  // ehr#736 (Jay 10-31): no Delete in the footer any more (it wiped the whole Demographics record).
  // The chart is archive-only: a client is archived from the ⋮ menu (admin / manager).

  // ehr#743 (V15): Authorizations is a Profile section with no fields of its own (its panel is the
  // old top tab's), so `active` stays a fields section; the card shows the panel for it instead.
  const active = SECTIONS.find((s) => s.key === section) ?? SECTIONS[0]!;
  // ehr#661: the client's preferred contact method has nothing to reach (warned on Contact Info).
  const prefGap = preferredContactGap(data.contact);

  // Minor gate for the Family & Contacts required-flag: explicit checkbox or a DOB
  // under 18 (mockup auto-detects from date of birth).
  const dobAge = ageFromDob(data.identity?.dob ?? '');
  const isMinor = data.guardian?.isMinor === 'true' || (dobAge !== null && dobAge < 18);

  // ehr#743 (V15): the section tabs, in V15's order (Authorizations after Insurance / Payor, More
  // holding Pharmacy and Employment). Authorizations keeps the old top tab's rule: client charts,
  // and only inside the Profile, so a role that cannot see the Profile's PHI never sees it.
  // ehr#730: a share that hides Authorizations hides this section too
  const authShown = isClient && tabLevel('authorizations') !== 'hidden';
  const sectionTabs = useMemo(() => profileTabs(SECTIONS, authShown), [authShown]);
  // The amber dots: what Save record would flag red, or a section the chart flags as empty.
  const needInfo = sectionsNeedingInfo(SECTIONS, data, {
    stored: storedContact.current,
    isMinor,
    optional: OPTIONAL_SECTIONS,
  });

  // ehr#743 (V11): what Search everything looks through: the header, then every Profile field
  // (each section and added entry, label and value), section by section in the tabs' order (V15
  // 31a). Jay (#743, 10-31) wants every tab, notes and messages too: that comes in the next PRs.
  const searchItems = useMemo<ChartHit[]>(
    () => [
      ...headerIndex([
        { label: 'Status', value: meta.status ?? 'No status' },
        { label: 'Owner', value: meta.owner },
        { label: 'Added', value: meta.added },
        { label: 'Client Balance', value: balanceCents != null ? money(balanceCents) : null },
        { label: 'Unapplied', value: unappliedCents ? money(unappliedCents) : null },
        { label: 'Auth Remaining', value: authRemaining != null ? String(authRemaining) : null },
      ]),
      ...profileTabOrder(sectionTabs).flatMap((t) =>
        t.key === AUTH_SECTION
          ? [sectionHit(AUTH_SECTION, AUTH_LABEL)]
          : buildProfileIndex(
              SECTIONS.filter((s) => s.key === t.key),
              data,
              extras,
              {
                display: (f, raw, entry) =>
                  f.type === 'provider'
                    ? raw
                      ? (directory.find((x) => x.id === raw)?.display ??
                        [entry.firstName, entry.lastName].filter(Boolean).join(' '))
                      : ''
                    : undefined,
                entryTitle: (sec, n) =>
                  `${REPEATABLE[sec]?.entry ?? SECTIONS.find((x) => x.key === sec)?.label ?? sec} #${n}`,
                hasNotes: (sec) => sec !== 'security',
              },
            ),
      ),
    ],
    [meta, balanceCents, unappliedCents, authRemaining, data, extras, directory, sectionTabs],
  );
  // Picking a result opens its section (and any closed group around the field), then scrolls to
  // the field, puts the cursor in it and flashes it. A header result flashes in the header.
  const pickSearchHit = (hit: ChartHit, root: ParentNode) => {
    if (hit.kind === 'head') {
      const el = headerTarget(root, hit.label);
      if (el) flashEl(el);
      return;
    }
    if (!hit.section) return;
    setView('demographics');
    setSection(hit.section);
    if (hit.opens.length) setOpenHeads((prev) => new Set([...prev, ...hit.opens]));
    window.setTimeout(() => {
      const el = profileTarget(root, hit);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const ctl = el.matches('input, textarea, select')
        ? el
        : el.querySelector<HTMLElement>('input:not([type="hidden"]), textarea, select, button');
      ctl?.focus({ preventScroll: true });
      flashEl(el);
    }, 60);
  };

  // A trailing "*" in a field label marks a required field — render it red
  // (mockup .field .req), the rest of the label unchanged.
  const fieldLabel = (label: string) =>
    label.endsWith('*') ? (
      <>
        {label.slice(0, -1)}
        <span className="demo-req">*</span>
      </>
    ) : (
      label
    );

  // Render one entry's field grid against a data key (base = section key,
  // extra entries = `${section}#${id}`). Shared by base + repeated entries.
  const renderGrid = (dataKey: string) => {
    // Default-on lookup for every checkbox in the section — standalone fields and
    // inline label-row checks alike (V22 "…same" toggles start checked).
    const checkboxDefault = new Map<string, boolean>();
    active.fields.forEach((f) => {
      if (f.type === 'checkbox') checkboxDefault.set(f.key, !!f.defaultChecked);
      f.inlineChecks?.forEach((c) => checkboxDefault.set(c.key, !!c.defaultChecked));
      f.checks?.forEach((c) => checkboxDefault.set(c.key, !!c.defaultChecked));
      f.aiChecks?.forEach((c) => checkboxDefault.set(c.key, !!c.defaultChecked));
    });
    // A checkbox is on when stored 'true', or unset with a default-on flag.
    const isChecked = (key: string) => {
      const v = data[dataKey]?.[key] ?? '';
      return v === 'true' || (v === '' && !!checkboxDefault.get(key));
    };
    // A field's label, with any inline label-row checkboxes on the right (V22; ehr#208).
    const labelRow = (f: Field) =>
      f.inlineChecks?.length ? (
        <span className="demo-field-labelrow">
          <span className="demo-field-label">{fieldLabel(f.label)}</span>
          <span className="demo-inline-checks">
            {f.inlineChecks.map((c) => (
              <label key={c.key} className="demo-inline-check" title={c.title}>
                <input
                  type="checkbox"
                  checked={isChecked(c.key)}
                  onChange={(e) =>
                    set(dataKey, c.key, e.target.checked ? 'true' : c.defaultChecked ? 'false' : '')
                  }
                />
                {c.label}
              </label>
            ))}
          </span>
        </span>
      ) : (
        <span className="demo-field-label">
          {/* ehr#733 / #734: Contact Info's required phones get the red star. */}
          {fieldLabel(
            dataKey === 'contact' ? contactFieldLabel(data.contact, f.key, f.label) : f.label,
          )}
        </span>
      );
    // Collapsible heads (V22): map each field to the nearest preceding collapsible
    // head; any head ends the previous group. Fields under a closed head hide.
    const ownerHead = new Map<string, string | null>();
    let curHead: string | null = null;
    for (const f of active.fields) {
      if (f.type === 'subhead' || f.type === 'linkhead') curHead = f.collapsible ? f.key : null;
      else {
        // A field flagged endsGroup closes the open collapsible so it (and any
        // field after it) escape the show/hide scope (V22 — Insurance card).
        if (f.endsGroup) curHead = null;
        ownerHead.set(f.key, curHead);
      }
    }
    const headOpen = (headKey: string) => openHeads.has(`${dataKey}.${headKey}`);
    return (
      // ehr#743: data-entry lets a Search everything result find this entry's field.
      <div className="demo-grid" data-entry={dataKey}>
        {active.fields
          .filter((f) => {
            // Conditional groups (V22): hide a field while its controlling "…same"
            // checkbox is checked (Portal email, Mailing address).
            if (f.hideWhen && isChecked(f.hideWhen)) return false;
            // ehr#208: the Linked chart picker shows while either link switch is on.
            if (f.showWhenAny && !f.showWhenAny.some(isChecked)) return false;
            // Collapsed collapsible head hides everything it owns.
            const owner = ownerHead.get(f.key);
            if (owner && !headOpen(owner)) return false;
            // ehr#743 (Jay's ruling 32A): Search everything never hides a field; only its
            // results list changes (the old #14 filter is gone).
            return true;
          })
          .map((f) => {
            const cls = `demo-field${f.full ? ' demo-field-full' : ''}${
              invalid.has(`${dataKey}.${f.key}`) ? ' demo-field-invalid' : ''
            }${f.disableWhen && isChecked(f.disableWhen) ? ' demo-field-disabled' : ''}`;
            if (f.type === 'subhead' || f.type === 'linkhead') {
              const innerCls = f.type === 'subhead' ? 'demo-subhead' : 'demo-linkhead';
              if (f.collapsible) {
                const open = headOpen(f.key);
                return (
                  <div key={f.key} className={cls} data-field={f.key}>
                    <button
                      type="button"
                      className={`${innerCls} demo-collapse-head${open ? ' open' : ''}`}
                      onClick={() => toggleHead(`${dataKey}.${f.key}`)}
                      aria-expanded={open}
                    >
                      <span>{f.label}</span>
                      <svg
                        className="demo-collapse-chev"
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                        fill="none"
                        aria-hidden
                      >
                        <path
                          d="M1 1.5L6 6.5L11 1.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                );
              }
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  {f.type === 'subhead' ? (
                    <div className="demo-subhead">{f.label}</div>
                  ) : (
                    <span className="demo-linkhead">{f.label}</span>
                  )}
                </div>
              );
            }
            const val = data[dataKey]?.[f.key] || f.defaultValue || '';
            if (f.type === 'contact') {
              // ehr#208: another client's chart. Picking one also fills a blank first and
              // last name on this entry from that chart.
              const entry = data[dataKey] ?? {};
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  <span className="demo-field-label">{fieldLabel(f.label)}</span>
                  <ContactPickField
                    value={{ id: val, name: f.nameKey ? (entry[f.nameKey] ?? '') : '' }}
                    excludeId={contact.id}
                    onChange={(c) => {
                      set(dataKey, f.key, c?.id ?? '');
                      if (f.nameKey) set(dataKey, f.nameKey, c?.name ?? '');
                      if (c && !entry.firstName?.trim() && !entry.lastName?.trim()) {
                        set(dataKey, 'firstName', c.firstName);
                        set(dataKey, 'lastName', c.lastName);
                      }
                    }}
                  />
                  <span className="demo-field-hint">
                    The person’s own chart. <b>Link charts</b> shows them beside this client’s name;{' '}
                    <b>Display in portal</b> lets their portal login open this chart.
                  </span>
                </div>
              );
            }
            if (f.type === 'provider') {
              const entry = data[dataKey] ?? {};
              const gone = val && !directory.some((p) => p.id === val);
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  <span className="demo-field-label">{fieldLabel(f.label)}</span>
                  <Dd
                    value={val}
                    onChange={(v) => pickProvider(dataKey, v)}
                    placeholder="Pick a provider, or type the details below"
                    ariaLabel="Practice provider"
                    options={[
                      { value: '', label: 'None — type the details in' },
                      ...directory.map((p) => ({
                        value: p.id,
                        label: p.title ? `${p.display} · ${p.title}` : p.display,
                        search: `${p.npi} ${p.email}`,
                      })),
                      ...(gone
                        ? [
                            {
                              value: val,
                              label: `${[entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'Provider'} (no longer on the team)`,
                            },
                          ]
                        : []),
                    ]}
                  />
                </div>
              );
            }
            if (f.type === 'payor') {
              // ehr#176: only payors configured in RCM. A value saved before (or typed in
              // by hand / imported) that RCM doesn't have stays visible, flagged.
              const known = rcmPayors ?? [];
              const inList = !val || known.some((p) => p.name === val);
              // ehr#176 (Chase 10-12): RCM has one payor per plan state, so options are payor
              // ids; an entry saved by name alone, with that name in several states, asks again.
              const picked = payorValue(known, val, String(data[dataKey]?.payerRcmId ?? ''));
              const askState = !!val && !picked && inList;
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  <span className="demo-field-label">{fieldLabel(f.label)}</span>
                  <Dd
                    value={val ? picked || SAVED_PAYOR : ''}
                    ariaLabel="Payor / insurance company"
                    placeholder={
                      rcmPayors === null
                        ? 'Loading payors…'
                        : known.length
                          ? 'Pick a payor…'
                          : // ehr#176: say where payors are turned on
                            'No payors turned on in RCM (RCM → Payors → Active)'
                    }
                    onChange={(v) => {
                      if (v === SAVED_PAYOR) return;
                      const p = known.find((x) => x.id === v);
                      set(dataKey, f.key, p?.name ?? '');
                      set(dataKey, 'payerRcmId', p?.id ?? '');
                    }}
                    options={[
                      { value: '', label: '— None —' },
                      ...payorOptions(known),
                      ...(val && !picked
                        ? [
                            {
                              value: SAVED_PAYOR,
                              label: askState
                                ? `${val} (pick the plan's state)`
                                : `${val} (not in RCM payors)`,
                            },
                          ]
                        : []),
                    ]}
                  />
                  {payorsErr ? (
                    <span className="demo-field-err">Could not load the payor list from RCM.</span>
                  ) : rcmPayors !== null && !known.length ? (
                    <span className="demo-field-err">
                      Add payors in RCM → Chargemaster → Payors first.
                    </span>
                  ) : !inList ? (
                    <span className="demo-field-err">
                      Not in RCM's payor list. Pick one from the list.
                    </span>
                  ) : askState ? (
                    <span className="demo-field-err">
                      RCM has {val} for more than one state. Pick the one for this client.
                    </span>
                  ) : null}
                </div>
              );
            }
            if (f.type === 'checkbox') {
              return (
                <label key={f.key} className={`${cls} demo-check`} data-field={f.key}>
                  <input
                    type="checkbox"
                    checked={isChecked(f.key)}
                    onChange={(e) => {
                      set(
                        dataKey,
                        f.key,
                        e.target.checked ? 'true' : f.defaultChecked ? 'false' : '',
                      );
                      // ehr#191 — one primary clinician: clear it on the other entries.
                      if (f.exclusive && e.target.checked) {
                        const keys = [
                          active.key,
                          ...(extras[active.key] ?? []).map((n) => `${active.key}#${n}`),
                        ];
                        for (const k of keys)
                          if (k !== dataKey && data[k]?.[f.key] === 'true') set(k, f.key, '');
                      }
                    }}
                  />
                  <span className="demo-field-label">{fieldLabel(f.label)}</span>
                </label>
              );
            }
            if (f.type === 'checkboxes') {
              const sel = new Set(val ? val.split('||') : []);
              const otherVal = f.otherKey ? (data[dataKey]?.[f.otherKey] ?? '') : '';
              const grpOpen = !f.collapsible || headOpen(f.key);
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  {f.collapsible ? (
                    <button
                      type="button"
                      className={`demo-linkhead demo-collapse-head${grpOpen ? ' open' : ''}`}
                      onClick={() => toggleHead(`${dataKey}.${f.key}`)}
                      aria-expanded={grpOpen}
                    >
                      <span>{f.label}</span>
                      <svg
                        className="demo-collapse-chev"
                        width="12"
                        height="8"
                        viewBox="0 0 12 8"
                        fill="none"
                        aria-hidden
                      >
                        <path
                          d="M1 1.5L6 6.5L11 1.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  ) : (
                    f.label && <span className="demo-field-label">{fieldLabel(f.label)}</span>
                  )}
                  {grpOpen && (
                    <>
                      <div className="demo-check-group box">
                        {(f.options ?? []).map((o) => (
                          <label key={o} className="demo-check-item">
                            <input
                              type="checkbox"
                              checked={sel.has(o)}
                              onChange={() => {
                                if (sel.has(o)) sel.delete(o);
                                else sel.add(o);
                                set(dataKey, f.key, [...sel].join('||'));
                                if (f.otherKey && !sel.has('Other')) set(dataKey, f.otherKey, '');
                              }}
                            />
                            {o}
                          </label>
                        ))}
                      </div>
                      {f.otherKey && sel.has('Other') && (
                        <input
                          className="demo-other-input"
                          value={otherVal}
                          placeholder="Please specify…"
                          onChange={(e) => set(dataKey, f.otherKey!, e.target.value)}
                        />
                      )}
                    </>
                  )}
                </div>
              );
            }
            if (f.type === 'select') {
              const otherVal = f.otherKey ? (data[dataKey]?.[f.otherKey] ?? '') : '';
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  {labelRow(f)}
                  <Cd
                    value={val}
                    options={
                      f.optionsFrom === 'referral-sources'
                        ? referralSourceOptions(
                            (referralSources ?? []).map((r) => r.name),
                            val,
                          )
                        : (f.options ?? [])
                    }
                    onChange={(v) => {
                      set(dataKey, f.key, v);
                      // Clear the write-in when the choice is no longer "Other".
                      if (f.otherKey && v !== 'Other') {
                        set(dataKey, f.otherKey, '');
                        dropMark(`${dataKey}.${f.otherKey}`); // ehr#758: nothing to specify now
                      }
                    }}
                  />
                  {f.otherKey && val === 'Other' && (
                    <input
                      className={`demo-other-input${
                        invalid.has(`${dataKey}.${f.otherKey}`) ||
                        referralSpecifyRed(dataKey, f.otherKey, otherVal)
                          ? ' demo-other-invalid'
                          : ''
                      }`}
                      aria-invalid={
                        invalid.has(`${dataKey}.${f.otherKey}`) ||
                        referralSpecifyRed(dataKey, f.otherKey, otherVal) ||
                        undefined
                      }
                      aria-required={isReferralSpecifyBox(dataKey, f.otherKey) || undefined}
                      data-field={f.otherKey}
                      value={otherVal}
                      placeholder={
                        isReferralSpecifyBox(dataKey, f.otherKey)
                          ? REFERRAL_SPECIFY_PLACEHOLDER
                          : 'Please specify…'
                      }
                      onChange={(e) => set(dataKey, f.otherKey!, e.target.value)}
                    />
                  )}
                  {/* ehr#661: the "fill in" message while a save is refused for it */}
                  {dataKey === 'contact' &&
                    f.key === 'preferredContact' &&
                    invalid.has('contact.preferredContact') &&
                    prefGap && <span className="fm-missing-msg">{prefGap.fillIn}</span>}
                </div>
              );
            }
            if (f.type === 'file') {
              const pair = f.pair ?? ['Front', 'Back'];
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  <span className="demo-field-label">{fieldLabel(f.label)}</span>
                  <div className="upload-pair">
                    {pair.map((cap, idx) => {
                      const slotKey = `${f.key}${idx}`;
                      const uk = `${dataKey}.${slotKey}`;
                      return (
                        <UploadZone
                          key={uk}
                          label={cap}
                          value={uploads[uk] ?? ''}
                          busy={uploadBusy[uk]}
                          error={uploadErr[uk]}
                          onPick={(file) => pickImage(dataKey, slotKey, file)}
                          onRemove={() => removeImage(dataKey, slotKey)}
                          onExpand={(src) => setLightbox({ src, label: cap })}
                        />
                      );
                    })}
                  </div>
                  <div className="upload-hint">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {IMAGE_HINT}
                  </div>
                </div>
              );
            }
            if (f.type === 'date' && f.ageChip) {
              const age = ageFromDob(val);
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  <span className="demo-field-label">{fieldLabel(f.label)}</span>
                  <div className="demo-dob-row">
                    <DateField value={val} onChange={(v) => set(dataKey, f.key, v)} />
                    <span className="demo-age-chip" aria-live="polite">
                      Age <span className="demo-age-num">{age ?? '—'}</span>
                    </span>
                  </div>
                </div>
              );
            }
            if (f.key === 'ssn') {
              // ehr#712: masked once saved, with an Edit button (audited reveal) - SsnField.
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  <span className="demo-field-label">{fieldLabel(f.label)}</span>
                  <SsnField
                    contactId={contact.id}
                    section={dataKey}
                    value={val}
                    onChange={(v) => set(dataKey, f.key, v)}
                    onRestore={(v) =>
                      setData((d) => ({ ...d, [dataKey]: { ...(d[dataKey] ?? {}), [f.key]: v } }))
                    }
                    placeholder={f.placeholder}
                    maxLength={f.maxLength}
                    invalid={invalid.has(`${dataKey}.${f.key}`)}
                    label={f.label.replace(/\*$/, '')}
                  />
                </div>
              );
            }
            if (f.type === 'date') {
              // #45 — Credify-themed date picker (was a raw native <input type="date">).
              return (
                <div key={f.key} className={cls} data-field={f.key}>
                  <span className="demo-field-label">{fieldLabel(f.label)}</span>
                  <DateField value={val} onChange={(v) => set(dataKey, f.key, v)} />
                </div>
              );
            }
            const placeholder = f.placeholder ?? (f.type === 'tel' ? '(___) ___-____' : undefined);
            const fmtInvalid =
              (f.type === 'tel' && !phoneValid(val)) ||
              (f.type === 'email' && !emailValid(val)) ||
              (f.key === 'zip' && !zipValid(val)) ||
              // ehr#743 (V11): a part-typed Referring provider NPI shows red as you type.
              (f.key === 'referringNpi' && !referralNpiValid(val));
            // A field carrying nested <label>s (per-phone checks or an inline
            // check) must be a <div>, not a <label>, or the HTML is invalid.
            const Wrap = f.checks || f.aiChecks || f.inlineChecks ? 'div' : 'label';
            return (
              <Wrap key={f.key} className={cls} data-field={f.key}>
                {labelRow(f)}
                {f.datalist ? (
                  // #11 — searchable payor combo: custom, correctly-sized panel on
                  // desktop (the native <datalist> popup is oversized/misaligned),
                  // native suggestions on touch. Free text still allowed.
                  <Combobox
                    value={val}
                    options={f.datalist}
                    placeholder={placeholder ?? 'Type or pick a payor…'}
                    onChange={(v) => set(dataKey, f.key, v)}
                  />
                ) : (
                  <input
                    type={f.type ?? 'text'}
                    className={fmtInvalid ? 'demo-input-invalid' : undefined}
                    value={val}
                    placeholder={placeholder}
                    inputMode={f.key === 'zip' ? 'numeric' : f.inputMode}
                    maxLength={f.maxLength}
                    onChange={(e) => {
                      let v = e.target.value;
                      if (f.type === 'tel') v = maskPhone(v);
                      else if (f.key === 'ssn') v = maskSsn(v);
                      else if (f.key === 'zip') v = maskZip(v);
                      else if (DIGITS_ONLY.has(f.key)) v = v.replace(/\D/g, '');
                      set(dataKey, f.key, v);
                    }}
                  />
                )}
                {fmtInvalid && (
                  <span className="demo-field-err">
                    {f.type === 'email'
                      ? 'Enter a valid email'
                      : f.key === 'zip'
                        ? 'Enter a 5-digit ZIP (or ZIP+4)'
                        : f.key === 'referringNpi'
                          ? 'Enter the 10-digit NPI'
                          : 'Enter a 10-digit phone'}
                  </span>
                )}
                {dataKey === 'contact' && invalid.has(`contact.${f.key}`) && !val.trim() && (
                  // ehr#733 / #734: after a refused Save record, why this phone is needed.
                  <span className="demo-field-err">
                    {requiredContactMessage(data.contact, f.key)}
                  </span>
                )}
                {f.checks && (
                  <div className="demo-phone-checks">
                    {f.checks.map((c) => (
                      <label key={c.key} className="demo-check-inline" title={c.title}>
                        <input
                          type="checkbox"
                          checked={isChecked(c.key)}
                          onChange={(e) => {
                            // ehr#233: the mobile "OK to call" / "OK to text" are the dated
                            // channel answers; ticking or unticking records them.
                            if (dataKey === 'contact' && CONTACT_CHANNEL_KEYS[c.key]) {
                              setChannelAnswer(c.key, e.target.checked);
                              return;
                            }
                            set(
                              dataKey,
                              c.key,
                              e.target.checked ? 'true' : c.defaultChecked ? 'false' : '',
                            );
                          }}
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                )}
                {f.aiChecks && (
                  // ehr#570: the phone's AI-call consent, tagged so it can't pass for the
                  // person-call boxes above.
                  <div
                    className="demo-phone-checks demo-phone-checks-ai"
                    role="group"
                    aria-label={`AI calls to ${f.label.toLowerCase()}`}
                  >
                    <span className="demo-phone-checks-tag">AI Calls</span>
                    {f.aiChecks.map((c) => (
                      <label key={c.key} className="demo-check-inline" title={c.title}>
                        <input
                          type="checkbox"
                          checked={isChecked(c.key)}
                          onChange={(e) =>
                            set(
                              dataKey,
                              c.key,
                              e.target.checked ? 'true' : c.defaultChecked ? 'false' : '',
                            )
                          }
                        />
                        {c.label}
                      </label>
                    ))}
                  </div>
                )}
              </Wrap>
            );
          })}
      </div>
    );
  };

  // Per-section Notes textarea (mockup .section-notes-wrap), stored under the
  // entry's `_notes` key so each form (base + repeated) keeps its own notes.
  const renderNotes = (dataKey: string) => (
    <div className="section-notes-wrap">
      <label htmlFor={`notes-${dataKey}`}>Notes</label>
      <textarea
        id={`notes-${dataKey}`}
        rows={3}
        placeholder="Add notes for this section…"
        value={data[dataKey]?._notes ?? ''}
        onChange={(e) => set(dataKey, '_notes', e.target.value)}
      />
    </div>
  );

  // "+ Add …" button for the active section (if repeatable). Insurance cycles
  // secondary → tertiary then hides.
  const addButton = () => {
    const cfg = REPEATABLE[active.key];
    if (!cfg) return null;
    const n = (extras[active.key] ?? []).length;
    const labels = Array.isArray(cfg.label) ? cfg.label : null;
    if (labels && n >= labels.length) return null;
    const label = labels ? labels[n]! : (cfg.label as string);
    return (
      <div className="demo-add-row">
        <button type="button" className={cfg.style} onClick={() => addEntry(active.key)}>
          {label}
        </button>
      </div>
    );
  };

  return (
    <div className="modal-backdrop" onClick={requestClose}>
      <div
        className={maximized ? 'drawer maximized' : 'drawer'}
        role="dialog"
        aria-modal="true"
        aria-label={`${contact.firstName} ${contact.lastName}`}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={onDrawerContextMenu}
      >
        <header className="drawer-head">
          <div className="drawer-head-top">
            <div className="drawer-title-wrap">
              {followupOverdue && (
                <span className="drawer-overdue-badge" title="Follow-up is overdue">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  Overdue
                </span>
              )}
              <div className={`drawer-title-row${related.related.length ? ' has-related' : ''}`}>
                {/* ehr#636: the open chart is the primary group (large serif name, its role
                    beneath); each linked chart sits to the right, smaller, with its role
                    beneath, split by thin dividers. Without links the head is unchanged. */}
                <div className="drawer-rel-group drawer-rel-primary">
                  <h3 className="drawer-title" style={{ margin: 0, minWidth: 0 }}>
                    {manager ? (
                      <input
                        className="drawer-title-input"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        onFocus={() => {
                          nameFocused.current = true;
                        }}
                        onBlur={() => {
                          nameFocused.current = false;
                          saveName();
                        }}
                        aria-label="Contact name"
                        style={
                          chartLinks.length
                            ? { width: `${Math.max(nameDraft.length, 4) + 1}ch`, maxWidth: '100%' }
                            : undefined
                        }
                      />
                    ) : (
                      `${(liveName ?? contact).firstName} ${(liveName ?? contact).lastName}`
                    )}
                  </h3>
                  {related.selfLabel && (
                    <span className="drawer-rel-label">{related.selfLabel}</span>
                  )}
                </div>
                {related.related.length > 0 && (
                  <span className="drawer-links" aria-label="Linked charts">
                    {related.related.map((l) => (
                      <span key={l.contactId} className="drawer-link drawer-rel-group">
                        <button
                          type="button"
                          className="drawer-link-name"
                          title={`Open ${l.name}’s chart`}
                          disabled={!onOpenContact}
                          onClick={() => requestLeave(() => onOpenContact?.(l.contactId))}
                        >
                          {l.name}
                        </button>
                        {l.label && <span className="drawer-rel-label">{l.label}</span>}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            </div>
            <div className="drawer-head-actions" ref={headActionsRef}>
              {/* Owner chip → the Reassign window (ehr#730 V11), for whoever may reassign */}
              <div style={{ position: 'relative' }}>
                <button
                  className={`owner-chip ${chip.pending ? 'pending' : meta.owner ? '' : 'unassigned'}`}
                  onClick={() =>
                    canReassign && !chip.pending && access.openReassign([accessTarget()])
                  }
                  disabled={!canReassign || chip.pending}
                  title={canReassign ? 'Reassign' : 'Owner'}
                  aria-busy={chip.pending || undefined}
                  aria-label={chip.pending ? 'Owner (loading)' : undefined}
                >
                  <span
                    className="user-avatar small"
                    style={{ background: 'var(--green-primary)' }}
                  >
                    {chip.initials}
                  </span>
                  <strong>{chip.text}</strong>
                </button>
              </div>
              {/* ⋮ More options */}
              <div style={{ position: 'relative' }}>
                <button
                  className="drawer-dot-btn"
                  onClick={() => setKebabOpen((o) => !o)}
                  aria-label="More options"
                  aria-expanded={kebabOpen}
                  title="More options"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <circle cx="12" cy="5" r="1.5" />
                    <circle cx="12" cy="12" r="1.5" />
                    <circle cx="12" cy="19" r="1.5" />
                  </svg>
                </button>
                {kebabOpen && (
                  <div className="drawer-dot-panel" role="menu">
                    <button
                      type="button"
                      className="bv-dot-menu-item"
                      onClick={() => {
                        setView('followup');
                        setKebabOpen(false);
                      }}
                    >
                      {kebabIcon(
                        '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
                      )}
                      Schedule a meeting
                    </button>
                    {category === 'client' && (
                      <button
                        type="button"
                        className={`bv-dot-menu-item ${manager ? '' : 'muted'}`}
                        onClick={() => {
                          if (!manager || inviting) return;
                          void inviteToPortal();
                        }}
                        title={manager ? 'Send a client portal invitation' : 'Admins/managers only'}
                      >
                        {kebabIcon(
                          '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-10 5L2 7"/>',
                        )}
                        {inviting ? 'Inviting…' : 'Invite To Portal'}
                      </button>
                    )}
                    <div className="bv-dot-menu-divider" />
                    {/* ehr#730 (V11 record ⋮ menu): Share, Reassign, Manage Access */}
                    <button
                      type="button"
                      className={`bv-dot-menu-item ${canShare ? '' : 'muted'}`}
                      onClick={() => {
                        if (!canShare) return;
                        setKebabOpen(false);
                        access.openShare([accessTarget()]);
                      }}
                      title={canShare ? '' : ACCESS_DENIED_MESSAGE}
                    >
                      {kebabIcon(
                        '<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>',
                      )}
                      Share
                    </button>
                    <button
                      type="button"
                      className={`bv-dot-menu-item ${canReassign ? '' : 'muted'}`}
                      onClick={() => {
                        if (!canReassign) return;
                        setKebabOpen(false);
                        access.openReassign([accessTarget()]);
                      }}
                      title={canReassign ? '' : ACCESS_DENIED_MESSAGE}
                    >
                      {kebabIcon(
                        '<polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><path d="M4 4h7v7"/>',
                      )}
                      Reassign
                    </button>
                    <button
                      type="button"
                      className={`bv-dot-menu-item ${canShare ? '' : 'muted'}`}
                      onClick={() => {
                        if (!canShare) return;
                        setKebabOpen(false);
                        access.openManage(accessTarget());
                      }}
                      title={canShare ? '' : ACCESS_DENIED_MESSAGE}
                    >
                      {kebabIcon('<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>')}
                      Manage Access
                    </button>
                    {contact.archivedAt ? (
                      <button
                        type="button"
                        className={`bv-dot-menu-item ${canArchive ? '' : 'muted'}`}
                        onClick={() => canArchive && unarchiveContact()}
                        title={canArchive ? '' : ACCESS_DENIED_MESSAGE}
                      >
                        {kebabIcon(
                          '<polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
                        )}
                        Restore
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`bv-dot-menu-item ${canArchive ? '' : 'muted'}`}
                        onClick={() => canArchive && archiveContact()}
                        title={canArchive ? '' : ACCESS_DENIED_MESSAGE}
                      >
                        {kebabIcon(
                          '<polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/>',
                        )}
                        Archive
                      </button>
                    )}
                    <div className="bv-dot-menu-divider" />
                    <div className="bv-dot-menu-cat">
                      <span className="bv-dot-menu-cat-label">Contact Type</span>
                      <div className="bv-dot-menu-cat-opts">
                        {(
                          [
                            ['client', 'Client'],
                            ['referral_source', 'Referral'],
                            ['staff', 'Staff'],
                          ] as [string, string][]
                        ).map(([val, lbl]) => (
                          <button
                            key={val}
                            type="button"
                            className={`bv-cat-opt ${category === val ? 'active' : ''}`}
                            onClick={() => manager && changeCategory(val)}
                            disabled={!manager}
                          >
                            {lbl}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="bv-dot-menu-divider" />
                    <button
                      type="button"
                      className="bv-dot-menu-item muted"
                      title="Not available yet"
                    >
                      {kebabIcon(
                        '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
                      )}
                      Sync to Google Calendar
                    </button>
                  </div>
                )}
              </div>
              <button
                className="drawer-maximize"
                onClick={() => setMaximized((m) => !m)}
                aria-label={maximized ? 'Restore drawer width' : 'Maximize drawer width'}
                aria-pressed={maximized}
                title={maximized ? 'Restore' : 'Maximize'}
              >
                {maximized ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="14" y1="10" x2="21" y2="3" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                )}
              </button>
              <button className="drawer-close" onClick={requestClose} aria-label="Close">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
          <div className="drawer-stage-row">
            {/* #— Touches + Status pill sit at the start of the meta row, before
                Stage / Owner / Added (per the screenshot). */}
            <TouchesControl
              contactId={contact.id}
              contactName={shownName}
              statusLabel={meta.status}
            />
            <StatusControl contactId={contact.id} manager={manager} refreshKey={liveTick} />
            {/* #105 - Stage removed from the header: redundant with Status. */}
            {meta.owner && (
              <>
                <span className="drawer-stage-sep">·</span>
                <span className="drawer-kv">
                  <span className="drawer-stage-label">Owner</span>
                  <span className="drawer-stage-val">{meta.owner}</span>
                </span>
              </>
            )}
            {/* Status moved to the header pill (StatusControl), per the mockup. */}
            {meta.added && (
              <>
                <span className="drawer-stage-sep">·</span>
                <span className="drawer-kv">
                  <span className="drawer-stage-label">Added</span>
                  <span className="drawer-stage-val">{meta.added}</span>
                </span>
              </>
            )}
            {/* #104 - static Client Balance (past-visit balance from the ledger). */}
            {contact.category === 'client' && balanceCents != null && (
              <>
                <span className="drawer-stage-sep">·</span>
                <span className="drawer-kv">
                  <span className="drawer-stage-label">Client Balance</span>
                  <span className="drawer-stage-val">{money(balanceCents)}</span>
                </span>
              </>
            )}
            {/* rcm#91 - money received in RCM but not yet applied to a claim (read only). */}
            {contact.category === 'client' && unappliedCents != null && unappliedCents > 0 && (
              <>
                <span className="drawer-stage-sep">·</span>
                <span className="drawer-kv">
                  <span className="drawer-stage-label">Unapplied</span>
                  <span className="drawer-stage-val">{money(unappliedCents)}</span>
                </span>
              </>
            )}
            {/* ehr#565 - authorizations left (AUTH_MOCK_10-20-V1); ehr#743 (V15): opens Profile ›
                Authorizations, where the old Authorizations tab went. */}
            {contact.category === 'client' && authRemaining != null && (
              <>
                <span className="drawer-stage-sep">·</span>
                <button
                  type="button"
                  className="drawer-auth-remaining"
                  onClick={() => {
                    setView('demographics');
                    setSection(AUTH_SECTION);
                  }}
                  title="Open Profile › Authorizations"
                >
                  <span className="drawer-stage-label">Auth Remaining</span>
                  <span className="drawer-stage-val">{authRemaining}</span>
                </button>
              </>
            )}
          </div>
        </header>

        {/* ehr#893: the tabs that don't fit the row go under More (no sideways scrollbar). */}
        <ChartTabs
          tabs={stripTabs.map((t) => ({
            key: t.key,
            label: t.label,
            badge: (counts as Record<string, number | undefined>)[t.key] || undefined,
            overdue: t.key === 'followup' && followupOverdue,
          }))}
          current={view}
          onPick={(k) => setView(k as DrawerView)}
        />
        {/* ehr#743 (V11): Search everything, pinned under the top tabs (outside the scrolling body).
            On the Profile tab for now; the bar (and "/") goes on every tab with every-tab search. */}
        {view === 'demographics' && isClient && status === 'ready' && (
          <ChartSearch items={searchItems} currentSection={section} onPick={pickSearchHit} />
        )}
        <div id="drawer-body" className="drawer-body">
          {readOnlyTab(view) && tabLevel(view) === 'view' && (
            <div className="acs-viewonly" role="note">
              View only. You can see this tab but not change it.
            </div>
          )}
          {view === 'summary' && isClient && status === 'ready' && (
            <SummaryTab
              contactId={contact.id}
              refreshKey={liveTick}
              firstName={(data.contact?.preferredName ?? '').trim() || contact.firstName}
              data={data}
              balanceCents={balanceCents}
              onOpenSection={(sec) => {
                setSection(sec);
                setView('demographics');
              }}
              onOpenForms={() => setView('forms')}
            />
          )}
          {view === 'summary' && isClient && status === 'loading' && (
            <p className="muted">Loading summary…</p>
          )}
          {view === 'summary' && isClient && (status === 'forbidden' || status === 'error') && (
            <p className="muted">
              {status === 'forbidden'
                ? "Your role cannot view this client's summary."
                : 'The summary could not be loaded.'}
            </p>
          )}
          {view === 'notes' && (
            <NotesPanel
              contactId={contact.id}
              isClient={isClient}
              readOnly={readOnlyTab('notes')}
            />
          )}
          {view === 'followup' && (
            <FollowupPanel contactId={contact.id} readOnly={readOnlyTab('followup')} />
          )}
          {view === 'appointments' && (
            <AppointmentsPanel contactId={contact.id} readOnly={readOnlyTab('appointments')} />
          )}
          {view === 'clinical_notes' && (
            <ClinicalNotesPanel
              contactId={contact.id}
              contactName={shownName}
              // tp#299: the TP opens only from an ACTIVE client's chart (null when archived)
              tpLink={tpChartLink(contact)}
              openNoteId={initialView === 'clinical_notes' ? initialRecord : undefined}
              refreshKey={liveTick}
              readOnly={readOnlyTab('clinical_notes')}
            />
          )}
          {view === 'forms' && (
            <FormsPanel
              patientId={contact.id}
              contactCategory={contact.category}
              contactName={shownName}
              openSubmissionId={
                initialView === 'forms' || initialView === 'screeners' ? initialRecord : undefined
              }
              initialKind={initialView === 'screeners' ? 'screeners' : 'all'}
              refreshKey={liveTick}
              readOnly={readOnlyTab('forms')}
            />
          )}
          {view === 'stage_actions' && <StageActionsPanel />}
          {view === 'tasks' && (
            <ContactTasksPanel
              contactId={contact.id}
              refreshKey={liveTick}
              openTaskId={initialView === 'tasks' ? initialRecord : undefined}
              readOnly={readOnlyTab('tasks')}
            />
          )}
          {view === 'email' && <EmailPanel />}
          {view === 'messages' && <MessagesPanel contactId={contact.id} contactName={shownName} />}
          {view === 'eligibility' && (
            <EligibilityPanel contactId={contact.id} readOnly={readOnlyTab('eligibility')} />
          )}
          {view === 'billing' && <BillingPanel contactId={contact.id} />}
          {view === 'files' && (
            <FilesPanel
              contactId={contact.id}
              contactName={shownName}
              isClient={isClient}
              refreshKey={liveTick}
              readOnly={readOnlyTab('files')}
            />
          )}
          {view === 'cadence' && <CadencePanel contactId={contact.id} />}
          {view === 'activity' && <ActivityPanel contactId={contact.id} />}
          {view === 'licensure' && (
            <LicensurePanel contactId={contact.id} readOnly={readOnlyTab('licensure')} />
          )}

          {view === 'demographics' && !isClient && (
            <p className="muted" style={{ padding: '16px 0' }}>
              The Profile is available for Clients. This is a {contact.category.replace('_', ' ')}{' '}
              record.
            </p>
          )}

          {view === 'demographics' && isClient && status === 'loading' && (
            <p className="muted">Loading the profile…</p>
          )}
          {view === 'demographics' && isClient && status === 'forbidden' && (
            <p className="muted">Your role cannot view the PHI on this profile (403).</p>
          )}
          {view === 'demographics' && isClient && status === 'error' && (
            <p className="muted">Could not load the profile.</p>
          )}

          {view === 'demographics' && isClient && status === 'ready' && (
            <>
              {/* ehr#743 (V15): the section tabs (Mock B). Not a wizard: any tab, any order. */}
              <ProfileSectionTabs
                row={sectionTabs.row.map((t) => ({ ...t, need: needInfo.has(t.key) }))}
                more={sectionTabs.more.map((t) => ({ ...t, need: needInfo.has(t.key) }))}
                current={section}
                onPick={setSection}
              />

              {section !== AUTH_SECTION || !authShown ? (
                // ehr#730: a Profile at View shows the answers; nothing in it can be changed
                <div
                  className="demo-card"
                  data-section={active.key}
                  inert={readOnlyTab('demographics')}
                >
                  <div className="demo-section-head">
                    {/* ehr#737: no pill tag before the section title, just the title. */}
                    <h4 className="demo-section-title">{active.label}</h4>
                  </div>
                  {active.key === 'contact' && prefGap && <PreferredContactWarning gap={prefGap} />}
                  {active.key === 'security' ? (
                    <SecurityQuestionsSection
                      contactId={contact.id}
                      clientName={(data.contact?.firstName ?? '').trim() || contact.firstName}
                    />
                  ) : (
                    renderGrid(active.key)
                  )}
                  {REPEATABLE[active.key] &&
                    (extras[active.key] ?? []).map((id, idx) => (
                      <div className="demo-entry" key={id}>
                        <div className="demo-entry-head">
                          <span className="demo-entry-title">
                            {REPEATABLE[active.key]!.entry ?? active.label} #{idx + 2}
                          </span>
                          <button
                            type="button"
                            className="demo-remove-entry"
                            onClick={() => removeEntry(active.key, id)}
                          >
                            Remove
                          </button>
                        </div>
                        {renderGrid(`${active.key}#${id}`)}
                      </div>
                    ))}
                  {addButton()}
                  {/* ehr#375 (Jay 10-13): the sliding scale, after the insurance and above the notes */}
                  {active.key === 'insurance' && <SlidingScaleSection ss={ss} />}
                  {/* ehr#311: no free-text notes on Security Questions, so an answer never lands there in plain text */}
                  {active.key !== 'security' && renderNotes(active.key)}
                </div>
              ) : (
                // ehr#743 (V15): the old Authorizations top tab, as a Profile section (same panel).
                <div className="demo-card" data-section={AUTH_SECTION}>
                  <div className="demo-section-head">
                    <h4 className="demo-section-title">{AUTH_LABEL}</h4>
                  </div>
                  <AuthorizationsPanel
                    contactId={contact.id}
                    onChanged={() => setAuthTick((t) => t + 1)}
                    readOnly={readOnlyTab('authorizations')}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {((view === 'files' && !readOnlyTab('files')) || profileBar) && (
          <footer className="drawer-foot">
            {view === 'files' && !readOnlyTab('files') && (
              <button
                className="btn docs-upload-btn"
                onClick={() => document.getElementById('docs-file-input')?.click()}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Upload File
              </button>
            )}
            {profileBar && (
              <div className="demo-actionbar-left">
                {valErr ? (
                  <span className="demo-dirty demo-val-err dup-contact" role="alert">
                    {valErr}
                    {openableDup(dupHit) && onOpenContact && (
                      <>
                        {' '}
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => requestLeave(() => onOpenContact(openableDup(dupHit)!.id))}
                        >
                          View chart
                        </button>
                      </>
                    )}
                    {dupHit && dupOpen && (
                      <DuplicateContactDialog
                        dup={dupHit}
                        onOpen={
                          onOpenContact
                            ? (id) => {
                                setDupOpen(false);
                                requestLeave(() => onOpenContact(id));
                              }
                            : undefined
                        }
                        onClose={() => setDupOpen(false)}
                      />
                    )}
                  </span>
                ) : (
                  <span id="dirtyNote" className="demo-dirty">
                    {/* ehr#681: no "Saved ✓" here any more; the card above the button says it. */}
                    {anyDirty
                      ? view === 'demographics'
                        ? 'Unsaved changes'
                        : 'Unsaved changes on Profile' // ehr#743: the bar follows to other tabs
                      : saved && prefGap
                        ? prefGap.message // ehr#661: saved, gap still there
                        : 'No unsaved changes'}
                  </span>
                )}
              </div>
            )}
            <div className={`drawer-foot-right${profileBar ? ' has-save-last' : ''}`}>
              {profileBar && (
                <SaveButtons
                  savingAt={savingAt}
                  disabled={saving}
                  notice={saveNote}
                  onCloseNotice={() => setSaveNote(null)}
                  lastLine={lastSaved ? lastSavedLine(lastSaved, nowTick) : ''}
                  onSave={saveFromBar}
                  onCancel={cancelEdits}
                  cancelDisabled={!anyDirty}
                />
              )}
            </div>
          </footer>
        )}
        {drawerCtx && (
          <ContactActionsMenu
            contactId={contact.id}
            contactName={shownName}
            statusLabel={meta.status}
            positionStyle={{
              position: 'fixed',
              left: drawerCtx.x,
              top: drawerCtx.y,
              zIndex: 1200,
            }}
            onClose={() => setDrawerCtx(null)}
          />
        )}
        {access.element}
        {(inviteResult || inviteError) && (
          <Modal
            title="Invite to Portal"
            onClose={() => {
              setInviteResult(null);
              setInviteError(null);
              setCopied(false);
            }}
            actions={
              inviteResult ? (
                <>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      void navigator.clipboard?.writeText(inviteResult.portalUrl);
                      setCopied(true);
                    }}
                  >
                    {copied ? 'Copied ✓' : 'Copy link'}
                  </button>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => {
                      setInviteResult(null);
                      setCopied(false);
                    }}
                  >
                    Done
                  </button>
                </>
              ) : (
                <button type="button" className="btn primary" onClick={() => setInviteError(null)}>
                  Close
                </button>
              )
            }
          >
            {inviteError ? (
              <p className="muted" style={{ margin: 0 }}>
                {inviteError}
              </p>
            ) : inviteResult ? (
              <>
                <p style={{ marginTop: 0 }}>
                  Portal email ready for <strong>{inviteResult.email}</strong>. Your email app
                  should have opened with the message ready to send — if it didn’t, copy the portal
                  link below and send it yourself.
                </p>
                <label className="muted" style={{ display: 'block', marginBottom: 4 }}>
                  Portal link
                </label>
                <input
                  type="text"
                  readOnly
                  value={inviteResult.portalUrl}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{ fontSize: 12 }}
                />
                <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
                  The client opens this link and signs in with Google at login.credifyfast.com using
                  this email address — access is granted automatically.
                </p>
              </>
            ) : null}
          </Modal>
        )}
        {closeAsk && (
          <ConfirmDialog
            message="You have unsaved changes in this client record. Save them before you leave?"
            confirmLabel={saving ? 'Saving…' : 'Save'}
            altLabel="Don’t Save"
            cancelLabel="Cancel"
            danger={false}
            busy={saving}
            onClose={() => setCloseAsk(false)}
            onAlt={() => {
              setCloseAsk(false);
              leaveTo.current();
            }}
            onConfirm={() => {
              void save(false, true).then((ok) => {
                setCloseAsk(false);
                // A missing required field keeps the record open: the "Not saved" pop-up
                // (ehr#179) says what is missing and offers a draft.
                if (ok) leaveTo.current();
              });
            }}
          />
        )}
        {saveBlock && (
          <SaveBlockDialog
            block={saveBlock}
            sectionLabel={(sec) => SECTIONS.find((s) => s.key === sec)?.label}
            onGoTo={goToField}
            busy={saving}
            onDraft={() => {
              const block = saveBlock;
              void save(true).then((ok) => {
                setSaveBlock(null);
                if (ok && block.thenClose) onClose();
              });
            }}
            onClose={() => setSaveBlock(null)}
          />
        )}
        {lightbox &&
          createPortal(
            <div
              className="img-lightbox"
              role="dialog"
              aria-modal="true"
              aria-label={`${lightbox.label} — enlarged`}
              onClick={() => setLightbox(null)}
            >
              <img src={lightbox.src} alt={lightbox.label} onClick={(e) => e.stopPropagation()} />
              <button
                type="button"
                className="img-lightbox-x"
                aria-label="Close"
                onClick={() => setLightbox(null)}
              >
                ×
              </button>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}
